import ViralClipComposer from "./social/ViralClipComposer";
import { StrokeLabelEditor } from "./StrokeLabelEditor";
import { StrokeAnalysisPanel } from "./StrokeAnalysisPanel";
import { VideoFrameAnnotator } from "./VideoFrameAnnotator";
import { saveCycleFeedbackCue } from "./funtionsBD/digipingSmartService";
import { strokeLabelStorageKey } from "../utils/strokeLabelModel";
import {
  bufferedEndSeconds,
  estimateVideoLoadProgress,
  formatEtaSeconds,
} from "../utils/videoLoadProgress";
import ClipShareSheet from "./social/ClipShareSheet";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  lazy,
  Suspense,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronUp,
  FlipHorizontal2,
  FlipVertical2,
  Heart,
  Info,
  Loader2,
  Pause,
  PersonStanding,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Save,
  Smartphone,
  Target,
  Undo2,
  Upload,
  Film,
  Trophy,
  Share2,
  Download,
  Tag,
  Sparkles,
  Trash2,
  MessageSquare,
  MessageSquarePlus,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import {
  composeVideoCssTransform,
  hasUserVideoTransform,
  normalizeRotationDeg,
  resolveVideoCssTransform,
} from "../utils/videoDisplayTransform";
import { buildDisplayViewSetup, isViewMirroredX } from "../utils/videoOrientCoords";
import {
  attachCameraSetupToViewSetup,
  clipCameraFromSources,
  formatCameraSetupLabel,
} from "../utils/evaluationCameraSetup";
import { resolveEvaluationVideoReadyWall, formatEvalWallMs } from "../utils/evaluationVideoMediaMeta";
import {
  isModeratorStaff,
  readActiveHomeRole,
  readStoredUser,
} from "../utils/homeRole";
import {
  DEFAULT_FPS as PLAYER_DEFAULT_FPS,
  getVideoFrameDurationSeconds,
  probeVideoSourceFps,
} from "../utils/videoFrameDuration";
import { useVideoPinchZoom } from "../hooks/useVideoPinchZoom";
import {
  buildVideoViewSetupPayload,
  hasSavedVideoViewSetup,
  hrAtVideoTime,
  parseVideoViewSetup,
} from "../utils/videoViewSetup";
import {
  fetchOrEnqueuePoseResult,
  tryLoadExistingPose,
  flushPoseResults,
  detectPoseCalibrationFrame,
  detectPoseInRoi,
  poseResultHasBothPlayers,
  fetchPose3dResult,
  refreshTableDependentMetrics,
} from "../api/poseAnalysisApi";
import {
  fetchStrokeAnalysis,
  saveStrokeAnalysis,
} from "../api/strokeAnalysisApi";
import {
  fetchVideoAnnotations,
  saveVideoAnnotations,
} from "../api/videoAnnotationApi";
import {
  createFeedbackRecorder,
  eventsDue,
  feedbackAnchorMs,
  formatFeedbackClock,
  frameFromVideoMs,
  normalizeFeedbackFps,
  pickAutoFeedbackSession,
  releaseConsumedFeedback,
  sessionHasContent,
  sessionReplayDurationMs,
  normalizeSessionForReplay,
  noteBelongsToSession,
  strokesVisibleAt,
  feedbackAtPlayhead,
} from "../utils/feedbackSession";
import {
  buildStrokeAnalysisDocument,
} from "../utils/strokeGestureAnalysis";
import Pose3DComparePanel from "./pose/Pose3DComparePanel";
import "./pose3d/pose3d.css";
import { pose3dMatchesSegment } from "./pose3d/pose3dCoords";
import {
  fetchOrEnqueueBallResult,
  tryLoadExistingBall,
  flushBallResults,
  poseStrokeContactMs,
  BALL_STROKE_KINDS,
  buildBallStrokeWindows,
  countStrokesForKind,
  normalizeBallStrokeKind,
  ballPlayerBoxesMode,
  ballPlayerRoisReady,
} from "../api/ballAnalysisApi";
import { reencodeEvaluationVideo, formatReencodeEta, reencodeJobProgressPct } from "../api/evaluationVideoBakeApi";
import { uploadEvaluationVideo } from "../api/evaluationVideoUploadApi";
import {
  resolveSignedMediaUrl,
  peekSignedMediaUrl,
  playableMediaSrc,
  rememberSignedMediaUrl,
  isTtdataUrl,
  needsSignedMediaUrl,
  isAlreadySignedMediaUrl,
  clearSignedMediaUrlCache,
} from "../api/signedMediaUrl";
import { exportStrokeSlowMoReel } from "../utils/exportStrokeSlowMoReel";
import {
  detectionsFromPoseResult,
  pickReelOverlayPlayers,
  planMaxRallyExport,
  planStrokeSlowMoExport,
  buildStrokeMotionRange,
  warmupTitleForReel,
} from "../utils/strokeSlowMoReel";
import {
  adjacentPointSeekSec,
  nextPointSeekSec,
  resolveKotPointSegments,
  snapToPointPlayback,
} from "../utils/kotPointSegments";
import {
  enqueueSegmentJob,
  fetchSegmentResults,
  tryLoadExistingSegment,
} from "../api/segmentAnalysisApi";
import {
  getActiveTableGeometry,
  saveTableGeometry,
} from "../api/tableGeometryApi";
import { sampleForPlayback } from "../utils/poseTimeline";
import {
  jointOverlaySampleFromCalibration,
  jointOverlaySampleFromRoiDetection,
  jointOverlaySampleFromSides,
} from "../utils/strokeSlowMoKneeOverlay";
import { roisFromSuggestedPlayers } from "../utils/playerRoiFromJoints";
import { capturePausedVideoJpeg } from "../utils/videoFrameCapture";
import {
  PoseSkeletonOverlay,
  TABLE_HIGHLIGHT_MS,
} from "./pose/PoseSkeletonOverlay";
import { JointAngleOverlay } from "./pose/JointAngleOverlay";
import {
  BallTrackOverlay,
  ballStateAt,
} from "./pose/BallTrackOverlay";
import { PoseAnglesPanel } from "./pose/PoseAnglesPanel";
import { PoseAngleCharts } from "./pose/PoseAngleCharts";
import { PoseProcessingOverlay } from "./pose/PoseProcessingOverlay";
import { TableCornerCalibrationOverlay } from "./pose/TableCornerCalibrationOverlay";
import { PlayerCalibrationOverlay } from "./pose/PlayerCalibrationOverlay";
import { ConfirmCalibrationOverlay } from "./pose/ConfirmCalibrationOverlay";
import { DetectionRoiOverlay } from "./pose/DetectionRoiOverlay";
import { useClubLicense } from "../context/ClubLicenseContext";
import {
  clampDetectionRoiNorm,
  computeDetectionRoiNorm,
  normalizeStoredDetectionRoi,
} from "../utils/detectionRoi";
import { resolveBallDebug } from "../utils/ballDebug";
import {
  detectBhTopspinLeft,
  detectBhLeft,
  detectFhLeft,
  detectFhTopspinLeft,
  detectServes,
} from "../utils/poseHeuristics";
import { pickTableCorners, cornersChanged } from "../utils/tableCorners";
import { applyTableCornersToPoseResult } from "../utils/applyTableCornersToPose";
import { VideoAnalysisSettingsDialog } from "./VideoAnalysisSettingsDialog";
import {
  loadVideoAnalysisSettings,
  roisForPoseDetectionPlayer,
  normalizeVideoAnalysisSettings,
  normalizePoseDetectionPlayer,
} from "../utils/videoAnalysisSettings";
import { ip_back } from "../config";
import { redirectToLoginIfSessionExpired } from "../utils/authExpired";
import "./ReviewVideoPlayer.css";
import "./pose/poseOverlays.css";

function pickActiveHrPoints(hrSeries, hrPoints, activePlayerId) {
  const hasPoints = (entry) =>
    Array.isArray(entry?.points) && entry.points.length > 0;
  if (Array.isArray(hrSeries) && hrSeries.length) {
    const pid = activePlayerId != null ? String(activePlayerId) : "";
    const match =
      (pid &&
        hrSeries.find(
          (entry) => String(entry?.playerId || "") === pid && hasPoints(entry)
        )) ||
      hrSeries.find(hasPoints);
    if (hasPoints(match)) return match.points;
  }
  return Array.isArray(hrPoints) && hrPoints.length ? hrPoints : null;
}

function strokeTitleForDetection(id, t) {
  if (id === "fh_left") return t("reelStrokeTitleFh", "Forehand");
  if (id === "bh_topspin_left") {
    return t("reelStrokeTitleBhTopspin", "Topspin Backhand");
  }
  if (id === "bh_left") return t("reelStrokeTitleBh", "Backhand");
  return t("reelStrokeTitleFhTopspin", "Topspin Forehand");
}

const PLAYBACK_SPEEDS = [0.2, 0.5, 1];
const Pose3DWebViewer = lazy(() => import("./pose3d/Pose3DWebViewer"));

const SEEK_SECONDS = 3;
const UI_HIDE_MS = 2000;
/**
 * Mid-play buffer stall → treat as incomplete/corrupt instead of hanging forever.
 * Only fires when playback time also stops advancing (slow S3 starts are OK).
 */
const BUFFER_STALL_MS = 45000;
/**
 * Initial open: large progressive MP4s (championship ~50MB) often keep
 * readyState=0 until the index arrives near the end. Do not hard-fail while
 * buffered ranges keep growing. Only soft-stall after this idle gap.
 */
const INITIAL_LOAD_IDLE_MS = 90000;

/**
 * Chrome's default pitch preservation periodically speeds the picture back up
 * at 0.5× / 0.2× (about every few seconds). Keep pitch only at 1×.
 */
function applyPlaybackRate(video, rate) {
  if (!video) return;
  const next = Number(rate) > 0 ? Number(rate) : 1;
  const preserve = Math.abs(next - 1) < 0.001;
  try {
    if (video.preservesPitch !== preserve) video.preservesPitch = preserve;
    if (video.mozPreservesPitch !== preserve) video.mozPreservesPitch = preserve;
    if (video.webkitPreservesPitch !== preserve) video.webkitPreservesPitch = preserve;
  } catch {
    /* older browsers */
  }
  if (video.playbackRate !== next) {
    try {
      video.playbackRate = next;
    } catch {
      /* ignore */
    }
  }
}

/** Ultra-slow (0.2×) replay defaults to muted — chippy pitch is hard to listen to. */
function isUltraSlowPlaybackRate(rate) {
  return Math.abs(Number(rate) - 0.2) < 0.001;
}

/** Keeps review playback inside one player's chronometer. Past the end, park at the start. */
function clampPlaybackWindow(time, windowRange) {
  const start = Number(windowRange?.start);
  const end = Number(windowRange?.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start + 0.05) {
    return { time, held: false, start: 0, end: null };
  }
  if (time >= end - 0.04) {
    return { time: Math.max(start, end - 0.08), held: true, start, end };
  }
  if (time < start) {
    return { time: start, held: false, start, end };
  }
  return { time, held: false, start, end };
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatVideoResLabel(w, h) {
  const width = Number(w) || 0;
  const height = Number(h) || 0;
  if (!(width > 0 && height > 0)) return null;
  const short = Math.min(width, height);
  if (short >= 2160) return "4K";
  if (short >= 1440) return "1440p";
  if (short >= 1080) return "1080p";
  if (short >= 720) return "720p";
  if (short >= 480) return "480p";
  return `${width}×${height}`;
}

function formatFileSizeLabel(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) {
    const mb = n / (1024 * 1024);
    return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
  }
  const gb = n / (1024 * 1024 * 1024);
  return `${gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
}

function formatCodecLabel(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (
    s === "h264" ||
    s === "avc1" ||
    s === "avc" ||
    s === "avc3" ||
    s.includes("h.264")
  ) {
    return "H.264";
  }
  if (
    s === "hevc" ||
    s === "h265" ||
    s === "hvc1" ||
    s === "hev1" ||
    s.includes("h.265")
  ) {
    return "H.265";
  }
  if (s === "av1" || s === "av01") return "AV1";
  if (s === "vp9" || s === "vp09") return "VP9";
  if (s === "vp8") return "VP8";
  return String(raw).trim().toUpperCase();
}

function formatExactResLabel(w, h) {
  const width = Number(w) || 0;
  const height = Number(h) || 0;
  if (!(width > 0 && height > 0)) return null;
  const short = formatVideoResLabel(width, height);
  const exact = `${width}×${height}`;
  if (short && short !== exact) return `${exact} · ${short}`;
  return exact;
}

function formatFpsValue(fps) {
  const n = Number(fps);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) ? `${n} fps` : `${n.toFixed(1)} fps`;
}

function poseOriginLabel(settings) {
  const src = String(
    settings?.source || settings?.device_tier || settings?.device || ""
  ).toLowerCase();
  if (!src) return null;
  if (
    src.includes("ios") ||
    src.includes("on_device") ||
    src.includes("on-device") ||
    src.includes("iphone") ||
    src.includes("phone") ||
    src.includes("lidar")
  ) {
    return "iPhone";
  }
  if (src.includes("gpu") || src.includes("cuda") || src.includes("metal")) {
    return "Remote GPU";
  }
  if (src.includes("cpu")) return "Remote server (CPU)";
  if (src.includes("server") || src.includes("web")) return "Remote server";
  return null;
}

function pose3dModeLabel(mode) {
  const m = String(mode || "").toLowerCase();
  if (m === "mono_world" || m === "mono") return "Mono (1 camera)";
  if (m === "multi_triangulate" || m === "multi") return "Multi-camera";
  if (m === "lidar_iphone" || m === "lidar") return "iPhone LiDAR";
  if (m === "strided_transformer") return "StridedTransformer";
  return mode || null;
}

function ballDetectorLabel(detector) {
  const d = String(detector || "").toLowerCase();
  if (d === "tracknet") return "TrackNet";
  if (d === "fmo") return "FMO";
  if (d === "yolov5") return "YOLOv5";
  if (d === "yolo26") return "YOLO26";
  return detector || null;
}

function ballSelectionMatches(result, detector, strokeKind) {
  if (!result) return false;
  if ((result.settings?.detector || "tracknet") !== detector) return false;
  return (
    normalizeBallStrokeKind(result.settings?.stroke_kind) ===
    normalizeBallStrokeKind(strokeKind)
  );
}

const BALL_DETECTORS = new Set(["tracknet", "fmo", "yolov5", "yolo26"]);

function hudRow(label, value) {
  if (value == null || value === "") return null;
  return { label, value: String(value) };
}

function formatHitPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${Math.round(n * 100)}%`;
}

function formatDropMap(drops, frames) {
  if (!drops || typeof drops !== "object") return null;
  const nFrames = Number(frames) || 0;
  const entries = Object.entries(drops)
    .filter(([, n]) => Number(n) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (!entries.length) return "none";
  return entries
    .slice(0, 4)
    .map(([key, n]) => {
      const count = Number(n);
      if (nFrames > 0) {
        return `${key} ${count} (${Math.round((count / nFrames) * 100)}%)`;
      }
      return `${key} ${count}`;
    })
    .join(" · ");
}

/** Detect video codec fourcc from an MP4 header buffer. */
function detectMp4VideoCodecFromBuffer(buffer) {
  if (!buffer || !(buffer.byteLength > 16)) return null;
  let latin = "";
  try {
    latin = new TextDecoder("latin1").decode(new Uint8Array(buffer));
  } catch {
    const bytes = new Uint8Array(buffer);
    const parts = [];
    for (let i = 0; i < bytes.length; i += 1) {
      parts.push(String.fromCharCode(bytes[i]));
    }
    latin = parts.join("");
  }
  if (/hvc1|hev1|hvcc/i.test(latin)) return "H.265";
  if (/avc1|avc3|avcC/i.test(latin)) return "H.264";
  if (/av01|av1C/i.test(latin)) return "AV1";
  if (/vp09|vpcC/i.test(latin)) return "VP9";
  return null;
}

function parseContentLengthFromHeaders(headers) {
  if (!headers) return null;
  const cr = String(headers.get("content-range") || "");
  const m = /\/(\d+)\s*$/.exec(cr);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  // Only trust Content-Length when it is not a partial range response without Content-Range.
  const cl = Number(headers.get("content-length"));
  if (Number.isFinite(cl) && cl > 0 && !cr) return cl;
  return null;
}

/**
 * Resolve remote file size + codec for the analysis HUD.
 * OVH S3 is not CORS-enabled — never Range-GET ttdata from the browser
 * (Chrome logs that as a failed Fetch). Use videoHeaderBytes instead.
 */
async function fetchVideoFileInfo(url, { timeoutMs = 4000 } = {}) {
  const target = String(url || "").trim();
  if (!target || !/^https?:\/\//i.test(target)) {
    return { sizeBytes: null, codec: null };
  }

  let sizeBytes = null;
  let codec = null;
  const isRemoteS3 =
    /ttdata\.s3\.|s3\.gra\.io\.cloud\.ovh\.net/i.test(target);

  if (!isRemoteS3) {
    try {
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = controller
        ? window.setTimeout(() => controller.abort(), timeoutMs)
        : null;
      try {
        const response = await fetch(target, {
          headers: { Range: "bytes=0-262143" },
          credentials: "omit",
          signal: controller?.signal,
        });
        if (response.ok || response.status === 206) {
          sizeBytes = parseContentLengthFromHeaders(response.headers);
          const buffer = await response.arrayBuffer();
          codec = detectMp4VideoCodecFromBuffer(buffer);
        }
      } finally {
        if (timer != null) window.clearTimeout(timer);
      }
    } catch {
      /* try API fallback */
    }

    if (codec && sizeBytes) {
      return { sizeBytes, codec };
    }
  }

  try {
    const token =
      (typeof window !== "undefined" && localStorage.getItem("token")) || "";
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const response = await fetch(`${ip_back}api/mobileAPI/videoHeaderBytes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({ url: target }),
        signal: controller?.signal,
      });
      if (response.ok) {
        const json = await response.json().catch(() => ({}));
        const b64 = json?.data?.bytes_base64;
        if (b64 && !codec) {
          const binary = atob(b64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
          }
          codec = detectMp4VideoCodecFromBuffer(bytes.buffer);
        }
        if (!codec) {
          codec = formatCodecLabel(json?.data?.video_codec);
        }
        const apiLen = Number(json?.data?.content_length);
        if (!sizeBytes && Number.isFinite(apiLen) && apiLen > 0) {
          sizeBytes = apiLen;
        }
      }
    } finally {
      if (timer != null) window.clearTimeout(timer);
    }
  } catch {
    /* ignore */
  }

  if (!sizeBytes && !isRemoteS3) {
    try {
      const head = await fetch(target, {
        method: "HEAD",
        credentials: "omit",
      });
      const cl = Number(head.headers.get("content-length"));
      if (Number.isFinite(cl) && cl > 0) sizeBytes = cl;
    } catch {
      /* ignore */
    }
  }

  return { sizeBytes, codec };
}

function clampTime(time, duration) {
  if (!Number.isFinite(duration) || duration <= 0) return Math.max(0, time);
  return Math.min(Math.max(0, time), duration);
}

/** Scrubber / seek ceiling when `duration` is 0, NaN, or Infinity (progressive MP4). */
function mediaSeekHorizon(video) {
  if (!video) return 0;
  const dur = Number(video.duration);
  if (Number.isFinite(dur) && dur > 0) return dur;
  try {
    const { seekable } = video;
    if (seekable && seekable.length > 0) {
      const end = Number(seekable.end(seekable.length - 1));
      if (Number.isFinite(end) && end > 0) return end;
    }
  } catch {
    /* ignore */
  }
  const t = Number(video.currentTime);
  // Keep the range movable before metadata arrives.
  if (Number.isFinite(t) && t >= 0) return Math.max(t + 120, 1);
  return 1;
}

/** Start a short clip immediately. Mobile autoplay with sound is often blocked. */
function playReviewClip(video, { eager = false, onMuted } = {}) {
  if (!video) return;
  let attempt;
  try {
    attempt = video.play();
  } catch {
    return;
  }
  if (!attempt || typeof attempt.catch !== "function") return;
  attempt.catch(() => {
    if (!eager) return;
    try {
      video.muted = true;
    } catch {
      /* ignore */
    }
    onMuted?.();
    video.play?.().catch(() => {});
  });
}

/**
 * Review video player with pinch/trackpad zoom, pan, keyboard transport,
 * orientation tools, and saved view setup.
 *
 * Keyboard (when view is ready): Space play/pause, ←/→ ±3s, f/b ±1 frame.
 */
export function ReviewVideoPlayer({
  url,
  mode = "embedded",
  autoPlay = false,
  className = "",
  videoClassName = "",
  onLoadedMetadata,
  onMediaEnded,
  onMediaTimeUpdate,
  onMediaError,
  mediaElRef,
  canTransformVideo = false,
  messageId = null,
  initialViewSetup = null,
  saveViewSetup,
  onViewSetupSaved,
  onRequestReupload,
  hrPoints = null,
  hrSeries = null,
  sequenceTimestamp = 0,
  teId = null,
  tableId = null,
  enablePose = false,
  /** Optional override; otherwise uses coach settings from localStorage. */
  poseFps = null,
  /** 1-based sequence_order for multi-sequence exercises. */
  sequenceOrder = null,
  exerciseTypeEn = "",
  exerciseSubtypeEn = "",
  exerciseNameEn = "",
  /** Active roster player_id for stroke slow-mo reel upload. */
  activePlayerId = null,
  /** Sequence row (player_a_role / player_b_role) for pose side + upload context. */
  sequenceMeta = null,
  /** Names / roles / levels burned onto max-rally reels. */
  reelOwners = null,
  onStrokeReelUploaded = null,
  /** King of the Table match clip — enables points-only playback when segments exist. */
  isKotMatch = false,
  kotMatchId = null,
  /**
   * Short autoeval / evaluation sequence clip (not KoT, not personal match).
   * Game-points UI is hidden for these; shown for KoT, personal match uploads,
   * and all other pose-enabled videos (coach video feedback, session media).
   */
  isEvaluationVideo = false,
  /** True when the loaded clip is already a generated max-rally / slow-mo reel. */
  isShareableReel = false,
  shareableReelKind = "max_rally",
  /** Eval/KOT: paused-frame knees/elbows overlay for every role. */
  enableJointOverlay = false,
  /** Joint overlay draws every detected player instead of only the left one. */
  jointOverlayAllPlayers = false,
  /** Seek once after metadata loads (seconds into this URL). */
  initialCurrentTime = null,
  /**
   * When both are set, playback and the scrubber stay inside this span.
   * Used so a player or parent only sees that player's chronometer.
   */
  playbackWindowStart = null,
  playbackWindowEnd = null,
  /** Override chrome auto-hide delay (ms). Defaults to 2s. */
  controlsHideMs = null,
  /**
   * Match review: play / seek only. Feedback stays off unless the club
   * player in the clip opens it.
   */
  basicPlayback = false,
  /**
   * Full evaluation player, but existing feedback stays closed until the
   * club player taps the feedback button.
   */
  feedbackOnDemand = false,
  /** True when this viewer is a club player who appears in the clip. */
  feedbackViewer = false,
  /**
   * Short clip (table replay): buffer immediately and start on the first
   * frame instead of waiting for canplaythrough.
   */
  eagerPlayback = false,
  /**
   * Score-box drawing: one finger draws. Pinch and pan stay on two fingers
   * so a drag does not slide the picture.
   */
  twoFingerGesturesOnly = false,
}) {
  const { t, i18n } = useTranslation();
  const canSeeVideoAnalysisInfo = useMemo(() => {
    const user = readStoredUser();
    const homeRole = readActiveHomeRole(user);
    if (isModeratorStaff(user, { homeRole })) return true;
    // Coaches / admins reviewing a match need the same clip metadata.
    return (
      homeRole === "coach" ||
      homeRole === "admin" ||
      homeRole === "committee" ||
      homeRole === "moderator"
    );
  }, []);
  const { hasFeature, tier } = useClubLicense();
  const videoRef = useRef(null);
  const viewportRef = useRef(null);
  const rootRef = useRef(null);
  const [toolbarHost, setToolbarHost] = useState(null);
  const frameDurationRef = useRef(1 / PLAYER_DEFAULT_FPS);
  const fpsProbeGenRef = useRef(0);
  const suppressClickRef = useRef(0);
  const appliedSetupKeyRef = useRef("");
  const pendingAutoPlayRef = useRef(false);
  /** After 1f / F/B seek, some browsers resume playback — force stay paused. */
  const holdPausedAfterSeekRef = useRef(false);
  const holdPausedGenRef = useRef(0);
  const holdPausedTimerRef = useRef(0);
  const holdPausedSeekedRef = useRef(null);
  const setupPreparedRef = useRef(false);
  const metadataLoadGenRef = useRef(0);
  const initialSeekAppliedRef = useRef("");
  const bufferStallTimerRef = useRef(0);
  /** Wall-clock + media time when a buffer wait started — progress resets the stall. */
  const bufferStallAnchorRef = useRef({ mediaTime: 0, atMs: 0 });
  /** Samples of bufferedEnd for ETA / percent while loading or mid-play buffering. */
  const loadProgressSamplesRef = useRef([]);
  const [analysisSettings, setAnalysisSettings] = useState(() =>
    loadVideoAnalysisSettings()
  );
  const [analysisSettingsOpen, setAnalysisSettingsOpen] = useState(false);
  const [strokeLabelOpen, setStrokeLabelOpen] = useState(false);
  const [strokeAnalysisOpen, setStrokeAnalysisOpen] = useState(false);
  const [storedStrokeAnalyses, setStoredStrokeAnalyses] = useState(null);
  const [annotateOpen, setAnnotateOpen] = useState(false);
  const [feedbackModeOpen, setFeedbackModeOpen] = useState(false);
  /** Compact list of saved feedbacks (HUD + feedback-review bar). */
  const [feedbackListOpen, setFeedbackListOpen] = useState(false);
  const annotateOpenRef = useRef(false);
  const [frameNotes, setFrameNotes] = useState([]);
  const [feedbackSessions, setFeedbackSessions] = useState([]);
  const [replaySessionId, setReplaySessionId] = useState(null);
  const [replayElapsed, setReplayElapsed] = useState(0);
  const [autoPlayFeedback, setAutoPlayFeedback] = useState(() => {
    try {
      const raw =
        typeof window !== "undefined"
          ? window.localStorage.getItem("smarttt:autoPlayFeedback")
          : null;
      if (raw === "0" || raw === "false") return false;
      return true;
    } catch {
      return true;
    }
  });
  const feedbackRecorderRef = useRef(null);
  const feedbackLiveRef = useRef({
    notes: [],
    sessions: [],
    angles: "off",
    pose: false,
  });
  const replayAppliedRef = useRef(0);
  const replayStartedRef = useRef(0);
  const replayVoiceRef = useRef(null);
  const replayScreenRef = useRef(null);
  const deletedFeedbackRef = useRef(new Set());
  const feedbackSeedRef = useRef(null);
  const screenCaptureRef = useRef(null);
  const [feedbackRecording, setFeedbackRecording] = useState(false);
  /** Sessions already played until the viewer rewinds before them. */
  const consumedFeedbackRef = useRef(new Set());
  const prevFeedbackTimeRef = useRef(0);
  const replayEndedAtRef = useRef(0);
  const arrivalSecRef = useRef(null);
  const zoomBeforeReplayRef = useRef(null);
  const replaySessionRef = useRef(null);
  const applyReplayEventRef = useRef(() => {});
  const releaseHoldPausedRef = useRef(() => {});
  /** Auto-play arrival pauses the clip; ignore the recording's opening play snapshot. */
  const suppressSeedPlayRef = useRef(false);
  /** Resume the clip after an auto-play feedback take finishes. */
  const resumeAfterFeedbackRef = useRef(false);
  const strokeAnalysisSaveSigRef = useRef("");

  useEffect(() => {
    const onSettings = (event) => {
      setAnalysisSettings(
        normalizeVideoAnalysisSettings(event?.detail || loadVideoAnalysisSettings())
      );
    };
    window.addEventListener("smarttt:video-analysis-settings", onSettings);
    return () =>
      window.removeEventListener("smarttt:video-analysis-settings", onSettings);
  }, []);

  const effectivePoseFps = useMemo(() => {
    const fromProp = Number(poseFps);
    if (Number.isFinite(fromProp) && fromProp > 0) {
      return Math.max(0.5, Math.min(60, fromProp));
    }
    return analysisSettings.poseFps;
  }, [poseFps, analysisSettings.poseFps]);

  const viewSetupIdentity = useMemo(() => {
    if (initialViewSetup == null) return "null";
    if (typeof initialViewSetup === "string") return initialViewSetup;
    try {
      return JSON.stringify(initialViewSetup);
    } catch {
      return String(initialViewSetup);
    }
  }, [initialViewSetup]);

  const parsedViewSetup = useMemo(
    () => parseVideoViewSetup(initialViewSetup),
    // viewSetupIdentity collapses new object identities with the same content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewSetupIdentity]
  );
  const effectiveKotMatch = useMemo(() => {
    if (isKotMatch) return true;
    if (kotMatchId) return true;
    if (parsedViewSetup?.kot_match_id) return true;
    return false;
  }, [isKotMatch, kotMatchId, parsedViewSetup]);
  const keepEvalKotTransport = Boolean(isEvaluationVideo || effectiveKotMatch);
  /** Detect / play-only game points: KoT, personal match uploads, and non-eval clips. */
  const enableGamePoints = Boolean(
    enablePose && teId && (effectiveKotMatch || !isEvaluationVideo)
  );
  /** Knees/elbows overlay for eval/KOT viewers and any pose-enabled coach session. */
  const jointOverlayEnabled = Boolean(enableJointOverlay || enablePose);
  /** Staff/coach: pick A/B and require a player box for angle detection. */
  const jointOverlayCoachControls = Boolean(
    jointOverlayEnabled && !jointOverlayAllPlayers && canSeeVideoAnalysisInfo
  );
  const needsViewGate = hasSavedVideoViewSetup(parsedViewSetup);

  const webPlaybackStatus = useMemo(() => {
    let data = initialViewSetup;
    if (data == null) return null;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return null;
      }
    }
    if (!data || typeof data !== "object") return null;
    const status = data.web_playback?.status;
    return status ? String(status) : null;
  }, [initialViewSetup]);
  /** Live status while polling — overrides the snapshot from when the player opened. */
  const [liveWebPlaybackStatus, setLiveWebPlaybackStatus] = useState(null);
  const effectiveWebPlaybackStatus = liveWebPlaybackStatus || webPlaybackStatus;
  const viewSetupKey = useMemo(() => {
    if (!parsedViewSetup) return `${url || ""}::null`;
    return `${url || ""}::${parsedViewSetup.rotationDeg}|${parsedViewSetup.flipH}|${parsedViewSetup.flipV}|${parsedViewSetup.zoomScale}|${parsedViewSetup.panXRatio}|${parsedViewSetup.panYRatio}`;
  }, [url, parsedViewSetup]);

  // Seed transforms from saved setup so the first painted frame already has them.
  const [metadataTransform, setMetadataTransform] = useState("");
  const [rotationDeg, setRotationDeg] = useState(
    () => parsedViewSetup?.rotationDeg || 0
  );
  // Charts (Recharts × full pose samples) freeze the main thread on open —
  // keep them off until the coach explicitly enables them.
  const [poseChartsVisible, setPoseChartsVisible] = useState(false);
  const [flipH, setFlipH] = useState(() => Boolean(parsedViewSetup?.flipH));
  const [flipV, setFlipV] = useState(() => Boolean(parsedViewSetup?.flipV));
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const playbackWindowRef = useRef({ start: null, end: null });
  playbackWindowRef.current = {
    start: playbackWindowStart,
    end: playbackWindowEnd,
  };
  const [playbackRate, setPlaybackRate] = useState(1);
  /** Playback mute — independent of the view-gate muted=!viewReady silence. */
  const [audioMuted, setAudioMuted] = useState(false);
  /** User's explicit mute preference (survives leaving 0.2× auto-mute). */
  const userAudioMutedRef = useRef(false);
  const [showControls, setShowControls] = useState(false);
  const [chromeEpoch, setChromeEpoch] = useState(0);
  const [topChromeCollapsed, setTopChromeCollapsed] = useState(() =>
    Boolean(canTransformVideo)
  );
  const [jointOverlayMode, setJointOverlayMode] = useState("off");
  /** Coach target for knees/elbows: A = left (default), B = right. */
  const [jointOverlayPlayer, setJointOverlayPlayer] = useState("A");
  const [jointOverlaySample, setJointOverlaySample] = useState(null);
  const [jointOverlayLoading, setJointOverlayLoading] = useState(false);
  const jointOverlayAbortRef = useRef(null);
  const jointOverlayStoreRef = useRef(new Map());
  const jointOverlaySide =
    normalizePoseDetectionPlayer(jointOverlayPlayer) === "B" ? "right" : "left";
  const fpsProbedSrcRef = useRef("");
  const [saving, setSaving] = useState(false);
  const [reuploading, setReuploading] = useState(false);
  const [requestingPhoneReupload, setRequestingPhoneReupload] = useState(false);
  /** @type {[{ pct: number, etaSeconds: number|null, phase: string }|null, Function]} */
  const [reencodeProgress, setReencodeProgress] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  // Only hide first paint when a non-identity saved view exists.
  const [viewReady, setViewReady] = useState(true);
  const [setupPrepared, setSetupPrepared] = useState(true);
  // Keep the real URL on <video> at all times. Clearing src paints about:blank
  // (sandbox console noise) and can stall metadata forever on some browsers.
  const [mediaSrc, setMediaSrc] = useState(() => {
    if (!url) return null;
    if (!isTtdataUrl(url) || isAlreadySignedMediaUrl(url)) return url;
    return peekSignedMediaUrl(url);
  });
  const [mediaSigning, setMediaSigning] = useState(() => {
    if (!url || !needsSignedMediaUrl(url) || isAlreadySignedMediaUrl(url)) {
      return false;
    }
    return !peekSignedMediaUrl(url);
  });
  const [mediaLoadError, setMediaLoadError] = useState("");
  const [isBuffering, setIsBuffering] = useState(false);
  /** True until playback can actually start — covers large progressive opens. */
  const [isMediaOpening, setIsMediaOpening] = useState(() => Boolean(url));
  const isMediaOpeningRef = useRef(Boolean(url));
  const eagerPlaybackRef = useRef(eagerPlayback);
  eagerPlaybackRef.current = eagerPlayback;
  /** Soft notice while a large progressive file is still downloading slowly. */
  const [loadIdleSlow, setLoadIdleSlow] = useState(false);
  /** @type {[{ percent: number|null, etaSec: number|null, bufferedEnd: number, duration: number, indeterminate: boolean }|null, Function]} */
  const [mediaLoadProgress, setMediaLoadProgress] = useState(null);
  const strokePlayStopRef = useRef(null);

  useEffect(() => {
    isMediaOpeningRef.current = isMediaOpening;
  }, [isMediaOpening]);

  // Private OVH objects 403 without a signature. Never point <video> at the
  // bare URL: that 403 arrives after the signed URL is ready and Chrome
  // reports it as "this format can't be played".
  useEffect(() => {
    let cancelled = false;
    const raw = url || null;
    if (!raw) {
      setMediaSrc(null);
      setMediaSigning(false);
      return undefined;
    }
    if (playingStrokeReelRef.current) {
      setMediaSigning(false);
      return undefined;
    }
    if (!isTtdataUrl(raw) || isAlreadySignedMediaUrl(raw)) {
      if (isAlreadySignedMediaUrl(raw)) rememberSignedMediaUrl(raw);
      setMediaSigning(false);
      setMediaSrc(raw);
      return undefined;
    }
    const cached = peekSignedMediaUrl(raw);
    if (cached) {
      setMediaSigning(false);
      setMediaSrc(cached);
      return undefined;
    }
    setMediaSigning(true);
    setMediaLoadError("");
    // Keep the previous playable src while resigning a remux URL — clearing it
    // blanked the player and looked like the coach viewer closed itself.
    resolveSignedMediaUrl(raw)
      .then((signed) => {
        if (cancelled) return;
        if (signed && isAlreadySignedMediaUrl(signed)) {
          setMediaSrc(signed);
          setMediaLoadError("");
          return;
        }
        setMediaSrc(null);
        setMediaLoadError(
          t(
            "videoLoadPrivateStorage",
            "This clip is stored privately and could not be authorized for playback. Retry, or ask an admin to check Object Storage signing."
          )
        );
      })
      .catch(() => {
        if (cancelled) return;
        setMediaSrc(null);
        setMediaLoadError(
          t(
            "videoLoadPrivateStorage",
            "This clip is stored privately and could not be authorized for playback. Retry, or ask an admin to check Object Storage signing."
          )
        );
      })
      .finally(() => {
        if (!cancelled) setMediaSigning(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url, t]);

  const [poseResult, setPoseResult] = useState(null);
  const [poseOverlayOn, setPoseOverlayOn] = useState(false);
  const [poseAnalyzing, setPoseAnalyzing] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [poseProgress, setPoseProgress] = useState(0);
  const [poseEtaSeconds, setPoseEtaSeconds] = useState(null);
  const [poseError, setPoseError] = useState("");

  const [ballResult, setBallResult] = useState(null);
  const [segmentResult, setSegmentResult] = useState(null);
  const [, setKotSegmentSource] = useState(null);
  const [, setKotDetectStatus] = useState("");
  const [kotDetectRequested, setKotDetectRequested] = useState(false);
  const [kotDetectNonce, setKotDetectNonce] = useState(0);
  const [, setKotDetectPhase] = useState("idle");
  const [kotPointsOnly, setKotPointsOnly] = useState(false);
  const kotPointsOnlyRef = useRef(false);
  const kotPointSegmentsRef = useRef([]);
  const [ballOverlayOn, setBallOverlayOn] = useState(true);
  const [ballAnalyzing, setBallAnalyzing] = useState(false);
  const [ballProgress, setBallProgress] = useState(0);
  const [ballEtaSeconds, setBallEtaSeconds] = useState(null);
  const [ballError, setBallError] = useState("");
  const [ballDetector, setBallDetector] = useState("tracknet");
  const [ballStrokeKind, setBallStrokeKind] = useState("all");
  const ballAbortRef = useRef(null);
  const strokeReelAbortRef = useRef(null);
  const [strokeReelExporting, setStrokeReelExporting] = useState(false);
  const [strokeReelProgress, setStrokeReelProgress] = useState(0);
  const [strokeReelStatus, setStrokeReelStatus] = useState("");
  const [strokeReelError, setStrokeReelError] = useState("");
  const [playingStrokeReel, setPlayingStrokeReel] = useState(false);
  const playingStrokeReelRef = useRef(false);
  const strokeReelBlobUrlRef = useRef(null);
  const strokeReelBlobRef = useRef(null);
  const strokeReelKindRef = useRef("max_rally");
  const strokeReelOriginalSrcRef = useRef(null);

  useEffect(() => {
    if (shareableReelKind) setShareReelKind(shareableReelKind);
  }, [shareableReelKind]);

  /** FPS actually used for the loaded/completed analysis (this clip). */
  const usedPoseFps = useMemo(() => {
    const n = Number(poseResult?.settings?.pose_fps);
    if (Number.isFinite(n) && n > 0) return Math.max(0.5, Math.min(60, n));
    return null;
  }, [poseResult?.settings?.pose_fps]);

  /** Settings preference drives the chip; title explains analyzed vs next when they differ. */
  const displayedPoseFps = effectivePoseFps;
  const poseFpsMatchesResult =
    usedPoseFps == null || Math.abs(usedPoseFps - effectivePoseFps) < 0.05;
  const [analysisInfoOpen, setAnalysisInfoOpen] = useState(false);
  /** Inclusive frame range for pose; null = unset (full video / open end). */
  const [poseStartFrame, setPoseStartFrame] = useState(null);
  const [poseEndFrame, setPoseEndFrame] = useState(null);
  const [pose3dEnabled, setPose3dEnabled] = useState(false);
  const [pose3dMode, setPose3dMode] = useState("mono_world");
  const [pose3dJobId, setPose3dJobId] = useState("");
  const [pose3dStatus, setPose3dStatus] = useState("");
  const [pose3dResult, setPose3dResult] = useState(null);
  const [pose3dViewerOpen, setPose3dViewerOpen] = useState(false);
  const [showClipComposer, setShowClipComposer] = useState(false);
  const [showReelShareSheet, setShowReelShareSheet] = useState(false);
  const [shareReelKind, setShareReelKind] = useState(
    shareableReelKind || "max_rally"
  );
  const [pose3dCameraGroupId, setPose3dCameraGroupId] = useState("");
  const [pose3dPeerUrl, setPose3dPeerUrl] = useState("");
  const [pose3dPeerCameraId, setPose3dPeerCameraId] = useState("cam1");
  const [showPose3dCompare, setShowPose3dCompare] = useState(false);
  /** null | 'table' | 'players' | 'confirm' | 'detroi' — calibration wizard / confirm before pose/ball. */
  const [calibPhase, setCalibPhase] = useState(null);
  /** 'pose' = full wizard then pose; 'ball' = full wizard then ball detection. */
  const [calibIntent, setCalibIntent] = useState("pose");
  const [calibSaving, setCalibSaving] = useState(false);
  const [pendingCorners, setPendingCorners] = useState(null);
  /** Coach-drawn ROIs for Player A (left) / B (right); pose crops to these. */
  const [playerRois, setPlayerRois] = useState({ left: null, right: null });
  /** Pending ball run while coach reviews / redraws detection ROI. */
  const [pendingBallDetector, setPendingBallDetector] = useState(null);
  const [sessionCalibrated, setSessionCalibrated] = useState(false);
  const [swapPlayers, setSwapPlayers] = useState(false);
  const [activeGeometry, setActiveGeometry] = useState(null);
  const jointOverlaySelectionRoi = useMemo(() => {
    if (!jointOverlayCoachControls) return null;
    if (jointOverlaySide === "right") {
      return (
        playerRois.right ||
        activeGeometry?.roi_right ||
        poseResult?.settings?.roi_right ||
        null
      );
    }
    return (
      playerRois.left ||
      activeGeometry?.roi_left ||
      poseResult?.settings?.roi_left ||
      null
    );
  }, [
    jointOverlayCoachControls,
    jointOverlaySide,
    playerRois.left,
    playerRois.right,
    activeGeometry,
    poseResult,
  ]);
  const [videoPixelSize, setVideoPixelSize] = useState({ w: 0, h: 0 });
  /** Probed source fps for the clip currently loaded in <video> (info HUD). */
  const [videoSourceFps, setVideoSourceFps] = useState(null);
  const [videoFileMeta, setVideoFileMeta] = useState({
    sizeBytes: null,
    codec: null,
  });
  const poseAbortRef = useRef(null);
  const calibrating = calibPhase != null;
  const [calibToolbarEl, setCalibToolbarEl] = useState(null);
  const tableGlowPendingRef = useRef(false);
  const [tableHighlightUntil, setTableHighlightUntil] = useState(0);

  const startTableGlow = useCallback(() => {
    setTableHighlightUntil(Date.now() + TABLE_HIGHLIGHT_MS);
  }, []);

  const beginTableGlowIfPending = useCallback(() => {
    if (!tableGlowPendingRef.current) return;
    tableGlowPendingRef.current = false;
    startTableGlow();
  }, [startTableGlow]);

  useEffect(() => {
    if (!tableHighlightUntil) return undefined;
    const remaining = tableHighlightUntil - Date.now();
    if (remaining <= 0) {
      setTableHighlightUntil(0);
      return undefined;
    }
    const timer = window.setTimeout(() => setTableHighlightUntil(0), remaining);
    return () => window.clearTimeout(timer);
  }, [tableHighlightUntil]);

  const handleViewportTap = useCallback(() => {
    if (calibrating) return;
    if (keepEvalKotTransport) return;
    // Feedback review keeps its own bar — taps only toggle normal chrome.
    if (replaySessionId) return;
    suppressClickRef.current = Date.now();
    setShowControls((visible) => !visible);
    setChromeEpoch((n) => n + 1);
  }, [calibrating, keepEvalKotTransport, replaySessionId]);

  /** Restart the inactivity hide timer whenever the coach uses a control. */
  const controlsPointerDownRef = useRef(false);
  const pingControlsActivity = useCallback(() => {
    if (calibrating || keepEvalKotTransport || replaySessionId) return;
    setShowControls(true);
    setChromeEpoch((n) => n + 1);
  }, [calibrating, keepEvalKotTransport, replaySessionId]);

  const onControlsActivityCapture = useCallback(
    (e) => {
      // Empty chrome is pointer-events:none; ignore only if somehow the shell itself is hit.
      if (e.target === e.currentTarget) return;
      if (e.type === "pointerdown") {
        controlsPointerDownRef.current = true;
      }
      pingControlsActivity();
    },
    [pingControlsActivity]
  );

  const onControlsPointerUpCapture = useCallback(() => {
    if (!controlsPointerDownRef.current) return;
    controlsPointerDownRef.current = false;
    pingControlsActivity();
  }, [pingControlsActivity]);

  const {
    style: zoomStyle,
    scale,
    panX,
    panY,
    reset: resetZoom,
    apply: applyZoom,
  } = useVideoPinchZoom(viewportRef, viewReady && !calibrating, {
    onTap: handleViewportTap,
    twoFingerGesturesOnly: annotateOpen || twoFingerGesturesOnly,
  });
  const applyZoomRef = useRef(applyZoom);
  const resetZoomRef = useRef(resetZoom);
  const zoomLiveRef = useRef({ scale: 1, panX: 0, panY: 0 });
  applyZoomRef.current = applyZoom;
  resetZoomRef.current = resetZoom;
  zoomLiveRef.current = { scale, panX, panY };

  const displayTransform = useMemo(
    () =>
      playingStrokeReel
        ? ""
        : composeVideoCssTransform(metadataTransform, {
            rotationDeg,
            flipH,
            flipV,
          }),
    [playingStrokeReel, metadataTransform, rotationDeg, flipH, flipV]
  );

  const clipCamera = useMemo(() => {
    const meta = clipCameraFromSources({
      viewSetup: initialViewSetup,
      filename: url || mediaSrc,
    });
    return {
      ...meta,
      label: formatCameraSetupLabel(meta.cameraSetup, meta.cameraOrdinal, t),
    };
  }, [initialViewSetup, url, mediaSrc, t]);

  const viewSetup = useMemo(
    () =>
      attachCameraSetupToViewSetup(
        buildDisplayViewSetup({
          rotationDeg,
          flipH,
          flipV,
          metadataTransform,
        }),
        clipCamera
      ),
    [rotationDeg, flipH, flipV, metadataTransform, clipCamera]
  );

  const wantsFhDetectors = true;

  const serverDetections = poseResult?.summary?.detections || null;

  // Client stroke heuristics are expensive — never run them synchronously when
  // Charts turns on. Prefer server/upload summary; otherwise compute idle.
  const [clientDetections, setClientDetections] = useState(null);
  useEffect(() => {
    setClientDetections(null);
  }, [poseResult]);

  useEffect(() => {
    if (!wantsFhDetectors || !poseResult?.samples?.length) return undefined;
    const hasServer =
      (serverDetections?.fh_topspin_left &&
        Array.isArray(serverDetections.fh_topspin_left.strokes)) ||
      (serverDetections?.fh_left &&
        Array.isArray(serverDetections.fh_left.strokes)) ||
      (serverDetections?.bh_topspin_left &&
        Array.isArray(serverDetections.bh_topspin_left.strokes)) ||
      (serverDetections?.bh_left &&
        Array.isArray(serverDetections.bh_left.strokes));
    if (hasServer) return undefined;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      try {
        const next = {
          fh_topspin_left: detectFhTopspinLeft(poseResult.samples, viewSetup, {
            clipKind: poseResult?.settings?.clip_kind || "",
            exerciseLabels: poseResult?.settings?.exercise || null,
          }),
          fh_left: detectFhLeft(poseResult.samples, viewSetup, {
            clipKind: poseResult?.settings?.clip_kind || "",
            exerciseLabels: poseResult?.settings?.exercise || null,
          }),
          bh_topspin_left: detectBhTopspinLeft(poseResult.samples, viewSetup, {
            clipKind: poseResult?.settings?.clip_kind || "",
            exerciseLabels: poseResult?.settings?.exercise || null,
          }),
          bh_left: detectBhLeft(poseResult.samples, viewSetup, {
            clipKind: poseResult?.settings?.clip_kind || "",
            exerciseLabels: poseResult?.settings?.exercise || null,
          }),
        };
        startTransition(() => {
          if (!cancelled) setClientDetections(next);
        });
      } catch {
        /* ignore heuristic failures */
      }
    };

    const idle =
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(run, { timeout: 2200 })
        : window.setTimeout(run, 500);
    return () => {
      cancelled = true;
      if (
        typeof window !== "undefined" &&
        typeof window.cancelIdleCallback === "function"
      ) {
        window.cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle);
      }
    };
  }, [wantsFhDetectors, poseResult, viewSetup, serverDetections]);

  const fhTopspinDetection = useMemo(() => {
    if (!wantsFhDetectors || !poseResult) return null;
    const fromServer = serverDetections?.fh_topspin_left;
    if (fromServer && Array.isArray(fromServer.strokes)) return fromServer;
    return clientDetections?.fh_topspin_left || null;
  }, [wantsFhDetectors, poseResult, serverDetections, clientDetections]);

  const fhDetection = useMemo(() => {
    if (!wantsFhDetectors || !poseResult) return null;
    const fromServer = serverDetections?.fh_left;
    if (fromServer && Array.isArray(fromServer.strokes)) return fromServer;
    return clientDetections?.fh_left || null;
  }, [wantsFhDetectors, poseResult, serverDetections, clientDetections]);

  const bhTopspinDetection = useMemo(() => {
    if (!wantsFhDetectors || !poseResult) return null;
    const fromServer = serverDetections?.bh_topspin_left;
    if (fromServer && Array.isArray(fromServer.strokes)) return fromServer;
    return clientDetections?.bh_topspin_left || null;
  }, [wantsFhDetectors, poseResult, serverDetections, clientDetections]);

  const bhLeftDetection = useMemo(() => {
    if (!wantsFhDetectors || !poseResult) return null;
    const fromServer = serverDetections?.bh_left;
    if (fromServer && Array.isArray(fromServer.strokes)) return fromServer;
    return clientDetections?.bh_left || null;
  }, [wantsFhDetectors, poseResult, serverDetections, clientDetections]);

  const [clientServeDetection, setClientServeDetection] = useState(null);

  /** Saved table calibration first, then this clip's frozen pose geometry. */
  const resolvedTableCorners = useMemo(
    () =>
      pickTableCorners(
        pendingCorners,
        activeGeometry,
        poseResult?.table_geometry,
        poseResult?.table_corners,
        poseResult?.corners_norm
      ),
    [pendingCorners, activeGeometry, poseResult]
  );
  const hasExistingCorners = Boolean(resolvedTableCorners);

  useEffect(() => {
    setClientServeDetection(null);
  }, [poseResult]);

  useEffect(() => {
    if (!poseResult?.samples?.length) return undefined;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      try {
        const next = detectServes(poseResult.samples, viewSetup, {
          tableCorners: resolvedTableCorners,
        });
        startTransition(() => {
          if (!cancelled) setClientServeDetection(next);
        });
      } catch {
        /* ignore heuristic failures */
      }
    };

    const idle =
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(run, { timeout: 2200 })
        : window.setTimeout(run, 500);
    return () => {
      cancelled = true;
      if (
        typeof window !== "undefined" &&
        typeof window.cancelIdleCallback === "function"
      ) {
        window.cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle);
      }
    };
  }, [poseResult, viewSetup, resolvedTableCorners]);

  const serveDetection = useMemo(() => {
    if (!poseResult) return null;
    let det = clientServeDetection;
    if (!det) {
      const fromServer = serverDetections?.serve_toss;
      if (fromServer && Array.isArray(fromServer.events)) det = fromServer;
    }
    if (!det) return null;
    const analyzed = ballResult?.serve_analysis?.serves;
    const metrics = ballResult?.serve_analysis?.metrics;
    if (!Array.isArray(analyzed) || !analyzed.length) {
      return metrics ? { ...det, ...metrics } : det;
    }
    const events = Array.isArray(det.events) ? det.events : [];
    const ordered = [...events].sort((a, b) => {
      const ta = Number(a?.phases?.contact?.t_start_ms ?? a?.t_peak_ms ?? a?.t_ms) || 0;
      const tb = Number(b?.phases?.contact?.t_start_ms ?? b?.t_peak_ms ?? b?.t_ms) || 0;
      return ta - tb;
    });
    const labeled = ordered.map((ev, i) => {
      const a = analyzed[i];
      if (!a) return ev;
      return {
        ...ev,
        p1: a.p1,
        p2: a.p2,
        p3: a.p3,
        length: a.length,
        miss_reason: a.miss_reason,
      };
    });
    return {
      ...det,
      ...(metrics || {}),
      events: labeled,
    };
  }, [poseResult, serverDetections, clientServeDetection, ballResult]);

  const ballStrokeDetections = useMemo(
    () => ({
      fh_topspin_left: fhTopspinDetection,
      fh_left: fhDetection,
      bh_topspin_left: bhTopspinDetection,
      bh_left: bhLeftDetection,
      serve_toss: serveDetection,
    }),
    [
      fhTopspinDetection,
      fhDetection,
      bhTopspinDetection,
      bhLeftDetection,
      serveDetection,
    ]
  );

  const strokeReelPlan = useMemo(() => {
    if (!enablePose || !poseResult?.samples?.length) return null;
    return planStrokeSlowMoExport({
      detections: ballStrokeDetections,
      videoDurationSec: duration || undefined,
      sequence: sequenceMeta,
      videoUrl: url || mediaSrc,
      viewSetup: parsedViewSetup,
    });
  }, [
    enablePose,
    poseResult,
    ballStrokeDetections,
    duration,
    sequenceMeta,
    url,
    mediaSrc,
    parsedViewSetup,
  ]);

  const strokeReelCanStart = Boolean(teId && activePlayerId);
  const strokeReelReady = Boolean(strokeReelPlan?.ok && strokeReelCanStart);

  // Load persisted stroke analyses with the clip.
  useEffect(() => {
    if (!enablePose || !teId) {
      setStoredStrokeAnalyses(null);
      strokeAnalysisSaveSigRef.current = "";
      return undefined;
    }
    let cancelled = false;
    setStoredStrokeAnalyses(null);
    strokeAnalysisSaveSigRef.current = "";
    (async () => {
      try {
        const data = await fetchStrokeAnalysis(teId, {
          videoUrl: url || mediaSrc || null,
        });
        if (cancelled) return;
        if (data?.status === "missing" || !data?.result) {
          setStoredStrokeAnalyses(null);
          return;
        }
        setStoredStrokeAnalyses(data.result);
        if (data.source_signature) {
          strokeAnalysisSaveSigRef.current = String(data.source_signature);
        } else if (data.result?.source_signature) {
          strokeAnalysisSaveSigRef.current = String(
            data.result.source_signature
          );
        }
      } catch {
        if (!cancelled) setStoredStrokeAnalyses(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enablePose, teId, url, mediaSrc]);

  // When pose detections are ready, compute analyses and persist if changed.
  useEffect(() => {
    if (!enablePose || !teId || !poseResult?.samples?.length) return undefined;
    const hasStrokes = Object.values(ballStrokeDetections || {}).some(
      (det) => Array.isArray(det?.strokes) && det.strokes.length > 0
    );
    if (!hasStrokes) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const doc = buildStrokeAnalysisDocument({
          detections: ballStrokeDetections,
          samples: poseResult.samples,
          poseJobId: poseResult?.job_id || poseResult?.settings?.job_id || null,
        });
        if (!doc.stroke_count) return;
        if (
          doc.source_signature &&
          doc.source_signature === strokeAnalysisSaveSigRef.current
        ) {
          return;
        }
        const saved = await saveStrokeAnalysis(teId, {
          videoUrl: url || mediaSrc || null,
          poseJobId: doc.pose_job_id,
          sourceSignature: doc.source_signature,
          result: doc,
        });
        if (cancelled) return;
        const next = saved?.result || doc;
        setStoredStrokeAnalyses(next);
        strokeAnalysisSaveSigRef.current = String(
          saved?.source_signature ||
            next?.source_signature ||
            doc.source_signature ||
            ""
        );
      } catch {
        /* keep local UI working even if save fails */
      }
    }, 700);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enablePose, teId, poseResult, ballStrokeDetections, url, mediaSrc]);

  const annotationScope = useMemo(
    () => ({
      teId: teId || null,
      messageId: messageId || null,
      videoUrl: url || mediaSrc || null,
    }),
    [teId, messageId, url, mediaSrc]
  );
  const annotationScopeKey = `${annotationScope.teId || ""}|${annotationScope.messageId || ""}|${annotationScope.videoUrl || ""}`;

  useEffect(() => {
    if (!annotationScope.teId && !annotationScope.messageId && !annotationScope.videoUrl) {
      setFrameNotes([]);
      setFeedbackSessions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchVideoAnnotations(annotationScope);
        if (!cancelled) {
          const deleted = deletedFeedbackRef.current;
          const loadedSessions = Array.isArray(data?.sessions) ? data.sessions : [];
          const removed = loadedSessions.filter((session) => deleted.has(session.id));
          const sessions = loadedSessions.filter((session) => !deleted.has(session.id));
          const notes = (Array.isArray(data?.notes) ? data.notes : []).filter(
            (note) => !removed.some((session) => noteBelongsToSession(note, session))
          );
          setFrameNotes(notes);
          setFeedbackSessions(sessions);
        }
      } catch {
      if (!cancelled) {
        setFrameNotes([]);
        setFeedbackSessions([]);
      }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [annotationScopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const annotationWriteRef = useRef(0);

  const persistFrameNotes = useCallback(
    (next) => {
      const notes = typeof next === "function" ? next(feedbackLiveRef.current.notes) : next;
      const sessions = feedbackLiveRef.current.sessions;
      setFrameNotes(notes);
      feedbackLiveRef.current = { ...feedbackLiveRef.current, notes, sessions };
      if (!annotationScope.teId && !annotationScope.messageId && !annotationScope.videoUrl) {
        return;
      }
      const rev = ++annotationWriteRef.current;
      saveVideoAnnotations(annotationScope, notes, sessions)
        .then(() => {
          if (annotationWriteRef.current === rev) return;
          const live = feedbackLiveRef.current;
          saveVideoAnnotations(annotationScope, live.notes, live.sessions).catch(() => {});
        })
        .catch(() => {});
    },
    [annotationScope]
  );

  const persistFeedback = useCallback(
    (notes, sessions) => {
      setFrameNotes(notes);
      setFeedbackSessions(sessions);
      feedbackLiveRef.current = {
        ...feedbackLiveRef.current,
        notes,
        sessions,
      };
      if (!annotationScope.teId && !annotationScope.messageId && !annotationScope.videoUrl) {
        return;
      }
      const rev = ++annotationWriteRef.current;
      saveVideoAnnotations(annotationScope, notes, sessions)
        .then(() => {
          if (annotationWriteRef.current === rev) return;
          const live = feedbackLiveRef.current;
          saveVideoAnnotations(annotationScope, live.notes, live.sessions).catch(() => {});
        })
        .catch(() => {});
    },
    [annotationScope]
  );

  feedbackLiveRef.current = {
    notes: frameNotes,
    sessions: feedbackSessions,
    angles: jointOverlayMode,
    anglesPlayer: jointOverlayPlayer,
    pose: poseOverlayOn,
  };

  const viewMirroredX = useMemo(
    () => isViewMirroredX(displayTransform),
    [displayTransform]
  );

  const liveHrLines = useMemo(() => {
    const seriesList = Array.isArray(hrSeries) && hrSeries.length
      ? hrSeries
      : hrPoints
        ? [{ playerId: null, playerName: null, points: hrPoints }]
        : [];

    return seriesList
      .map((entry, index) => {
        const bpm = hrAtVideoTime(entry?.points, currentTime, {
          videoDurationSec: duration,
        });
        if (bpm == null) return null;
        return {
          key: entry?.playerId || entry?.playerName || `hr-${index}`,
          playerName: entry?.playerName || null,
          bpm,
        };
      })
      .filter(Boolean);
  }, [hrSeries, hrPoints, currentTime, duration]);

  const orientDirty = hasUserVideoTransform({
    rotationDeg,
    flipH,
    flipV,
  });
  const zoomDirty = scale > 1.001 || Math.abs(panX) > 0.5 || Math.abs(panY) > 0.5;
  const hasChanges = orientDirty || zoomDirty;
  const is4KVideo =
    Math.min(Number(videoPixelSize.w) || 0, Number(videoPixelSize.h) || 0) >=
    2160;
  const canBakeEncode = orientDirty || is4KVideo;

  const clearHoldPausedTimers = useCallback(() => {
    if (holdPausedTimerRef.current) {
      window.clearTimeout(holdPausedTimerRef.current);
      holdPausedTimerRef.current = 0;
    }
    const video = videoRef.current;
    const onSeeked = holdPausedSeekedRef.current;
    if (video && onSeeked) {
      video.removeEventListener("seeked", onSeeked);
    }
    holdPausedSeekedRef.current = null;
  }, []);

  const releaseHoldPaused = useCallback(() => {
    holdPausedGenRef.current += 1;
    holdPausedAfterSeekRef.current = false;
    clearHoldPausedTimers();
  }, [clearHoldPausedTimers]);

  const seekTo = useCallback((time, { pause = false } = {}) => {
    const video = videoRef.current;
    if (!video) return;
    const horizon = mediaSeekHorizon(video);
    const windowed = clampPlaybackWindow(
      clampTime(time, horizon),
      playbackWindowRef.current
    );
    const next = windowed.time;
    if (pause) {
      clearHoldPausedTimers();
      const gen = ++holdPausedGenRef.current;
      holdPausedAfterSeekRef.current = true;
      const enforcePause = () => {
        if (holdPausedGenRef.current !== gen || !holdPausedAfterSeekRef.current) {
          return;
        }
        if (!video.paused) video.pause();
        setIsPlaying(false);
      };
      const releaseHoldIfCurrent = () => {
        if (holdPausedGenRef.current !== gen) return;
        holdPausedAfterSeekRef.current = false;
        holdPausedTimerRef.current = 0;
      };
      const reassertPause = () => {
        let ticks = 0;
        const tick = () => {
          if (holdPausedGenRef.current !== gen || !holdPausedAfterSeekRef.current) {
            return;
          }
          enforcePause();
          // ~1s window — long enough for HEVC/4K seek decode to stop auto-resuming.
          if (++ticks < 12) {
            holdPausedTimerRef.current = window.setTimeout(tick, 80);
          } else {
            releaseHoldIfCurrent();
          }
        };
        holdPausedTimerRef.current = window.setTimeout(tick, 0);
      };

      enforcePause();
      video.currentTime = next;
      setCurrentTime(next);
      enforcePause();

      const onSeekedHold = () => {
        if (holdPausedSeekedRef.current === onSeekedHold) {
          holdPausedSeekedRef.current = null;
        }
        video.removeEventListener("seeked", onSeekedHold);
        enforcePause();
        reassertPause();
      };
      holdPausedSeekedRef.current = onSeekedHold;
      video.addEventListener("seeked", onSeekedHold);
      // If currentTime didn't change enough to fire seeked, still enforce pause.
      window.setTimeout(() => {
        if (holdPausedGenRef.current !== gen || !holdPausedAfterSeekRef.current) {
          return;
        }
        enforcePause();
        if (holdPausedSeekedRef.current === onSeekedHold) {
          video.removeEventListener("seeked", onSeekedHold);
          holdPausedSeekedRef.current = null;
          reassertPause();
        }
      }, 300);
      return;
    }
    video.currentTime = next;
    setCurrentTime(next);
  }, [clearHoldPausedTimers]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // Feedback is frame-locked: stay paused while the tool is open.
    if (annotateOpenRef.current && video.paused) return;
    if (video.paused) {
      const windowed = clampPlaybackWindow(
        video.currentTime + 0.05,
        playbackWindowRef.current
      );
      if (windowed.held) video.currentTime = windowed.start;
      releaseHoldPaused();
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [releaseHoldPaused]);

  useEffect(() => {
    annotateOpenRef.current = annotateOpen;
    if (!annotateOpen) return;
    const video = videoRef.current;
    if (video && !video.paused) {
      video.pause();
      setIsPlaying(false);
    }
  }, [annotateOpen]);

  const handleVideoClick = useCallback(() => {
    if (calibrating || annotateOpen) return;
    if (Date.now() - suppressClickRef.current < 450) return;
    togglePlay();
  }, [togglePlay, calibrating, annotateOpen]);

  const seekBySeconds = useCallback(
    (delta) => {
      const video = videoRef.current;
      if (!video) return;
      releaseHoldPaused();
      seekTo(video.currentTime + delta);
    },
    [seekTo, releaseHoldPaused]
  );

  const stepFrame = useCallback(
    (forward) => {
      const video = videoRef.current;
      if (!video) return;
      // Always land paused on the adjacent frame (pause first if playing).
      if (!video.paused) {
        video.pause();
        setIsPlaying(false);
      }
      const delta = forward
        ? frameDurationRef.current
        : -frameDurationRef.current;
      seekTo(video.currentTime + delta, { pause: true });
    },
    [seekTo]
  );

  // Laptop keyboard: Space play/pause, ←/→ ±3s, f/b frame step (stay paused).
  useEffect(() => {
    if (!viewReady || calibrating) return undefined;

    const isEditableTarget = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return Boolean(target.closest("[contenteditable='true']"));
    };

    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (isEditableTarget(event.target)) return;

      const key = event.key;
      const lower = key.length === 1 ? key.toLowerCase() : key;

      if (key === " " || key === "Spacebar") {
        event.preventDefault();
        setShowControls(true);
        togglePlay();
        return;
      }
      if (key === "ArrowRight") {
        event.preventDefault();
        setShowControls(true);
        if (event.shiftKey && kotPointSegmentsRef.current.length) {
          const video = videoRef.current;
          const next = adjacentPointSeekSec(
            kotPointSegmentsRef.current,
            video?.currentTime || 0,
            1
          );
          if (next != null) seekTo(next, { pause: false });
        } else {
          seekBySeconds(SEEK_SECONDS);
        }
        return;
      }
      if (key === "ArrowLeft") {
        event.preventDefault();
        setShowControls(true);
        if (event.shiftKey && kotPointSegmentsRef.current.length) {
          const video = videoRef.current;
          const prev = adjacentPointSeekSec(
            kotPointSegmentsRef.current,
            video?.currentTime || 0,
            -1
          );
          if (prev != null) seekTo(prev, { pause: false });
        } else {
          seekBySeconds(-SEEK_SECONDS);
        }
        return;
      }
      if (lower === "f" || lower === "b") {
        event.preventDefault();
        setShowControls(true);
        stepFrame(lower === "f");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    viewReady,
    calibrating,
    togglePlay,
    seekBySeconds,
    seekTo,
    stepFrame,
  ]);

  const handleSpeedChange = useCallback((rate) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Number(rate) > 0 ? Number(rate) : 1;
    setPlaybackRate(next);
    applyPlaybackRate(video, next);
    // 0.2× replay mutes by default; other speeds restore the user's mute choice.
    if (isUltraSlowPlaybackRate(next)) {
      setAudioMuted(true);
    } else {
      setAudioMuted(Boolean(userAudioMutedRef.current));
    }
  }, []);

  const toggleAudioMute = useCallback(() => {
    setAudioMuted((prev) => {
      const next = !prev;
      userAudioMutedRef.current = next;
      return next;
    });
    setShowControls(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !viewReady || audioMuted;
  }, [viewReady, audioMuted, mediaSrc, url]);

  const replaySession = useMemo(() => {
    const raw = feedbackSessions.find((s) => s.id === replaySessionId) || null;
    return raw ? normalizeSessionForReplay(raw) : null;
  }, [feedbackSessions, replaySessionId]);
  const feedbackFps = useMemo(() => {
    const analysisFps = Number(poseResult?.settings?.fps);
    if (Number.isFinite(analysisFps) && analysisFps > 1) return analysisFps;
    const fd = frameDurationRef.current;
    if (fd > 0) return 1 / fd;
    return PLAYER_DEFAULT_FPS;
  }, [poseResult, currentTime]);

  const replayStrokes = useMemo(() => {
    if (!replaySession) return null;
    const videoMs = Math.round(currentTime * 1000);
    const fps = replaySession.fps || feedbackFps;
    return strokesVisibleAt(replaySession, replayElapsed, {
      videoMs,
      frame: frameFromVideoMs(videoMs, fps),
      fps,
    });
  }, [replaySession, replayElapsed, currentTime, feedbackFps]);
  const replayScreenUrl =
    replaySession?.events?.find((event) => event.op === "screen" && event.screenDataUrl)
      ?.screenDataUrl || "";

  useEffect(() => {
    if (!feedbackRecording) {
      const rec = feedbackRecorderRef.current;
      feedbackRecorderRef.current = null;
      if (!rec) return undefined;
      const session = rec.finish();
      if (sessionHasContent(session)) {
        const live = feedbackLiveRef.current;
        persistFeedback(live.notes, [...live.sessions, session].slice(-40));
      }
      return undefined;
    }
    const video = videoRef.current;
    const anchor = Math.round((video?.currentTime || currentTime || 0) * 1000);
    const seed = feedbackSeedRef.current;
    feedbackSeedRef.current = null;
    const fps = feedbackFps;
    const rec = createFeedbackRecorder(anchor, { fps });
    feedbackRecorderRef.current = rec;
    const pausedAtStart = seed?.paused != null ? Boolean(seed.paused) : video ? video.paused : !isPlaying;
    const observeLive = (el) => {
      const videoMs = Math.round((el?.currentTime ?? currentTime) * 1000);
      rec.observe({
        videoMs,
        frame: frameFromVideoMs(videoMs, fps),
        paused: el ? el.paused : pausedAtStart,
        rate: el?.playbackRate || playbackRate || 1,
        angles: feedbackLiveRef.current.angles,
        anglesPlayer: feedbackLiveRef.current.anglesPlayer,
        pose: feedbackLiveRef.current.pose,
        zoomScale: zoomLiveRef.current.scale,
        panX: zoomLiveRef.current.panX,
        panY: zoomLiveRef.current.panY,
      });
    };
    observeLive(video);
    rec.seedStrokes(seed?.strokes);
    const timer = window.setInterval(() => {
      const el = videoRef.current;
      if (!el || feedbackRecorderRef.current !== rec) return;
      observeLive(el);
    }, 80);
    return () => window.clearInterval(timer);
  }, [feedbackRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyReplayEvent = useCallback(
    (event) => {
      const video = videoRef.current;
      if (!video || !event) return;
      const seekToEventFrame = () => {
        if (event.videoMs == null && event.frame == null) return;
        const fps = normalizeFeedbackFps(
          event.fps || feedbackFps
        );
        const sec =
          event.videoMs != null
            ? Math.max(0, Number(event.videoMs) / 1000)
            : Math.max(0, Number(event.frame) / Math.max(1, fps));
        // Half-frame tolerance so drawings land on the exact paused frame.
        const tol = Math.max(0.008, 0.5 / Math.max(1, fps));
        if (Math.abs(video.currentTime - sec) > tol) {
          video.currentTime = sec;
          setCurrentTime(sec);
        }
      };
      if (event.op === "seek") {
        seekToEventFrame();
      } else if (event.op === "pause") {
        seekToEventFrame();
        video.pause();
        setIsPlaying(false);
      } else if (event.op === "play") {
        seekToEventFrame();
        if (suppressSeedPlayRef.current && (Number(event.t) || 0) <= 0) {
          if (!video.paused) video.pause();
          setIsPlaying(false);
          return;
        }
        video.play().catch(() => {});
        setIsPlaying(true);
      } else if (event.op === "rate") {
        handleSpeedChange(Number(event.rate) > 0 ? Number(event.rate) : 1);
      } else if (event.op === "angles") {
        seekToEventFrame();
        const mode =
          event.mode === "knees" || event.mode === "elbows" ? event.mode : "off";
        setJointOverlayMode(mode);
        if (event.player === "B" || event.player === "A") {
          setJointOverlayPlayer(event.player);
        }
      } else if (event.op === "pose") {
        seekToEventFrame();
        if (canSeeVideoAnalysisInfo) setPoseOverlayOn(Boolean(event.on));
      } else if (event.op === "zoom") {
        seekToEventFrame();
        applyZoomRef.current(
          Number(event.zoomScale) > 0 ? Number(event.zoomScale) : 1,
          Number(event.panX) || 0,
          Number(event.panY) || 0
        );
      } else if (event.op === "stroke") {
        seekToEventFrame();
      } else if (event.op === "screen" && event.screenDataUrl) {
        seekToEventFrame();
        const screen = replayScreenRef.current;
        if (screen) {
          if (screen.getAttribute("src") !== event.screenDataUrl) {
            screen.src = event.screenDataUrl;
          }
          try {
            screen.muted = false;
            screen.currentTime = 0;
          } catch {
            /* ignore */
          }
          screen.play().catch(() => {});
        }
      } else if (event.op === "voice" && event.voiceDataUrl) {
        seekToEventFrame();
        try {
          replayVoiceRef.current?.pause();
        } catch {
          /* ignore */
        }
        const audio = new Audio(event.voiceDataUrl);
        replayVoiceRef.current = audio;
        audio.play().catch(() => {});
      }
    },
    [canSeeVideoAnalysisInfo, feedbackFps, handleSpeedChange]
  );

  replaySessionRef.current = replaySession;
  applyReplayEventRef.current = applyReplayEvent;
  releaseHoldPausedRef.current = releaseHoldPaused;

  const finishFeedbackReplay = useCallback((opts = {}) => {
    replayEndedAtRef.current = performance.now();
    const zoom = zoomBeforeReplayRef.current;
    zoomBeforeReplayRef.current = null;
    if (zoom) {
      applyZoomRef.current(zoom.scale, zoom.panX, zoom.panY);
    }
    const resume =
      opts.resume === true
        ? true
        : opts.resume === false
          ? false
          : resumeAfterFeedbackRef.current;
    const arrival = arrivalSecRef.current;
    resumeAfterFeedbackRef.current = false;
    arrivalSecRef.current = null;
    setReplaySessionId(null);
    setFeedbackListOpen(false);
    setShowControls(false);
    handleSpeedChange(1);
    const video = videoRef.current;
    if (video && resume && arrival != null && Math.abs(video.currentTime - arrival) > 0.04) {
      video.currentTime = arrival;
      setCurrentTime(arrival);
    }
    const settledSec = resume && arrival != null ? arrival : video?.currentTime || 0;
    prevFeedbackTimeRef.current = Math.round(Math.max(0, settledSec) * 1000);
    if (!resume || !video) return;
    releaseHoldPausedRef.current();
    video.play().catch(() => {});
    setIsPlaying(true);
  }, [handleSpeedChange]);

  const endFeedbackReview = useCallback(
    ({ resume = true } = {}) => {
      resumeAfterFeedbackRef.current = Boolean(resume);
      finishFeedbackReplay({ resume: Boolean(resume) });
      try {
        replayVoiceRef.current?.pause();
      } catch {
        /* ignore */
      }
      replayVoiceRef.current = null;
      const screen = replayScreenRef.current;
      if (screen) {
        screen.pause();
        screen.removeAttribute("src");
        try {
          screen.load();
        } catch {
          /* ignore */
        }
      }
    },
    [finishFeedbackReplay]
  );

  useEffect(() => {
    const session = replaySessionRef.current;
    if (!session || annotateOpen) return undefined;
    if (suppressSeedPlayRef.current) {
      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
        setIsPlaying(false);
      }
    }
    replayAppliedRef.current = 0;
    replayStartedRef.current = Date.now();
    setReplayElapsed(0);
    const replayMs = sessionReplayDurationMs(session);
    let raf = 0;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      const live = replaySessionRef.current;
      if (!live) return;
      const elapsed = Date.now() - replayStartedRef.current;
      setReplayElapsed(elapsed);
      const due = eventsDue(live, elapsed, replayAppliedRef.current);
      due.forEach((event) => applyReplayEventRef.current(event));
      suppressSeedPlayRef.current = false;
      replayAppliedRef.current += due.length;
      if (elapsed < replayMs + 400) {
        raf = window.requestAnimationFrame(tick);
      } else {
        stopped = true;
        finishFeedbackReplay();
      }
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      stopped = true;
      window.cancelAnimationFrame(raf);
      try {
        replayVoiceRef.current?.pause();
      } catch {
        /* ignore */
      }
      replayVoiceRef.current = null;
      const screen = replayScreenRef.current;
      if (screen) screen.pause();
    };
  }, [replaySessionId, annotateOpen, finishFeedbackReplay]);

  useEffect(() => {
    if (!annotateOpen && feedbackRecording) setFeedbackRecording(false);
  }, [annotateOpen, feedbackRecording]);

  useEffect(() => {
    let released = false;
    let sentinel = null;
    const request = async () => {
      if (released || document.visibilityState !== "visible") return;
      const api = navigator.wakeLock;
      if (!api?.request) return;
      try {
        sentinel = await api.request("screen");
        sentinel.addEventListener?.("release", () => {
          sentinel = null;
        });
      } catch {
        /* ignore unsupported or denied wake lock */
      }
    };
    request();
    const onVisible = () => {
      if (document.visibilityState === "visible") request();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      sentinel?.release?.().catch(() => {});
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "smarttt:autoPlayFeedback",
        autoPlayFeedback ? "1" : "0"
      );
    } catch {
      /* ignore */
    }
  }, [autoPlayFeedback]);

  useEffect(() => {
    const allowAutoFeedback =
      basicPlayback || feedbackOnDemand
        ? feedbackViewer && feedbackModeOpen
        : autoPlayFeedback;
    if (
      annotateOpen ||
      replaySessionId ||
      !feedbackSessions.length ||
      !allowAutoFeedback
    ) {
      return;
    }
    const video = videoRef.current;
    const tMs = Math.round(
      Math.max(0, (video?.currentTime ?? currentTime) || 0) * 1000
    );
    const prev = prevFeedbackTimeRef.current;
    prevFeedbackTimeRef.current = tMs;
    const settling = performance.now() - replayEndedAtRef.current < 800;
    if (!settling) {
      releaseConsumedFeedback(
        feedbackSessions,
        tMs,
        prev,
        consumedFeedbackRef.current
      );
    }
    const hit = pickAutoFeedbackSession(
      feedbackSessions.filter((session) => !deletedFeedbackRef.current.has(session.id)),
      tMs,
      prev,
      consumedFeedbackRef.current
    );
    if (!hit) return;
    consumedFeedbackRef.current.add(hit.id);
    const anchorSec = feedbackAnchorMs(hit) / 1000;
    arrivalSecRef.current = anchorSec;
    zoomBeforeReplayRef.current = { ...zoomLiveRef.current };
    const wasPlaying = Boolean(video && !video.paused);
    if (video) {
      if (Math.abs(video.currentTime - anchorSec) > 0.04) {
        video.currentTime = anchorSec;
        setCurrentTime(anchorSec);
      }
      if (wasPlaying) {
        video.pause();
        setIsPlaying(false);
        suppressSeedPlayRef.current = true;
        resumeAfterFeedbackRef.current = true;
      } else {
        resumeAfterFeedbackRef.current = false;
      }
    }
    setShowControls(false);
    setReplaySessionId(hit.id);
  }, [
    currentTime,
    feedbackSessions,
    annotateOpen,
    replaySessionId,
    autoPlayFeedback,
    basicPlayback,
    feedbackOnDemand,
    feedbackViewer,
    feedbackModeOpen,
  ]);

  const jumpToFeedback = useCallback(
    (session) => {
      if (!session) return;
      consumedFeedbackRef.current.add(session.id);
      arrivalSecRef.current = null;
      resumeAfterFeedbackRef.current = false;
      zoomBeforeReplayRef.current = { ...zoomLiveRef.current };
      seekTo(feedbackAnchorMs(session) / 1000, { pause: true });
      setReplaySessionId(session.id);
      setShowControls(false);
    },
    [seekTo]
  );

  const stopFeedbackReplay = useCallback(() => {
    setReplaySessionId(null);
    setReplayElapsed(0);
    try {
      replayVoiceRef.current?.pause();
    } catch {
      /* ignore */
    }
    replayVoiceRef.current = null;
    const screen = replayScreenRef.current;
    if (screen) {
      screen.pause();
      screen.removeAttribute("src");
      try {
        screen.load();
      } catch {
        /* ignore */
      }
    }
    const video = videoRef.current;
    if (video) {
      applyPlaybackRate(video, 1);
      setPlaybackRate(1);
    }
  }, []);

  const toggleBasicFeedback = useCallback(() => {
    if (feedbackModeOpen) {
      setFeedbackModeOpen(false);
      stopFeedbackReplay();
      return;
    }
    setFeedbackModeOpen(true);
    setShowControls(true);
    const tMs = Math.round(
      Math.max(0, (videoRef.current?.currentTime ?? currentTime) || 0) * 1000
    );
    const hit = feedbackAtPlayhead(
      feedbackSessions.filter((session) => !deletedFeedbackRef.current.has(session.id)),
      tMs
    );
    if (hit) jumpToFeedback(hit);
  }, [currentTime, feedbackModeOpen, feedbackSessions, jumpToFeedback, stopFeedbackReplay]);

  const deleteFeedback = useCallback(
    (sessionId) => {
      const removed = feedbackSessions.find((session) => session.id === sessionId);
      deletedFeedbackRef.current.add(sessionId);
      const nextSessions = feedbackSessions.filter((session) => session.id !== sessionId);
      const nextNotes = removed
        ? frameNotes.filter((note) => !noteBelongsToSession(note, removed))
        : frameNotes;
      stopFeedbackReplay();
      setJointOverlayMode("off");
      consumedFeedbackRef.current.delete(sessionId);
      feedbackLiveRef.current = {
        ...feedbackLiveRef.current,
        notes: nextNotes,
        sessions: nextSessions,
        angles: "off",
      };
      persistFeedback(nextNotes, nextSessions);
    },
    [feedbackSessions, frameNotes, persistFeedback, stopFeedbackReplay]
  );

  const beginScreenRecording = useCallback(() => {
    if (screenCaptureRef.current?.stream) return Promise.resolve(true);
    let streamPromise;
    try {
      streamPromise = navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 12 },
        audio: false,
        preferCurrentTab: true,
      });
    } catch {
      return Promise.resolve(false);
    }
    const holder = { stream: null, rec: null, chunks: [] };
    screenCaptureRef.current = holder;
    return streamPromise
      .then(async (stream) => {
        if (screenCaptureRef.current !== holder) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        holder.stream = stream;
        const [track] = stream.getVideoTracks();
        const root = rootRef.current;
        const Crop = window.CropTarget || null;
        if (root && Crop && track?.cropTo) {
          try {
            const crop = await Crop.fromElement(root);
            await track.cropTo(crop);
          } catch {
            /* keep the tab capture */
          }
        }
        const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
          ? "video/webm;codecs=vp8"
          : "video/webm";
        const rec = new MediaRecorder(stream, { mimeType: mime });
        rec.ondataavailable = (e) => {
          if (e.data?.size) holder.chunks.push(e.data);
        };
        holder.rec = rec;
        rec.start(400);
        return true;
      })
      .catch(() => {
        if (screenCaptureRef.current === holder) screenCaptureRef.current = null;
        return false;
      });
  }, []);

  const endScreenRecording = useCallback(() => {
    const holder = screenCaptureRef.current;
    if (!holder) return Promise.resolve(false);
    screenCaptureRef.current = null;
    return new Promise((resolve) => {
      const attach = () => {
        holder.stream?.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(holder.chunks, {
          type: holder.rec?.mimeType || "video/webm",
        });
        if (!blob.size || blob.size > 8_000_000) {
          resolve(false);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const url = String(reader.result || "");
          if (url) feedbackRecorderRef.current?.addScreen(url);
          resolve(Boolean(url));
        };
        reader.onerror = () => resolve(false);
        reader.readAsDataURL(blob);
      };
      if (holder.rec && holder.rec.state === "recording") {
        holder.rec.onstop = attach;
        holder.rec.stop();
      } else {
        window.setTimeout(attach, 500);
      }
    });
  }, []);

  const playStrokeAtSlowSpeed = useCallback(
    (stroke) => {
      const video = videoRef.current;
      if (!video || !stroke) return;
      if (strokePlayStopRef.current) {
        video.removeEventListener("timeupdate", strokePlayStopRef.current);
        strokePlayStopRef.current = null;
      }
      const range = buildStrokeMotionRange(
        stroke,
        Number.isFinite(video.duration) ? video.duration : duration
      );
      const stopAt = Number(range.clipEnd) || 0;
      releaseHoldPaused();
      seekTo(range.preRollStart, { pause: false });
      handleSpeedChange(0.2);
      const onTick = () => {
        if (video.currentTime >= stopAt - 0.04) {
          video.pause();
          setIsPlaying(false);
          video.removeEventListener("timeupdate", onTick);
          strokePlayStopRef.current = null;
          handleSpeedChange(1);
        }
      };
      strokePlayStopRef.current = onTick;
      video.addEventListener("timeupdate", onTick);
      video.play().catch(() => {});
      setIsPlaying(true);
      setShowControls(true);
    },
    [duration, handleSpeedChange, releaseHoldPaused, seekTo]
  );

  const rotateBy = useCallback((delta) => {
    setRotationDeg((prev) => normalizeRotationDeg(prev + delta));
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
  }, []);

  const toggleFlipH = useCallback(() => {
    setFlipH((prev) => !prev);
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
  }, []);

  const toggleFlipV = useCallback(() => {
    setFlipV((prev) => !prev);
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
  }, []);

  const resetUserTransform = useCallback(() => {
    setRotationDeg(0);
    setFlipH(false);
    setFlipV(false);
    resetZoom();
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
  }, [resetZoom]);

  const handleSaveTransform = useCallback(async () => {
    if (!canTransformVideo || saving || reuploading || requestingPhoneReupload) return;
    if (!messageId) {
      setSaveError(
        t(
          "videoReencodeMissingId",
          "Cannot save: this video has no message id."
        )
      );
      return;
    }
    if (typeof saveViewSetup !== "function") {
      setSaveError(
        t("videoViewSetupUnavailable", "Saving the view is not available right now.")
      );
      return;
    }

    const viewport = viewportRef.current;
    const viewportRect = viewport?.getBoundingClientRect();
    const payload = buildVideoViewSetupPayload({
      rotationDeg: normalizeRotationDeg(rotationDeg),
      flipH,
      flipV,
      zoomScale: scale,
      panX,
      panY,
      viewportW: viewportRect?.width || 0,
      viewportH: viewportRect?.height || 0,
    });

    setSaving(true);
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
    try {
      const result = await saveViewSetup({
        messageId,
        viewSetup: payload,
      });
      setSaveOk(true);
      onViewSetupSaved?.(result);
    } catch (err) {
      setSaveError(
        err?.message ||
          t("videoViewSetupFailed", "Failed to save the video view.")
      );
    } finally {
      setSaving(false);
    }
  }, [
    canTransformVideo,
    saving,
    reuploading,
    requestingPhoneReupload,
    messageId,
    saveViewSetup,
    rotationDeg,
    flipH,
    flipV,
    scale,
    panX,
    panY,
    onViewSetupSaved,
    t,
  ]);

  const handleReupload = useCallback(async () => {
    if (!canTransformVideo || saving || reuploading || requestingPhoneReupload) return;
    if (!messageId) {
      setSaveError(
        t(
          "videoReencodeMissingId",
          "Cannot save: this video has no message id."
        )
      );
      return;
    }

    const rot = normalizeRotationDeg(rotationDeg);
    const hasOrient = rot !== 0 || Boolean(flipH) || Boolean(flipV);
    const encode4K = is4KVideo;
    if (!hasOrient && !encode4K) {
      setSaveError(
        t(
          "bakeOrientsNoneOne",
          "This video has no rotation/flip to bake."
        )
      );
      return;
    }

    let confirmMsg;
    if (hasOrient && encode4K) {
      confirmMsg = t(
        "confirmVideoReencodeOrientAnd4K",
        "Re-encode this 4K video with the current rotation/flip to 720p at 60fps and replace the file? Zoom and pan are not baked in."
      );
    } else if (encode4K) {
      confirmMsg = t(
        "confirmVideoReencode4K",
        "Re-encode this 4K video to 720p at 60fps and replace the file?"
      );
    } else {
      confirmMsg = t(
        "confirmVideoReencodeReplace",
        "Re-encode this video with the current rotation/flip and replace the file? Zoom and pan are not baked in."
      );
    }
    if (!window.confirm(confirmMsg)) return;

    setReuploading(true);
    setReencodeProgress({ pct: 0, etaSeconds: null, phase: "queued" });
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
    try {
      const payload = {
        rotation_deg: rot,
        flip_h: Boolean(flipH),
        flip_v: Boolean(flipV),
        // Explicitly ignore zoom/pan — bake orientation only.
        zoom_scale: 1,
        pan_x: 0,
        pan_y: 0,
        pan_x_ratio: 0,
        pan_y_ratio: 0,
        duration_sec: duration || undefined,
      };
      if (encode4K) {
        payload.target_height = 720;
        payload.target_fps = 60;
      }
      const job = await reencodeEvaluationVideo(messageId, payload, {
        onProgress: (jobStatus) => {
          setReencodeProgress({
            pct: reencodeJobProgressPct(jobStatus),
            etaSeconds:
              jobStatus?.eta_seconds != null
                ? Number(jobStatus.eta_seconds)
                : null,
            phase: String(jobStatus?.phase || ""),
          });
        },
      });
      // Poll payload nests the message under `.data` / `.result`.
      const data =
        job?.data?.video_message_url || job?.data?.message_id
          ? job.data
          : job?.result?.video_message_url
            ? job.result
            : job?.data || job || {};
      const bakedUrlRaw = String(
        data?.video_message_url || data?.video_url || ""
      ).trim();
      const bakedUrl = bakedUrlRaw
        ? /^https?:\/\//i.test(bakedUrlRaw)
          ? bakedUrlRaw
          : `${ip_back.replace(/\/?$/, "/")}${bakedUrlRaw.replace(/^\//, "")}`
        : "";
      setRotationDeg(0);
      setFlipH(false);
      setFlipV(false);
      resetZoom();
      setMediaLoadError("");
      setIsBuffering(false);
      // Old S3 object is deleted after bake — switch the <video> immediately
      // so we do not keep buffering a 403 URL (looks like a frozen player).
      if (bakedUrl) {
        const joiner = bakedUrl.includes("?") ? "&" : "?";
        const nextSrc = `${bakedUrl}${joiner}bake=${Date.now()}`;
        setMediaSrc(nextSrc);
        const video = videoRef.current;
        if (video) {
          try {
            video.src = nextSrc;
            video.load();
          } catch {
            /* ignore */
          }
        }
      }
      setSaveOk(true);
      onViewSetupSaved?.(data);
    } catch (err) {
      setSaveError(
        err?.message || t("bakeOrientsFailed", "Re-encode failed")
      );
    } finally {
      setReuploading(false);
      setReencodeProgress(null);
    }
  }, [
    canTransformVideo,
    saving,
    reuploading,
    requestingPhoneReupload,
    messageId,
    rotationDeg,
    flipH,
    flipV,
    is4KVideo,
    duration,
    resetZoom,
    onViewSetupSaved,
    t,
  ]);

  const restoreOriginalStrokeReelPlayback = useCallback(() => {
    const original = strokeReelOriginalSrcRef.current || url || null;
    playingStrokeReelRef.current = false;
    setPlayingStrokeReel(false);
    if (strokeReelBlobUrlRef.current) {
      URL.revokeObjectURL(strokeReelBlobUrlRef.current);
      strokeReelBlobUrlRef.current = null;
    }
    strokeReelOriginalSrcRef.current = null;
    pendingAutoPlayRef.current = true;
    setPlaybackRate(1);
    setMediaSrc(original);
    setStrokeReelStatus("");
  }, [url]);

  const playGeneratedStrokeReel = useCallback(
    (blob) => {
      if (!blob) return;
      strokeReelBlobRef.current = blob;
      const objectUrl = URL.createObjectURL(blob);
      if (strokeReelBlobUrlRef.current) {
        URL.revokeObjectURL(strokeReelBlobUrlRef.current);
      }
      strokeReelBlobUrlRef.current = objectUrl;
      if (!strokeReelOriginalSrcRef.current) {
        strokeReelOriginalSrcRef.current = mediaSrc || url || null;
      }
      playingStrokeReelRef.current = true;
      setPlayingStrokeReel(true);
      setPoseOverlayOn(false);
      setBallOverlayOn(false);
      setPoseChartsVisible(false);
      setPlaybackRate(1);
      pendingAutoPlayRef.current = true;
      frameDurationRef.current = 1 / PLAYER_DEFAULT_FPS;
      setMediaSrc(objectUrl);
    },
    [mediaSrc, url]
  );

  useEffect(() => {
    if (!playingStrokeReel || !mediaSrc) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const playReel = () => {
      try {
        frameDurationRef.current = 1 / PLAYER_DEFAULT_FPS;
        applyPlaybackRate(video, 1);
        if (video.currentTime > 0.05) video.currentTime = 0;
      } catch {
        /* ignore */
      }
      video.play().catch(() => {});
    };
    if (video.readyState >= 2 && video.currentSrc === mediaSrc) {
      playReel();
      return undefined;
    }
    video.addEventListener("loadeddata", playReel, { once: true });
    return () => video.removeEventListener("loadeddata", playReel);
  }, [playingStrokeReel, mediaSrc]);

  useEffect(() => {
    return () => {
      if (strokeReelBlobUrlRef.current) {
        URL.revokeObjectURL(strokeReelBlobUrlRef.current);
        strokeReelBlobUrlRef.current = null;
      }
    };
  }, []);

  const handleGenerateStrokeReel = useCallback(
    async (reelKind = "stroke_slowmo") => {
    if (strokeReelExporting) return;
    if (!teId) {
      setStrokeReelError(
        t("strokeSlowMoMissingExercise", "No training exercise on this clip.")
      );
      return;
    }
    if (!activePlayerId) {
      setStrokeReelError(
        t(
          "strokeSlowMoMissingPlayer",
          "No active player found for this sequence."
        )
      );
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    const isMaxRally = reelKind === "max_rally";
    if (isMaxRally && !hasFeature("max_rally_detection")) {
      window.alert(
        t(
          "maxRallyProRequired",
          "Max rally detection and reel export is available on the Pro pilot license. Please contact your club administrator to enable Pro."
        )
      );
      return;
    }
    if (!isMaxRally && !hasFeature("slow_mo_reel_export")) {
      window.alert(
        t(
          "slowMoPlusRequired",
          "Slow-motion stroke reel export with knee angles is available on the Plus and Pro pilot licenses. Please contact your club administrator to enable Plus."
        )
      );
      return;
    }
    if (
      !window.confirm(
        isMaxRally
          ? t(
              "confirmMaxRallyReelExport",
              "Generate a portrait max-rally reel from first prep to last recovery, with slow-mo on the middle stroke, then attach it to the active player?"
            )
          : t(
              "confirmStrokeSlowMoExport",
              "Generate a portrait slow-motion clip with knee angles around the best stroke in the max rally, then attach it to the active player?"
            )
      )
    ) {
      return;
    }

    strokeReelAbortRef.current?.abort();
    const ac = new AbortController();
    strokeReelAbortRef.current = ac;

    setStrokeReelExporting(true);
    setStrokeReelProgress(0);
    setStrokeReelStatus(t("strokeSlowMoLoadingPose", "Loading stored pose…"));
    setStrokeReelError("");
    setShowControls(true);
    video.pause();
    setIsPlaying(false);

    try {
      let pose = poseResult;
      if (!pose?.samples?.length) {
        pose = await tryLoadExistingPose(teId, {
          videoUrl: url || mediaSrc || null,
          sequenceOrder,
        });
        if (pose?.samples?.length) {
          setPoseResult(pose);
        }
      }
      if (!pose?.samples?.length) {
        throw new Error(
          t(
            "strokeSlowMoNeedStoredPose",
            "No stored pose for this clip yet. Run Pose once, then generate the reel."
          )
        );
      }

      const detections = detectionsFromPoseResult(pose, viewSetup);
      const exerciseLabels = pose?.settings?.exercise || null;
      const clipKind = pose?.settings?.clip_kind || "";
      const plan = isMaxRally
        ? planMaxRallyExport({
            detections,
            videoDurationSec: video.duration || duration || undefined,
            clipKind,
            exerciseLabels,
            sequence: sequenceMeta,
          })
        : planStrokeSlowMoExport({
            detections,
            videoDurationSec: video.duration || duration || undefined,
            clipKind,
            exerciseLabels,
            sequence: sequenceMeta,
          });
      if (!plan?.ok) {
        throw new Error(
          t(
            "strokeSlowMoNoStrokes",
            "Stored pose has no rally strokes to export."
          )
        );
      }

      setStrokeReelStatus(
        t("strokeSlowMoAnalyzing", "Loading clip for export…")
      );
      const owners = Array.isArray(reelOwners) ? reelOwners : [];
      const { performer, partner } = pickReelOverlayPlayers(owners, {
        activePlayerId,
      });
      const localeCode = String(i18n?.language || "en").slice(0, 2);
      const socialOverlay = {
        title:
          warmupTitleForReel({
            clipKind,
            exerciseLabels,
            sequence: sequenceMeta,
            detectionId: plan.detectionId,
            localeCode,
            fallbackTitle: strokeTitleForDetection(plan.detectionId, t),
          }) || strokeTitleForDetection(plan.detectionId, t),
        performerName: performer?.name || "",
        performerLevel: performer?.level || "",
        partnerName: partner?.name || "",
        partnerRole:
          partner?.role ||
          (partner?.name ? t("sparring", "Sparring") : ""),
        strokesPerMinute:
          plan.strokesPerMinute != null ? Math.round(plan.strokesPerMinute) : null,
        subtitle: "",
        badge: isMaxRally
          ? t("reelMaxRallyBadge", "{{count}} strokes rally", {
              count: plan.rallyStrokeCount ?? 0,
            })
          : "",
      };
      const blob = await exportStrokeSlowMoReel({
        video,
        poseSamples: pose.samples,
        segments: plan.segments,
        poseSide: plan.poseSide,
        strokesPerMinute: plan.strokesPerMinute,
        socialOverlay,
        hrPoints: pickActiveHrPoints(hrSeries, hrPoints, activePlayerId),
        sourceFps:
          Number(ballResult?.settings?.fps) ||
          Number(ballResult?.settings?.video_fps) ||
          Number(poseResult?.settings?.video_fps) ||
          (Number(poseResult?.settings?.fps) >= 48
            ? Number(poseResult.settings.fps)
            : null) ||
          (frameDurationRef.current > 0 &&
          1 / frameDurationRef.current >= 48
            ? 1 / frameDurationRef.current
            : null),
        signal: ac.signal,
        onProgress: ({ fraction }) => {
          const pct = Math.round((fraction || 0) * 100);
          setStrokeReelProgress(pct);
          setStrokeReelStatus(
            t("strokeSlowMoExportingPct", "Exporting slow-mo · {{pct}}%", {
              pct,
            })
          );
        },
      });

      playGeneratedStrokeReel(blob);
      const nextKind = isMaxRally ? "max_rally" : "stroke_slowmo";
      strokeReelKindRef.current = nextKind;
      setShareReelKind(nextKind);
      setShowReelShareSheet(true);

      setStrokeReelStatus(t("strokeSlowMoUploading", "Uploading clip…"));
      const uploadJson = await uploadEvaluationVideo({
        blob,
        mimeType: blob.type,
        teId,
        playerId: activePlayerId,
        sequenceNumber: sequenceOrder,
        recordedAt: new Date(),
        parentMessageId: messageId,
        clipKind: isMaxRally ? "max_rally" : "stroke_slowmo",
      });

      setStrokeReelStatus(
        isMaxRally
          ? t(
              "maxRallyReelComplete",
              "Max rally reel saved. Share it now, or find it later in this exercise Media tab."
            )
          : t(
              "strokeSlowMoComplete",
              "Stroke slow-mo reel saved. Share it now, or find it later in this exercise Media tab."
            )
      );
      onStrokeReelUploaded?.(uploadJson);
    } catch (err) {
      if (ac.signal.aborted) {
        setStrokeReelError(t("strokeSlowMoCancelled", "Export cancelled."));
      } else {
        setStrokeReelError(
          err?.message ||
            t("strokeSlowMoFailed", "Could not generate stroke slow-mo reel.")
        );
      }
    } finally {
      setStrokeReelExporting(false);
      strokeReelAbortRef.current = null;
    }
  }, [
    strokeReelExporting,
    teId,
    activePlayerId,
    sequenceOrder,
    sequenceMeta,
    poseResult,
    ballResult,
    viewSetup,
    parsedViewSetup,
    url,
    mediaSrc,
    duration,
    onStrokeReelUploaded,
    playGeneratedStrokeReel,
    messageId,
    hrSeries,
    hrPoints,
    reelOwners,
    t,
    i18n,
  ]);

  const handleRequestPhoneReupload = useCallback(async () => {
    if (!canTransformVideo || saving || reuploading || requestingPhoneReupload) return;
    if (!messageId || typeof onRequestReupload !== "function") {
      setSaveError(
        t(
          "videoReencodeMissingId",
          "Cannot save: this video has no message id."
        )
      );
      return;
    }
    const confirmed = window.confirm(
      t(
        "confirmRequestOriginalReupload",
        "Clear this cloud video and ask the recording phone to re-upload the original? Use this if a bake went wrong."
      )
    );
    if (!confirmed) return;

    setRequestingPhoneReupload(true);
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
    try {
      await onRequestReupload({ messageId });
      setSaveOk(true);
    } catch (err) {
      setSaveError(
        err?.message ||
          t("requestOriginalReuploadFailed", "Could not request re-upload")
      );
    } finally {
      setRequestingPhoneReupload(false);
    }
  }, [
    canTransformVideo,
    saving,
    reuploading,
    requestingPhoneReupload,
    messageId,
    onRequestReupload,
    t,
  ]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const loadGen = ++metadataLoadGenRef.current;
    frameDurationRef.current = getVideoFrameDurationSeconds(video, {
      fallbackFps: PLAYER_DEFAULT_FPS,
    });
    setDuration(mediaSeekHorizon(video));
    setCurrentTime(video.currentTime || 0);
    setVideoPixelSize({
      w: video.videoWidth || 0,
      h: video.videoHeight || 0,
    });
    if (frameDurationRef.current > 0) {
      const metaFps = 1 / frameDurationRef.current;
      setVideoSourceFps((prev) =>
        Number.isFinite(prev) && prev > 1 ? prev : metaFps
      );
    }
    applyPlaybackRate(video, playbackRate);

    setMediaLoadError("");
    setViewReady(true);
    // Keep the opening overlay until we can play — metadata alone is not
    // enough for progressive championship MP4s (early moov, bytes still coming).
    // Clearing here made Chrome's waiting → "Buffering…" look like an error.
    if (eagerPlayback || video.readyState >= 3) {
      setIsMediaOpening(false);
    }
    const seekKey = `${url}|${initialCurrentTime}`;
    if (
      initialCurrentTime != null &&
      Number.isFinite(Number(initialCurrentTime)) &&
      initialSeekAppliedRef.current !== seekKey
    ) {
      initialSeekAppliedRef.current = seekKey;
      const target = Math.max(0, Number(initialCurrentTime));
      try {
        const dur = Number(video.duration);
        video.currentTime = Number.isFinite(dur) && dur > 0
          ? Math.min(target, Math.max(0, dur - 0.05))
          : target;
      } catch {
        /* ignore */
      }
    }
    if (pendingAutoPlayRef.current || autoPlay) {
      pendingAutoPlayRef.current = false;
      playReviewClip(video, {
        eager: eagerPlayback,
        onMuted: () => setAudioMuted(true),
      });
    }

    onLoadedMetadata?.(video);

    // Defer matrix probe so open/playback is never blocked. Skip applying the
    // result once the user is already watching — swapping CSS transform mid-play
    // was hitching / freezing some clips.
    window.setTimeout(() => {
      if (loadGen !== metadataLoadGenRef.current) return;
      if (playingStrokeReelRef.current) return;
      void (async () => {
        let fileTransform = "";
        try {
          fileTransform =
            (await resolveVideoCssTransform(
              url,
              video.videoWidth,
              video.videoHeight,
              { timeoutMs: 1500 }
            )) || "";
        } catch {
          fileTransform = "";
        }
        if (loadGen !== metadataLoadGenRef.current) return;
        if (!fileTransform) return;
        const v = videoRef.current;
        if (v && !v.paused && v.currentTime > 0.2) return;
        setMetadataTransform(fileTransform);
      })();
    }, 600);
  }, [onLoadedMetadata, playbackRate, url, autoPlay, initialCurrentTime, eagerPlayback]);

  const revealIfMediaReady = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 1) return;
    setMediaLoadError("");
    setViewReady(true);
    if (eagerPlayback ? video.readyState >= 2 : video.readyState >= 3) {
      setIsMediaOpening(false);
      setMediaLoadProgress(null);
    }
    if (pendingAutoPlayRef.current || autoPlay) {
      pendingAutoPlayRef.current = false;
      playReviewClip(video, {
        eager: eagerPlayback,
        onMuted: () => setAudioMuted(true),
      });
    }
  }, [autoPlay, eagerPlayback]);

  const clearBufferStallTimer = useCallback(() => {
    if (bufferStallTimerRef.current) {
      window.clearTimeout(bufferStallTimerRef.current);
      bufferStallTimerRef.current = 0;
    }
  }, []);

  const handleMediaError = useCallback(() => {
    clearBufferStallTimer();
    const video = videoRef.current;
    const code = video?.error?.code;
    const currentSrc = String(video?.currentSrc || "");
    // Ignore the 403 from a bare private object URL once a signed src is in use.
    if (
      isTtdataUrl(url) &&
      currentSrc &&
      !/x-amz-signature=/i.test(currentSrc) &&
      /x-amz-signature=/i.test(String(mediaSrc || ""))
    ) {
      return;
    }
    const urlStr = String(url || "").toLowerCase();
    const srcStr = String(mediaSrc || "");
    const looksMov = /\.mov($|\?)/i.test(urlStr) || /\.m4v($|\?)/i.test(urlStr);
    const looksPhoneMp4 = /\.mp4($|\?)/i.test(urlStr);
    const privateNeedsSign =
      needsSignedMediaUrl(url || mediaSrc) && !isAlreadySignedMediaUrl(srcStr);
    // Still signing, or signed URL expired / 403 — not a codec problem.
    if (mediaSigning || privateNeedsSign) {
      setIsBuffering(false);
      setIsPlaying(false);
      setMediaLoadError(
        t(
          "videoLoadPrivateStorage",
          "This clip is stored privately and could not be authorized for playback. Retry, or ask an admin to check Object Storage signing."
        )
      );
      setViewReady(true);
      setupPreparedRef.current = true;
      setSetupPrepared(true);
      return;
    }
    const msg =
      code === 4 || looksMov
        ? looksMov || looksPhoneMp4
          ? t(
              "videoLoadUnsupportedFormat",
              "This clip’s format can’t be played in this browser (often iPhone HEVC). Wait for web conversion, re-encode, or open in Safari."
            )
          : t(
              "videoLoadNotFound",
              "Video file not found (404). It may have been moved or deleted."
            )
        : code === 2
          ? t(
              "videoLoadNetwork",
              "Network error while loading the video. Try again."
            )
          : t(
              "videoLoadFailed",
              "Could not load this video. Check the link or try again."
            );
    setIsBuffering(false);
    setIsPlaying(false);
    setMediaLoadError(msg);
    setViewReady(true);
    setupPreparedRef.current = true;
    setSetupPrepared(true);
    onMediaError?.(msg);
  }, [t, url, mediaSrc, mediaSigning, clearBufferStallTimer, onMediaError]);

  const handleBufferStall = useCallback(() => {
    const video = videoRef.current;
    // Already recovered (enough buffered data and still playing).
    if (video && !video.paused && video.readyState >= 3) {
      setIsBuffering(false);
      return;
    }
    // Slow progressive download: if the playhead advanced since we armed the
    // timer, keep waiting instead of labeling a valid S3 file as corrupt.
    if (video && !video.paused) {
      const anchor = bufferStallAnchorRef.current;
      const nowMedia = Number(video.currentTime) || 0;
      if (nowMedia > (Number(anchor.mediaTime) || 0) + 0.05) {
        bufferStallAnchorRef.current = {
          mediaTime: nowMedia,
          atMs: Date.now(),
        };
        bufferStallTimerRef.current = window.setTimeout(() => {
          bufferStallTimerRef.current = 0;
          handleBufferStall();
        }, BUFFER_STALL_MS);
        return;
      }
    }
    try {
      video?.pause?.();
    } catch {
      /* ignore */
    }
    setIsPlaying(false);
    setIsBuffering(false);
    setMediaLoadError(
      t(
        "videoBufferStalled",
        "Playback stalled. This file may be incomplete or corrupt. Retry, re-encode, or request a phone re-upload."
      )
    );
    setViewReady(true);
  }, [t]);

  const handleRetryMedia = useCallback(() => {
    clearBufferStallTimer();
    setMediaLoadError("");
    setIsBuffering(false);
    const video = videoRef.current;
    const raw = url || null;
    if (!video || !raw) return;
    const resumeAt = Number.isFinite(video.currentTime) ? video.currentTime : 0;
    const applySrc = (nextSrc) => {
      setMediaSrc(nextSrc);
      const onReady = () => {
        video.removeEventListener("loadeddata", onReady);
        if (resumeAt > 0.25 && Number.isFinite(video.duration) && video.duration > 0) {
          try {
            video.currentTime = Math.min(
              resumeAt,
              Math.max(0, video.duration - 0.05)
            );
          } catch {
            /* ignore */
          }
        }
        video.play().catch(() => {});
      };
      video.addEventListener("loadeddata", onReady);
      try {
        // Never append query params to a signed URL — that invalidates the signature.
        let loadSrc = nextSrc;
        if (!String(nextSrc).includes("X-Amz-")) {
          const joiner = nextSrc.includes("?") ? "&" : "?";
          loadSrc = `${nextSrc}${joiner}retry=${Date.now()}`;
        }
        video.src = loadSrc;
        setMediaSrc(loadSrc);
        video.load();
      } catch {
        video.removeEventListener("loadeddata", onReady);
      }
    };
    if (needsSignedMediaUrl(raw)) {
      clearSignedMediaUrlCache(raw);
      setMediaSigning(true);
      resolveSignedMediaUrl(raw, { force: true })
        .then((signed) => {
          if (signed && isAlreadySignedMediaUrl(signed)) {
            applySrc(signed);
            return;
          }
          setMediaLoadError(
            t(
              "videoLoadPrivateStorage",
              "This clip is stored privately and could not be authorized for playback. Retry, or ask an admin to check Object Storage signing."
            )
          );
        })
        .catch(() => {
          setMediaLoadError(
            t(
              "videoLoadPrivateStorage",
              "This clip is stored privately and could not be authorized for playback. Retry, or ask an admin to check Object Storage signing."
            )
          );
        })
        .finally(() => setMediaSigning(false));
      return;
    }
    applySrc(raw);
  }, [url, clearBufferStallTimer]);

  const armBufferStallTimer = useCallback(() => {
    if (bufferStallTimerRef.current || mediaLoadError) return;
    // Initial progressive open often fires waiting/stalled before the first
    // second of playback — do not treat that as a corrupt file.
    if (isMediaOpeningRef.current) return;
    const video = videoRef.current;
    if ((Number(video?.currentTime) || 0) < 0.5) return;
    bufferStallAnchorRef.current = {
      mediaTime: Number(video?.currentTime) || 0,
      atMs: Date.now(),
    };
    bufferStallTimerRef.current = window.setTimeout(() => {
      bufferStallTimerRef.current = 0;
      handleBufferStall();
    }, BUFFER_STALL_MS);
  }, [handleBufferStall, mediaLoadError]);

  // Keep <video src> in sync when the parent swaps the file. playableMediaSrc
  // never puts the bare private object URL back on the element.
  useEffect(() => {
    const next = url || null;
    if (!next) return;
    if (playingStrokeReelRef.current) return;
    setMediaSrc((prev) => playableMediaSrc(next, prev));
  }, [url]);

  // Apply saved rotation/flip/zoom once per viewSetupKey — never hide the
  // player. Hiding for saved views was why only some clips appeared frozen.
  useLayoutEffect(() => {
    const key = viewSetupKey;
    if (appliedSetupKeyRef.current === key) {
      return undefined;
    }
    appliedSetupKeyRef.current = key;

    const parsed = parsedViewSetup;
    setMediaLoadError("");
    setMetadataTransform("");
    setRotationDeg(parsed?.rotationDeg || 0);
    setFlipH(Boolean(parsed?.flipH));
    setFlipV(Boolean(parsed?.flipV));
    pendingAutoPlayRef.current = Boolean(autoPlay);
    setMediaSrc((prev) => playableMediaSrc(url, prev));
    setViewReady(true);
    setupPreparedRef.current = true;
    setSetupPrepared(true);

    let cancelled = false;
    let attempts = 0;

    const applySavedZoom = () => {
      if (cancelled) return;
      if (!needsViewGate) {
        resetZoomRef.current();
        return;
      }
      const viewport = viewportRef.current;
      const rect = viewport?.getBoundingClientRect();
      const w = rect?.width || 0;
      const h = rect?.height || 0;
      if (!(w > 0 && h > 0) && attempts < 20) {
        attempts += 1;
        window.requestAnimationFrame(applySavedZoom);
        return;
      }
      applyZoomRef.current(
        parsed?.zoomScale || 1,
        (parsed?.panXRatio || 0) * w,
        (parsed?.panYRatio || 0) * h
      );
    };

    const raf = window.requestAnimationFrame(applySavedZoom);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewSetupKey, needsViewGate, autoPlay, url]);

  // Progressive open: keep waiting while bytes arrive. Large championship MP4s
  // often expose metadata only after most of the file is downloaded — a short
  // hard timeout falsely shows "taking too long" a few seconds before playback.
  useEffect(() => {
    if (!url || !mediaSrc) return undefined;
    if (
      isTtdataUrl(url) &&
      !/x-amz-signature=/i.test(String(mediaSrc))
    ) {
      return undefined;
    }
    if (effectiveWebPlaybackStatus === "processing") return undefined;
    loadProgressSamplesRef.current = [];
    setMediaLoadProgress(null);
    setLoadIdleSlow(false);
    let lastBufferedEnd = 0;
    let lastGrowthAt = Date.now();

    const sample = () => {
      const el = videoRef.current;
      if (!el) return;
      if (el.error) {
        handleMediaError();
        return;
      }
      // Keep sampling through early readyStates so the opening overlay still
      // shows percent/ETA while autoplay waits for more bytes.
      if (el.readyState >= 3 && !isMediaOpeningRef.current) {
        setLoadIdleSlow(false);
        return;
      }
      const bufferedEnd = bufferedEndSeconds(el);
      const nowMs = Date.now();
      if (bufferedEnd > lastBufferedEnd + 0.05) {
        lastBufferedEnd = bufferedEnd;
        lastGrowthAt = nowMs;
        setLoadIdleSlow(false);
      }
      const samples = loadProgressSamplesRef.current;
      samples.push({ atMs: nowMs, bufferedEnd });
      if (samples.length > 40) samples.splice(0, samples.length - 40);
      setMediaLoadProgress(
        estimateVideoLoadProgress(el, { samples, nowMs })
      );

      // Soft notice only — never flip to a hard error while the file may still
      // be a progressive MP4 whose index arrives near the end.
      if (nowMs - lastGrowthAt >= INITIAL_LOAD_IDLE_MS) {
        setLoadIdleSlow(true);
      }
    };

    sample();
    const intervalId = window.setInterval(sample, 400);
    const video = videoRef.current;
    video?.addEventListener("progress", sample);
    video?.addEventListener("loadedmetadata", sample);
    return () => {
      window.clearInterval(intervalId);
      video?.removeEventListener("progress", sample);
      video?.removeEventListener("loadedmetadata", sample);
    };
  }, [url, mediaSrc, t, handleMediaError, effectiveWebPlaybackStatus]);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setPlaybackRate(1);
    setIsPlaying(false);
    setSaveError("");
    setSaveOk(false);
    setShowControls(true);
    setMediaLoadError("");
    setIsBuffering(false);
    setIsMediaOpening(Boolean(url));
    setLoadIdleSlow(false);
    setMediaLoadProgress(null);
    loadProgressSamplesRef.current = [];
    // Signing state is owned by the media-src effect — do not force true here
    // for unsigned private URLs or the "Authorizing…" banner never clears after
    // a cached/signed src is applied.
    setMediaSigning(
      Boolean(
        url &&
          needsSignedMediaUrl(url) &&
          !isAlreadySignedMediaUrl(url) &&
          !peekSignedMediaUrl(url)
      )
    );
    clearBufferStallTimer();
    setPoseResult(null);
    setPoseError("");
    setPoseAnalyzing(false);
    setPoseProgress(0);
    setPoseEtaSeconds(null);
    setPoseStartFrame(null);
    setPoseEndFrame(null);
    setAnalysisInfoOpen(false);
    setCalibPhase(null);
    setPendingCorners(null);
    setPlayerRois({ left: null, right: null });
    setSessionCalibrated(false);
    tableGlowPendingRef.current = false;
    setTableHighlightUntil(0);
    setSwapPlayers(false);
    setActiveGeometry(null);
    poseAbortRef.current?.abort();
    poseAbortRef.current = null;
    ballAbortRef.current?.abort();
    ballAbortRef.current = null;
    setBallResult(null);
    setSegmentResult(null);
    setKotSegmentSource(null);
    setKotDetectStatus("");
    setKotPointsOnly(false);
    setKotDetectRequested(false);
    setKotDetectNonce(0);
    setKotDetectPhase("idle");
    setBallError("");
    setLiveWebPlaybackStatus(null);
    setPose3dResult(null);
    setPose3dJobId("");
    setPose3dStatus("");
  }, [url, clearBufferStallTimer]);

  // While server remux is in progress, poll feedback so the banner clears and
  // the player swaps to the browser-ready URL without requiring a reopen.
  useEffect(() => {
    if (effectiveWebPlaybackStatus !== "processing") return undefined;
    if (!teId || !messageId) return undefined;
    let cancelled = false;

    const applyReady = (msg) => {
      if (!msg || cancelled) return false;
      let setup = msg.view_setup;
      if (typeof setup === "string" && setup) {
        try {
          setup = JSON.parse(setup);
        } catch {
          setup = null;
        }
      }
      const status = setup?.web_playback?.status
        ? String(setup.web_playback.status)
        : null;
      if (status !== "ready" && status !== "failed") return false;
      setLiveWebPlaybackStatus(status);
      const nextUrl = String(msg.video_message_url || "").trim();
      if (nextUrl && status === "ready") {
        const applySrc = (playable) => {
          if (cancelled || !playable) return;
          setMediaSrc(playable);
          setMediaLoadError("");
          setMediaSigning(false);
        };
        if (needsSignedMediaUrl(nextUrl) && !isAlreadySignedMediaUrl(nextUrl)) {
          setMediaSigning(true);
          resolveSignedMediaUrl(nextUrl)
            .then((signed) => {
              if (signed && isAlreadySignedMediaUrl(signed)) applySrc(signed);
            })
            .catch(() => {});
        } else {
          applySrc(nextUrl);
        }
        if (typeof onViewSetupSaved === "function") {
          try {
            onViewSetupSaved({
              message_id: msg.message_id || messageId,
              video_message_url: nextUrl,
              view_setup: setup,
            });
          } catch {
            /* ignore */
          }
        }
      }
      return true;
    };

    const tick = async () => {
      try {
        const token =
          (typeof window !== "undefined" && localStorage.getItem("token")) || "";
        const resp = await fetch(`${ip_back}api/mobileAPI/getExerciseFeedback`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify({ te_id: teId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (redirectToLoginIfSessionExpired(resp.status, json)) return;
        if (!resp.ok || cancelled) return;
        const list = Array.isArray(json?.data?.feedbacks)
          ? json.data.feedbacks
          : [];
        const mid = String(messageId);
        const match = list.find((f) => String(f?.message_id) === mid);
        applyReady(match);
      } catch {
        /* ignore transient poll errors */
      }
    };

    void tick();
    const timer = window.setInterval(tick, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    effectiveWebPlaybackStatus,
    teId,
    messageId,
    onViewSetupSaved,
  ]);

  // Preload complete pose + active table geometry when pose is enabled.
  // Wait until the video surface is ready so a multi‑MB result JSON + charts
  // never block the first paint / first play() of the clip.
  useEffect(() => {
    if (!enablePose || !teId || !viewReady) return undefined;
    let cancelled = false;
    const run = async () => {
      try {
        if (!effectiveKotMatch) {
          const existing = await tryLoadExistingPose(teId, {
            videoUrl: url || mediaSrc || null,
            sequenceOrder,
          });
          if (!cancelled && existing) {
          setPoseResult(existing);
          const poseGeo = existing.table_geometry || null;
          const poseCorners = pickTableCorners(poseGeo);
          if (poseGeo) {
            setActiveGeometry({
              ...poseGeo,
              corners_norm: poseCorners || poseGeo.corners_norm || null,
            });
          }
          if (poseCorners) setSessionCalibrated(true);
          if (existing.settings?.swap_players) setSwapPlayers(true);
          if (existing.settings?.roi_left || existing.settings?.roi_right) {
            setPlayerRois({
              left: existing.settings.roi_left || null,
              right: existing.settings.roi_right || null,
            });
            if (existing.settings?.roi_left) setSessionCalibrated(true);
          }
          if (existing.settings?.start_frame != null) {
            setPoseStartFrame(Number(existing.settings.start_frame) || 0);
          }
          if (
            existing.settings?.end_frame != null &&
            Number(existing.settings.end_frame) >= 0
          ) {
            setPoseEndFrame(Number(existing.settings.end_frame));
          }
          // Phone Generate pose uploads both players — prefer A+B detection settings.
          if (poseResultHasBothPlayers(existing)) {
            setAnalysisSettings((prev) =>
              normalizeVideoAnalysisSettings({
                ...prev,
                poseDetectionPlayer: "Both",
              })
            );
            // Skeleton stays off until a moderator toggles it.
          }
          // Reuse any completed Pose3D for this job (Open 3D without regenerating).
          const jid = existing.job_id || existing.video_hash || null;
          if (jid) {
            try {
              const p3dWrap = await fetchPose3dResult({
                jobId: jid,
                teId,
                videoUrl: url || mediaSrc || null,
                sequenceOrder,
              });
              const blob = p3dWrap?.result || p3dWrap || null;
              if (!cancelled && blob?.samples?.length) {
                setPose3dJobId(jid);
                setPose3dResult(blob);
                setPose3dStatus(
                  t("pose3dCachedReady", "Pose3D cached — Open 3D or Regenerate")
                );
              }
            } catch {
              /* no pose3d for this job yet */
            }
          }
        }
        }
      } catch {
        /* ignore */
      }
      if (tableId) {
        try {
          const geo = await getActiveTableGeometry(tableId);
          if (!cancelled && geo) {
            const tableCorners = pickTableCorners(geo);
            setActiveGeometry((prev) => {
              const prevCorners = pickTableCorners(prev);
              const corners = tableCorners || prevCorners;
              return {
                ...(prev || {}),
                ...geo,
                corners_norm: corners,
                roi_left: geo.roi_left || prev?.roi_left || null,
                roi_right: geo.roi_right || prev?.roi_right || null,
              };
            });
            setPlayerRois((prev) => ({
              left: prev.left || geo.roi_left || null,
              right: prev.right || geo.roi_right || null,
            }));
            if (tableCorners) setSessionCalibrated(true);
          }
        } catch {
          /* ignore */
        }
      }
    };
    const idle =
      typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(() => {
            void run();
          }, { timeout: 1200 })
        : window.setTimeout(() => {
            void run();
          }, 250);
    return () => {
      cancelled = true;
      if (typeof window !== "undefined" && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle);
      }
    };
  }, [enablePose, teId, tableId, viewReady, t, url, sequenceOrder, effectiveKotMatch]);

  // Preload cached ball track for the selected detector.
  useEffect(() => {
    if (!enablePose || !teId || effectiveKotMatch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const existing = await tryLoadExistingBall(teId, ballDetector, {
          videoUrl: url || mediaSrc || null,
          sequenceOrder,
        });
        if (!cancelled) {
          setBallResult(existing);
          if (existing) setBallOverlayOn(true);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enablePose, teId, ballDetector, url, sequenceOrder, effectiveKotMatch]);

  useEffect(() => {
    return () => {
      poseAbortRef.current?.abort();
      ballAbortRef.current?.abort();
    };
  }, []);

  const currentFrameIndex = useMemo(() => {
    const analysisFps = Number(poseResult?.settings?.fps);
    const fd =
      Number.isFinite(analysisFps) && analysisFps > 1
        ? 1 / analysisFps
        : frameDurationRef.current || 1 / PLAYER_DEFAULT_FPS;
    if (!(fd > 0)) return 0;
    return Math.max(0, Math.floor(currentTime / fd + 1e-6));
  }, [currentTime, poseResult]);

  const poseSample = useMemo(() => {
    if (!poseResult?.samples?.length) return null;
    return sampleForPlayback(poseResult.samples, {
      tMs: Math.round(currentTime * 1000),
      frameIndex: currentFrameIndex,
      padMs: 250,
    });
  }, [poseResult, currentTime, currentFrameIndex]);

  useEffect(() => {
    if (canTransformVideo) setTopChromeCollapsed(true);
  }, [url, canTransformVideo]);

  useEffect(() => {
    if (!jointOverlayEnabled) return undefined;
    if (isPlaying || jointOverlayMode === "off") {
      jointOverlayAbortRef.current?.abort();
      setJointOverlayLoading(false);
      setJointOverlaySample(null);
      return undefined;
    }

    const clipKey = String(url || mediaSrc || teId || "clip");
    const playerKey = jointOverlayAllPlayers
      ? "both"
      : normalizePoseDetectionPlayer(jointOverlayPlayer);
    const videoElNow = videoRef.current;
    const tMs = Math.max(
      0,
      Math.round(
        ((videoElNow && Number.isFinite(videoElNow.currentTime)
          ? videoElNow.currentTime
          : currentTime) || 0) * 1000
      )
    );
    const storeKey = `${clipKey}|${jointOverlayMode}|${playerKey}|${Math.round(tMs / 80)}`;
    const remembered = jointOverlayStoreRef.current.get(storeKey);
    if (remembered) {
      setJointOverlaySample(remembered);
      setJointOverlayLoading(false);
      return undefined;
    }

    if (!teId) {
      setJointOverlaySample(
        jointOverlaySampleFromSides(
          poseSample?.left,
          jointOverlayAllPlayers || jointOverlaySide === "right"
            ? poseSample?.right
            : null
        )
      );
      setJointOverlayLoading(false);
      return undefined;
    }

    // Coach must pick a player box before knees/elbows can run in that box.
    if (jointOverlayCoachControls) {
      const roi = jointOverlaySelectionRoi;
      if (!roi) {
        setJointOverlaySample(null);
        setJointOverlayLoading(false);
        setPoseError(
          jointOverlaySide === "right"
            ? t(
                "jointOverlayNeedPlayerBoxB",
                "Select Player B with a box, then try Knees / Elbows again."
              )
            : t(
                "jointOverlayNeedPlayerBoxA",
                "Select Player A (left) with a box, then try Knees / Elbows again."
              )
        );
        return undefined;
      }
    }

    const ac = new AbortController();
    jointOverlayAbortRef.current?.abort();
    jointOverlayAbortRef.current = ac;
    const timer = window.setTimeout(async () => {
      setJointOverlayLoading(true);
      try {
        const videoEl = videoRef.current;
        const clipUrl = String(url || mediaSrc || "").trim();
        const videoUrlForServer = /^https?:\/\//i.test(clipUrl) ? clipUrl : null;
        let frameJpeg = null;
        try {
          frameJpeg = await capturePausedVideoJpeg(
            videoEl,
            clipUrl || null,
            ac.signal,
            { proxy: false, maxEdge: 960, quality: 0.72 }
          );
        } catch (captureErr) {
          if (ac.signal.aborted || captureErr?.name === "AbortError") return;
          frameJpeg = null;
        }
        if (ac.signal.aborted) return;
        const captureMs =
          videoEl && Number.isFinite(videoEl.currentTime)
            ? Math.max(0, Math.round(videoEl.currentTime * 1000))
            : tMs;
        const videoSize = {
          w: videoEl?.videoWidth || videoPixelSize.w,
          h: videoEl?.videoHeight || videoPixelSize.h,
        };
        const useRoi =
          jointOverlayCoachControls && jointOverlaySelectionRoi;
        const detectFrame = (jpeg) =>
          useRoi
            ? detectPoseInRoi(
                teId,
                captureMs,
                jointOverlaySelectionRoi,
                ac.signal,
                jpeg,
                videoSize,
                videoUrlForServer,
                viewSetup
              )
            : detectPoseCalibrationFrame(
                teId,
                captureMs,
                ac.signal,
                jpeg,
                videoSize,
                videoUrlForServer,
                viewSetup,
                resolvedTableCorners
              );
        let data;
        try {
          data = await detectFrame(frameJpeg);
        } catch (detectErr) {
          if (ac.signal.aborted || detectErr?.name === "AbortError") return;
          const msg = String(detectErr?.message || "").toLowerCase();
          const network =
            detectErr instanceof TypeError ||
            msg.includes("failed to fetch") ||
            msg.includes("lost connection");
          if (!frameJpeg || !network) throw detectErr;
          data = await detectFrame(null);
        }
        if (ac.signal.aborted) return;
        const live = useRoi
          ? jointOverlaySampleFromRoiDetection(data, jointOverlaySide)
          : jointOverlaySampleFromCalibration(data, {
              both: Boolean(jointOverlayAllPlayers),
              side: jointOverlaySide,
            });
        if (live) {
          const savedKey = `${clipKey}|${jointOverlayMode}|${playerKey}|${Math.round(captureMs / 80)}`;
          jointOverlayStoreRef.current.set(savedKey, live);
          setJointOverlaySample(live);
          setPoseError("");
        } else {
          setJointOverlaySample(null);
          if (useRoi) {
            setPoseError(
              data?.message ||
                t(
                  "jointOverlayNoPlayerInBox",
                  "No player inside this box. Select a player with the box and try again."
                )
            );
          } else {
            setPoseError(
              jointOverlayAllPlayers
                ? t("jointOverlayNoPlayer", "No player pose on this frame.")
                : jointOverlaySide === "right"
                  ? t(
                      "jointOverlayNoRightPlayer",
                      "No right player pose on this frame."
                    )
                  : t(
                      "jointOverlayNoLeftPlayer",
                      "No left player pose on this frame."
                    )
            );
          }
        }
      } catch (err) {
        if (ac.signal.aborted || err?.name === "AbortError") return;
        setJointOverlaySample(null);
        setPoseError(
          err?.message ||
            t(
              "jointOverlayFailed",
              "Could not detect the player on this frame."
            )
        );
      } finally {
        if (!ac.signal.aborted) setJointOverlayLoading(false);
      }
    }, 180);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [
    jointOverlayEnabled,
    jointOverlayAllPlayers,
    jointOverlayCoachControls,
    jointOverlayPlayer,
    jointOverlaySide,
    jointOverlaySelectionRoi,
    isPlaying,
    jointOverlayMode,
    poseSample,
    teId,
    url,
    mediaSrc,
    viewSetup,
    resolvedTableCorners,
    videoPixelSize.w,
    videoPixelSize.h,
    currentTime,
    t,
  ]);

  const analysisHudSections = useMemo(() => {
    const sections = [];
    // Prefer what <video> is decoding now — analysis settings can lag or
    // describe a different encode than the file currently on screen.
    const live = videoRef.current;
    const w =
      Number(live?.videoWidth) ||
      Number(videoPixelSize.w) ||
      Number(ballResult?.settings?.width) ||
      Number(poseResult?.settings?.width) ||
      0;
    const h =
      Number(live?.videoHeight) ||
      Number(videoPixelSize.h) ||
      Number(ballResult?.settings?.height) ||
      Number(poseResult?.settings?.height) ||
      0;
    const probedFps = Number(videoSourceFps);
    const videoFps =
      (Number.isFinite(probedFps) && probedFps > 1 ? probedFps : 0) ||
      (frameDurationRef.current > 0 ? 1 / frameDurationRef.current : 0) ||
      Number(ballResult?.settings?.fps) ||
      Number(poseResult?.settings?.fps) ||
      0;
    const playDuration =
      Number(live?.duration) > 0
        ? Number(live.duration)
        : Number(duration) > 0
          ? Number(duration)
          : 0;
    let setupCodec = null;
    try {
      let data = initialViewSetup;
      if (typeof data === "string" && data) data = JSON.parse(data);
      setupCodec = data?.web_playback?.codec || null;
    } catch {
      setupCodec = null;
    }
    const readyWall = resolveEvaluationVideoReadyWall({
      view_setup: initialViewSetup,
    });
    const sizeBytes = Number(videoFileMeta?.sizeBytes);
    const bitrateKbps =
      Number.isFinite(sizeBytes) &&
      sizeBytes > 0 &&
      playDuration > 0.5
        ? Math.round((sizeBytes * 8) / playDuration / 1000)
        : null;
    const videoRows = [
      hudRow(
        t("cameraPosition", "Camera"),
        clipCamera.label
      ),
      hudRow("Resolution", formatExactResLabel(w, h)),
      hudRow("Frame rate", formatFpsValue(videoFps)),
      hudRow(
        "Duration",
        playDuration > 0 ? formatTime(playDuration) : null
      ),
      hudRow("Size", formatFileSizeLabel(sizeBytes)),
      hudRow(
        "Bitrate",
        bitrateKbps != null ? `${bitrateKbps} kb/s` : null
      ),
      hudRow(
        "Codec",
        formatCodecLabel(videoFileMeta?.codec) || formatCodecLabel(setupCodec)
      ),
      hudRow(
        t("evalVideoReadyWallLabel", "Upload + process"),
        readyWall?.pending
          ? t("evalVideoReadyProcessing", "Processing…")
          : readyWall?.label || null
      ),
      hudRow(
        t("evalVideoPoseWallLabel", "Pose process"),
        (() => {
          try {
            let data = initialViewSetup;
            if (typeof data === "string" && data) data = JSON.parse(data);
            const fromSetup = Number(data?.pose_wall_ms);
            if (Number.isFinite(fromSetup) && fromSetup > 0) {
              return formatEvalWallMs(fromSetup);
            }
          } catch {
            /* ignore */
          }
          const fromPerf = Number(poseResult?.perf?.node?.spawn_wall_ms);
          if (Number.isFinite(fromPerf) && fromPerf > 0) {
            return formatEvalWallMs(fromPerf);
          }
          return null;
        })()
      ),
    ].filter(Boolean);
    if (videoRows.length) {
      sections.push({
        id: "video",
        title: t("videoInfoSection", "Video"),
        rows: videoRows,
      });
    }

    const poseSettings = poseResult?.settings || {};
    const poseAnalyzed = Boolean(
      poseResult?.summary || poseResult?.samples?.length
    );
    const poseFpsAnalyzed =
      formatFpsValue(poseSettings.pose_fps) || formatFpsValue(usedPoseFps);
    const poseFpsNext = formatFpsValue(effectivePoseFps);
    const nPose =
      Number(poseResult?.summary?.n_frames) ||
      Number(poseResult?.samples?.length) ||
      0;
    const poseDetectFps = formatFpsValue(
      poseResult?.perf?.inference?.pose2d_detect_fps ||
        poseResult?.perf?.pose2d_detect_fps
    );
    const playersMode =
      poseSettings.players_mode ||
      analysisSettings.poseDetectionPlayer ||
      null;
    const poseRows = poseAnalyzed
      ? [
          hudRow("Status", t("poseAnalyzed", "Analyzed")),
          hudRow(
            t("evalVideoPoseWallLabel", "Pose process"),
            (() => {
              const fromPerf = Number(poseResult?.perf?.node?.spawn_wall_ms);
              if (Number.isFinite(fromPerf) && fromPerf > 0) {
                return formatEvalWallMs(fromPerf);
              }
              try {
                let data = initialViewSetup;
                if (typeof data === "string" && data) data = JSON.parse(data);
                const fromSetup = Number(data?.pose_wall_ms);
                if (Number.isFinite(fromSetup) && fromSetup > 0) {
                  return formatEvalWallMs(fromSetup);
                }
              } catch {
                /* ignore */
              }
              return null;
            })()
          ),
          hudRow("pose_fps", poseFpsAnalyzed),
          hudRow(
            "Source",
            poseOriginLabel(poseSettings) ||
              (poseResult?.samples?.length ? "Stored" : null)
          ),
          hudRow("Backend", poseSettings.backend || poseSettings.model || null),
          hudRow("Players", playersMode),
          hudRow("Frames", nPose > 0 ? String(nPose) : null),
          hudRow("Detect rate", poseDetectFps),
          hudRow(
            "Hit L/R",
            poseResult?.perf?.quality?.debug
              ? `L ${
                  formatHitPct(poseResult.perf.quality.debug.accepted_rate_left) ||
                  "n/a"
                } · R ${
                  formatHitPct(poseResult.perf.quality.debug.accepted_rate_right) ||
                  "n/a"
                }`
              : poseResult?.perf?.quality
                ? `L ${
                    formatHitPct(poseResult.perf.quality.hit_rate_left) || "n/a"
                  } · R ${
                    formatHitPct(poseResult.perf.quality.hit_rate_right) || "n/a"
                  }`
                : null
          ),
          poseFpsNext &&
          poseFpsAnalyzed &&
          Math.abs(Number(usedPoseFps) - Number(effectivePoseFps)) >= 0.05
            ? hudRow("Next run", poseFpsNext)
            : null,
        ].filter(Boolean)
      : enablePose
        ? [
            hudRow("Status", t("poseNotAnalyzed", "Not analyzed")),
            hudRow("pose_fps", poseFpsNext),
            hudRow("Players", analysisSettings.poseDetectionPlayer || null),
          ].filter(Boolean)
        : [];
    if (poseRows.length) {
      sections.push({
        id: "pose2d",
        title: t("pose2dInfoSection", "Pose 2D"),
        rows: poseRows,
      });
    }

    const poseDebug = poseResult?.perf?.quality?.debug || null;
    if (poseAnalyzed && poseDebug && Number(poseDebug.frames) > 0) {
      const dbgFrames = Number(poseDebug.frames) || 0;
      const debugRows = [
        hudRow(
          "Path",
          poseDebug.detect_path
            ? `${poseDebug.detect_path}${
                poseDebug.roi_usage ? ` · ${poseDebug.roi_usage}` : ""
              }`
            : null
        ),
        hudRow(
          "MP",
          [
            poseDebug.num_poses != null ? `num_poses ${poseDebug.num_poses}` : null,
            poseDebug.det_conf != null ? `det ${poseDebug.det_conf}` : null,
            poseDebug.mp_candidates_avg != null
              ? `avg ${Number(poseDebug.mp_candidates_avg).toFixed(2)}/frame`
              : null,
            poseDebug.mp_empty != null ? `empty ${poseDebug.mp_empty}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || null
        ),
        hudRow(
          "Accepted",
          `L ${formatHitPct(poseDebug.accepted_rate_left) || "n/a"} · R ${
            formatHitPct(poseDebug.accepted_rate_right) || "n/a"
          }`
        ),
        hudRow(
          "Assigned",
          `L ${poseDebug.assigned_left ?? 0} · R ${poseDebug.assigned_right ?? 0}`
        ),
        hudRow(
          "Torso-in-ROI",
          `L ${poseDebug.torso_in_roi_left ?? 0} · R ${
            poseDebug.torso_in_roi_right ?? 0
          }`
        ),
        hudRow(
          "Torso px",
          poseDebug.torso_px_left != null || poseDebug.torso_px_right != null
            ? `L ${poseDebug.torso_px_left ?? "—"} (${
                poseDebug.torso_px_on_mp256_left ?? "—"
              } on 256) · R ${poseDebug.torso_px_right ?? "—"} (${
                poseDebug.torso_px_on_mp256_right ?? "—"
              } on 256)`
            : null
        ),
        hudRow("Left drops", formatDropMap(poseDebug.drop_left, dbgFrames)),
        hudRow("Right drops", formatDropMap(poseDebug.drop_right, dbgFrames)),
        hudRow(
          "ROI reset",
          poseDebug.roi_reset_left != null || poseDebug.roi_reset_right != null
            ? `L ${poseDebug.roi_reset_left ?? 0} · R ${poseDebug.roi_reset_right ?? 0}`
            : null
        ),
        ...(Array.isArray(poseDebug.likely_causes)
          ? poseDebug.likely_causes.slice(0, 3).map((cause, i) =>
              hudRow(i === 0 ? "Cause" : `Cause ${i + 1}`, cause)
            )
          : []),
      ].filter(Boolean);
      if (debugRows.length) {
        sections.push({
          id: "pose2d-debug",
          title: t("pose2dDebugSection", "Pose 2D debug"),
          rows: debugRows,
        });
      }
    }

    const p3d = pose3dResult?.result || pose3dResult || null;
    const p3dSamples = Array.isArray(p3d?.samples) ? p3d.samples.length : 0;
    const p3dMode =
      p3d?.mode || p3d?.settings?.pose3d_mode || pose3dMode || null;
    const p3dOrigin =
      pose3dModeLabel(p3dMode) === "iPhone LiDAR"
        ? "iPhone"
        : poseOriginLabel(p3d?.settings) ||
          (p3dSamples ? "Remote server" : null);
    const p3dRows = p3dSamples
      ? [
          hudRow("Status", t("pose3dAnalyzed", "Ready")),
          hudRow("Mode", pose3dModeLabel(p3dMode)),
          hudRow("Source", p3dOrigin),
          hudRow(
            "pose_fps",
            formatFpsValue(p3d?.settings?.pose_fps ?? p3d?.fps)
          ),
          hudRow("Frames", String(p3dSamples)),
          hudRow("Metric", p3d?.metric || p3d?.settings?.metric || null),
        ].filter(Boolean)
      : enablePose
        ? [
            hudRow("Status", t("pose3dNotGenerated", "Not generated")),
            hudRow(
              "Mode",
              pose3dEnabled ? pose3dModeLabel(pose3dMode) : t("off", "Off")
            ),
          ].filter(Boolean)
        : [];
    if (p3dRows.length) {
      sections.push({
        id: "pose3d",
        title: t("pose3dInfoSection", "Pose 3D"),
        rows: p3dRows,
      });
    }

    const ballSettings = ballResult?.settings || {};
    const ballAnalyzed = Boolean(
      ballResult?.summary || ballResult?.samples?.length
    );
    const det = ballSettings.detector || ballDetector || null;
    const nDet =
      Number(ballResult?.summary?.n_detected) ||
      (Array.isArray(ballResult?.samples)
        ? ballResult.samples.filter((s) => s?.detected).length
        : 0);
    const nFrames =
      Number(ballResult?.summary?.n_frames) ||
      Number(ballResult?.samples?.length) ||
      0;
    const rate = Number(ballResult?.summary?.detect_rate);
    const ratePct =
      Number.isFinite(rate) && rate >= 0
        ? `${Math.round(rate * 100)}%`
        : nFrames > 0
          ? `${Math.round((nDet / nFrames) * 100)}%`
          : null;
    const ballFpsAnalyzed = formatFpsValue(ballSettings.ball_detection_fps);
    const ballFpsNext = formatFpsValue(analysisSettings.ballDetectionFps);
    const nB = Number(ballResult?.summary?.n_bounces);
    const nS = Number(ballResult?.summary?.n_strokes);
    const nO = Number(ballResult?.summary?.n_outs);
    const bounceCount = Number.isFinite(nB)
      ? nB
      : (ballResult?.events || []).filter((e) => e?.type === "TABLE_BOUNCE")
          .length;
    const strokeCount = Number.isFinite(nS)
      ? nS
      : (ballResult?.events || []).filter((e) => e?.type === "BEGIN_STROKE")
          .length;
    const outCount = Number.isFinite(nO)
      ? nO
      : (ballResult?.events || []).filter((e) => e?.type === "OUT_OF_TABLE")
          .length;
    const ballRows = ballAnalyzed
      ? [
          hudRow("Status", t("ballAnalyzed", "Analyzed")),
          hudRow("Detector", ballDetectorLabel(det)),
          hudRow("ball_fps", ballFpsAnalyzed || ballFpsNext),
          hudRow(
            "Detections",
            nFrames > 0 ? `${nDet} / ${nFrames}` : String(nDet)
          ),
          hudRow("Hit rate", ratePct),
          (() => {
            const detRoi = normalizeStoredDetectionRoi(
              ballSettings.detection_roi
            );
            return detRoi
              ? hudRow(
                  "Detect ROI",
                  `${Math.round(detRoi.w * 100)}% × ${Math.round(detRoi.h * 100)}%`
                )
              : null;
          })(),
          ballSettings.stroke_gated
            ? hudRow(
                t("ballStrokeGated", "Stroke windows"),
                String(
                  Array.isArray(ballSettings.stroke_windows_ms)
                    ? ballSettings.stroke_windows_ms.length
                    : poseStrokeContactMs(poseResult).length || "—"
                )
              )
            : null,
          Number.isFinite(bounceCount) || Number.isFinite(strokeCount)
            ? hudRow(
                "Events",
                `bounces ${bounceCount || 0} · strokes ${strokeCount || 0} · outs ${outCount || 0}`
              )
            : null,
          (() => {
            const sm = ballResult?.serve_analysis?.metrics;
            if (!sm || sm.serve_try_count == null) return null;
            return hudRow(
              t("serve_try_count", "Attempts"),
              `${Number(sm.serve_try_count) || 0} · ${Number(sm.serve_short_count) || 0} short · ${Number(sm.serve_long_count) || 0} long · ${Number(sm.serve_missed_count) || 0} missed`
            );
          })(),
        ].filter(Boolean)
      : enablePose
        ? [
            hudRow("Status", t("ballNotAnalyzed", "Not analyzed")),
            hudRow("Detector", ballDetectorLabel(det)),
            hudRow("ball_fps", ballFpsNext),
          ].filter(Boolean)
        : [];
    if (ballRows.length) {
      sections.push({
        id: "ball",
        title: t("ballInfoSection", "Ball"),
        rows: ballRows,
      });
    }

    const ballDebug = resolveBallDebug(ballResult);
    if (ballAnalyzed && ballDebug) {
      const funnel =
        ballDebug.source === "pipeline"
          ? `raw ${ballDebug.raw_nonzero ?? 0} → box−${
              ballDebug.rejected_keep_box ?? 0
            } → stride−${ballDebug.rejected_stride ?? 0} → kept ${
              ballDebug.kept ?? 0
            } → smooth ${ballDebug.after_smooth ?? 0} → windows ${
              ballDebug.after_windows ?? 0
            }`
          : `${ballDebug.n_detected ?? nDet} / ${
              ballDebug.n_frames ?? nFrames
            } kept`;
      const debugRows = [
        hudRow(
          "Source",
          ballDebug.source === "pipeline"
            ? t("ballDebugPipeline", "This run (funnel)")
            : t("ballDebugDerived", "Derived from stored result — Regen ball for full funnel")
        ),
        hudRow("Funnel", funnel),
        hudRow(
          "Video",
          [
            ballDebug.video_fps != null
              ? `${Number(ballDebug.video_fps).toFixed(0)} fps`
              : null,
            ballDebug.width && ballDebug.height
              ? `${ballDebug.width}×${ballDebug.height}`
              : null,
            ballDebug.stride != null ? `stride ${ballDebug.stride}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || null
        ),
        hudRow(
          "Native gate",
          Array.isArray(ballDebug.native_table_gate)
            ? ballDebug.native_table_gate.join(",")
            : null
        ),
        hudRow(
          "Keep-box",
          Array.isArray(ballDebug.keep_box)
            ? ballDebug.keep_box.join(",")
            : null
        ),
        hudRow(
          "FMO array",
          ballDebug.fmo_array_len != null
            ? String(ballDebug.fmo_array_len)
            : null
        ),
        hudRow(
          "Clusters",
          ballDebug.n_clusters != null
            ? `${ballDebug.n_clusters}${
                ballDebug.longest_cluster_n
                  ? ` · longest ${ballDebug.longest_cluster_n}`
                  : ""
              }`
            : null
        ),
        hudRow(
          "Windows",
          ballDebug.n_stroke_windows
            ? `${ballDebug.n_stroke_windows}${
                ballDebug.stroke_windows_fail_open ? " · fail-open" : ""
              }`
            : null
        ),
        ...(Array.isArray(ballDebug.likely_causes)
          ? ballDebug.likely_causes.slice(0, 4).map((cause, i) =>
              hudRow(i === 0 ? "Cause" : `Cause ${i + 1}`, cause)
            )
          : []),
      ].filter(Boolean);
      if (debugRows.length) {
        sections.push({
          id: "ball-debug",
          title: t("ballDebugSection", "Ball debug"),
          rows: debugRows,
        });
      }
    }

    return sections;
  }, [
    ballResult,
    poseResult,
    pose3dResult,
    pose3dMode,
    pose3dEnabled,
    videoPixelSize,
    videoSourceFps,
    videoFileMeta,
    initialViewSetup,
    usedPoseFps,
    effectivePoseFps,
    enablePose,
    ballDetector,
    duration,
    analysisSettings.poseDetectionPlayer,
    analysisSettings.ballDetectionFps,
    clipCamera.label,
    analysisInfoOpen,
    t,
  ]);

  useEffect(() => {
    let cancelled = false;
    setVideoFileMeta({ sizeBytes: null, codec: null });
    setVideoSourceFps(null);

    // Fast path from view_setup when server already marked the clip.
    let setupCodec = null;
    try {
      let data = initialViewSetup;
      if (typeof data === "string" && data) data = JSON.parse(data);
      if (data && typeof data === "object") {
        setupCodec = formatCodecLabel(data.web_playback?.codec);
      }
    } catch {
      setupCodec = null;
    }
    if (setupCodec) {
      setVideoFileMeta((prev) => ({ ...prev, codec: setupCodec }));
    }

    const src = String(url || "").trim();
    if (!src) return undefined;

    void fetchVideoFileInfo(src).then((info) => {
      if (cancelled) return;
      setVideoFileMeta((prev) => ({
        sizeBytes: info.sizeBytes ?? prev.sizeBytes,
        codec: info.codec || prev.codec || setupCodec,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [url, initialViewSetup]);

  useEffect(() => {
    if (!analysisInfoOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setAnalysisInfoOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [analysisInfoOpen]);

  // Refresh resolution / fps from the element currently on screen when Info opens.
  useEffect(() => {
    if (!analysisInfoOpen) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      setVideoPixelSize({
        w: video.videoWidth,
        h: video.videoHeight,
      });
    }
    if (Number(video.duration) > 0) setDuration(video.duration);
    const gen = ++fpsProbeGenRef.current;
    let cancelled = false;
    probeVideoSourceFps(video, {
      fallbackFps: PLAYER_DEFAULT_FPS,
      maxSamples: 18,
      timeoutMs: 1800,
    }).then((fps) => {
      if (cancelled || gen !== fpsProbeGenRef.current) return;
      if (Number.isFinite(fps) && fps > 1) {
        frameDurationRef.current = 1 / fps;
        setVideoSourceFps(fps);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [analysisInfoOpen, url, mediaSrc]);

  const deferredChartTime = useDeferredValue(currentTime);

  const ballPlayheadState = useMemo(() => {
    if (!ballResult?.states?.length) return null;
    return ballStateAt(ballResult.states, Math.round(currentTime * 1000));
  }, [ballResult, currentTime]);

  const kotPointResolved = useMemo(() => {
    if (!enableGamePoints) return { segments: [], source: null };
    return resolveKotPointSegments(
      { poseResult: null, segmentResult, ballResult: null },
      duration
    );
  }, [enableGamePoints, segmentResult, duration]);

  const kotPointSegments = kotPointResolved.segments;
  const kotPointsAvailable = kotPointSegments.length > 0;

  useEffect(() => {
    kotPointsOnlyRef.current = kotPointsOnly;
  }, [kotPointsOnly]);

  useEffect(() => {
    kotPointSegmentsRef.current = kotPointSegments;
  }, [kotPointSegments]);

  useEffect(() => {
    setKotSegmentSource(kotPointResolved.source);
  }, [kotPointResolved.source]);

  // Game points: on-demand motion scout after table calibration. No auto pose.
  useEffect(() => {
    if (!enableGamePoints || !teId || !kotDetectRequested) return undefined;
    if (!hasExistingCorners) {
      setKotDetectPhase("error");
      setKotDetectStatus(
        t(
          "kotPointsNeedCalibration",
          "Calibrate the table first to detect game points."
        )
      );
      return undefined;
    }
    let cancelled = false;
    let pollTimer = null;
    setKotDetectPhase("running");
    setKotDetectStatus(
      t("kotPointsDetecting", "Detecting game points from motion near the table…")
    );

    const applySegment = (result) => {
      if (!cancelled && result) setSegmentResult(result);
    };

    const run = async () => {
      try {
        const existing = await tryLoadExistingSegment({
          messageId,
          teId,
          videoUrl: url || mediaSrc || null,
        });
        if (
          existing?.table_corners &&
          (existing?.motion_point_segments?.length ||
            existing?.activity_islands?.length)
        ) {
          applySegment(existing);
          if (!cancelled) {
            setKotDetectStatus("");
            setKotDetectPhase("done");
          }
          return;
        }
        await enqueueSegmentJob({
          messageId,
          teId,
          videoUrl: url || mediaSrc || null,
          tableCorners: resolvedTableCorners,
        });
      } catch (err) {
        if (!cancelled) {
          setKotDetectPhase("error");
          setKotDetectStatus(
            err?.message ||
              t(
                "kotPointsNeedCalibration",
                "Calibrate the table first to detect game points."
              )
          );
        }
        return;
      }

      const poll = async () => {
        if (cancelled) return;
        try {
          const wrap = await fetchSegmentResults({
            messageId,
            teId,
            videoUrl: url || mediaSrc || null,
          });
          if (wrap?.status_text) {
            const pct = Number(wrap.progress);
            const label = String(wrap.status_text);
            setKotDetectStatus(
              Number.isFinite(pct) && pct > 0 && pct < 1
                ? `${label} (${Math.round(pct * 100)}%)`
                : label
            );
          }
          if (wrap?.status === "complete" && wrap.result) {
            applySegment(wrap.result);
            const found = Boolean(
              wrap.result.motion_point_segments?.length ||
                wrap.result.activity_islands?.length
            );
            setKotDetectPhase(found ? "done" : "error");
            setKotDetectStatus(
              found
                ? ""
                : t(
                    "kotPointsNoneFound",
                    "No game points found near the table."
                  )
            );
            if (pollTimer) window.clearInterval(pollTimer);
            return;
          }
          if (wrap?.status === "failed") {
            setKotDetectPhase("error");
            setKotDetectStatus(
              wrap.error_message ||
                t("kotPointsDetectFailed", "Could not detect game points.")
            );
            if (pollTimer) window.clearInterval(pollTimer);
          }
        } catch {
          /* ignore */
        }
      };

      await poll();
      pollTimer = window.setInterval(poll, 2500);
    };

    void run();
    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [
    enableGamePoints,
    teId,
    messageId,
    url,
    mediaSrc,
    kotDetectRequested,
    kotDetectNonce,
    hasExistingCorners,
    resolvedTableCorners,
    t,
  ]);

  const tableCornersForOverlay = resolvedTableCorners;

  const ballDetectionRoi = useMemo(() => {
    const stored = normalizeStoredDetectionRoi(
      ballResult?.settings?.detection_roi
    );
    if (stored) return stored;
    return computeDetectionRoiNorm({
      cornersNorm: tableCornersForOverlay,
      playerRois: {
        left:
          playerRois.left ||
          activeGeometry?.roi_left ||
          poseResult?.settings?.roi_left ||
          null,
        right:
          playerRois.right ||
          activeGeometry?.roi_right ||
          poseResult?.settings?.roi_right ||
          null,
      },
    });
  }, [
    ballResult,
    tableCornersForOverlay,
    playerRois,
    activeGeometry,
    poseResult,
  ]);

  const hasExistingCalibration = hasExistingCorners;

  const poseRangeLabel = useMemo(() => {
    if (poseStartFrame == null && poseEndFrame == null) return null;
    const a = poseStartFrame != null ? poseStartFrame : 0;
    const b = poseEndFrame != null ? poseEndFrame : "…";
    return `f${a}–f${b}`;
  }, [poseStartFrame, poseEndFrame]);

  const poseRangeMatchesResult = useMemo(() => {
    if (!poseResult?.samples?.length) return false;
    if (!poseResult?.settings) return poseStartFrame == null && poseEndFrame == null;
    const rs = Number(poseResult.settings.start_frame) || 0;
    const re =
      poseResult.settings.end_frame == null || poseResult.settings.end_frame === ""
        ? null
        : Number(poseResult.settings.end_frame);
    const wantStart = poseStartFrame != null ? poseStartFrame : 0;
    const wantEnd = poseEndFrame != null ? poseEndFrame : null;
    const resultEnd = Number.isFinite(re) && re >= 0 ? re : null;
    return wantStart === rs && wantEnd === resultEnd;
  }, [poseResult, poseStartFrame, poseEndFrame]);

  /** Pose samples already cover this clip — unlock stroke classification. */
  const fullClipPoseReady = useMemo(() => {
    if (!poseResult?.samples?.length) return false;
    const startRaw = poseResult.settings?.start_frame;
    const start =
      startRaw == null || startRaw === "" ? 0 : Number(startRaw);
    if (Number.isFinite(start) && start > 1) return false;
    const endRaw = poseResult.settings?.end_frame;
    const end =
      endRaw == null || endRaw === "" ? null : Number(endRaw);
    if (end == null || !Number.isFinite(end)) return true;
    const fps =
      Number(poseResult.settings?.fps) > 1
        ? Number(poseResult.settings.fps)
        : 30;
    if (!(duration > 0)) return true;
    const videoLast = Math.floor(duration * fps) - 1;
    return end >= videoLast - Math.max(2, Math.round(fps * 0.35));
  }, [poseResult, duration]);

  const markPoseIn = useCallback(() => {
    const f = currentFrameIndex;
    setPoseStartFrame(f);
    setPoseEndFrame((end) => (end != null && end < f ? f : end));
    setShowControls(true);
    videoRef.current?.pause();
  }, [currentFrameIndex]);

  const markPoseOut = useCallback(() => {
    const f = currentFrameIndex;
    setPoseEndFrame(f);
    setPoseStartFrame((start) => (start != null && start > f ? f : start));
    setShowControls(true);
    videoRef.current?.pause();
  }, [currentFrameIndex]);

  const clearPoseRange = useCallback(() => {
    setPoseStartFrame(null);
    setPoseEndFrame(null);
  }, []);

  // Pose range markers: i = In, o = Out.
  useEffect(() => {
    if (!viewReady || calibrating || !enablePose || !teId) return undefined;
    const isEditableTarget = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return Boolean(target.closest("[contenteditable='true']"));
    };
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (isEditableTarget(event.target)) return;
      const lower = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (lower === "i") {
        event.preventDefault();
        markPoseIn();
        return;
      }
      if (lower === "o") {
        event.preventDefault();
        markPoseOut();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [viewReady, calibrating, enablePose, teId, markPoseIn, markPoseOut]);

  const beginCalibrationWizard = useCallback((intent = "pose", opts = {}) => {
    videoRef.current?.pause();
    setIsPlaying(false);
    setPoseError("");
    setBallError("");
    setShowControls(false);
    const nextIntent =
      intent === "ball" || intent === "fmo" ? "ball" : "pose";
    setCalibIntent(nextIntent);
    const existing = pickTableCorners(
      pendingCorners,
      activeGeometry,
      poseResult?.table_geometry
    );
    setPendingCorners(existing);
    const redoTable = Boolean(opts.forceTable) || !existing;
    setCalibPhase(redoTable ? "table" : "players");
    if (!existing) setSessionCalibrated(false);
  }, [pendingCorners, activeGeometry, poseResult]);

  /** Ball detection needs table corners + player boxes (serve: server only). */
  const resolveBallCalibration = useCallback(
    (opts = {}) => {
      const corners =
        pickTableCorners(
          opts.cornersNorm,
          pendingCorners,
          activeGeometry,
          poseResult?.table_geometry
        );
      const left =
        opts.playerRois?.left ||
        playerRois?.left ||
        activeGeometry?.roi_left ||
        poseResult?.settings?.roi_left ||
        null;
      const right =
        opts.playerRois?.right ||
        playerRois?.right ||
        activeGeometry?.roi_right ||
        poseResult?.settings?.roi_right ||
        null;
      const cornersOk = Array.isArray(corners) && corners.length === 4;
      const boxesMode = ballPlayerBoxesMode(
        opts.strokeKind ?? ballStrokeKind,
        serveDetection,
        analysisSettings.poseDetectionPlayer
      );
      const roisOk = ballPlayerRoisReady(boxesMode, left, right);
      return {
        corners: cornersOk ? corners : null,
        playerRois: left || right ? { left: left || null, right: right || null } : null,
        ready: cornersOk && roisOk,
        missingCorners: !cornersOk,
        missingPlayers: !roisOk,
        boxesMode,
      };
    },
    [
      pendingCorners,
      activeGeometry,
      poseResult,
      playerRois,
      ballStrokeKind,
      serveDetection,
      analysisSettings.poseDetectionPlayer,
    ]
  );

  const runPoseAnalysis = useCallback(async (opts = {}) => {
    if (!enablePose || !teId || poseAnalyzing) return;
    setPoseError("");

    const skipCalibCheck = Boolean(opts.skipCalibCheck);
    if (!skipCalibCheck && !sessionCalibrated && !hasExistingCorners) {
      // No table calibration — proceed with auto player pick (largest torso L/R).
    }

    const rois = opts.rois || playerRois;
    const mode = analysisSettings.poseDetectionPlayer || "A";
    const calibrated = roisForPoseDetectionPlayer(mode, rois || {});
    const roisComplete =
      (mode === "A" && calibrated.roiLeft) ||
      (mode === "B" && calibrated.roiRight) ||
      (mode === "Both" && calibrated.roiLeft && calibrated.roiRight);
    const roiLeft = roisComplete ? calibrated.roiLeft : null;
    const roiRight = roisComplete ? calibrated.roiRight : null;

    poseAbortRef.current?.abort();
    const ac = new AbortController();
    poseAbortRef.current = ac;
    setPoseAnalyzing(true);
    setPoseProgress(0);
    setPoseEtaSeconds(null);
    videoRef.current?.pause();
    setIsPlaying(false);
    // In/Out segment: only those frames are analyzed (and Pose3D when enabled).
    const startFrame = poseStartFrame != null ? poseStartFrame : 0;
    const endFrame = poseEndFrame != null ? poseEndFrame : -1;
    const swap = Boolean(opts.swapPlayers ?? swapPlayers);
    const wantPose3d =
      Boolean(opts.forcePose3d) ||
      (pose3dEnabled && pose3dMode !== "lidar_iphone");
    try {
      const result = await fetchOrEnqueuePoseResult(teId, {
        poseFps: effectivePoseFps,
        startFrame,
        endFrame,
        swapPlayers: swap,
        players: mode,
        roiLeft,
        roiRight,
        videoUrl: url || mediaSrc || null,
        sequenceOrder,
        rotationDeg: viewSetup.user?.rotation_deg || 0,
        flipH: Boolean(viewSetup.user?.flip_h),
        flipV: Boolean(viewSetup.user?.flip_v),
        viewSetup,
        pose3dEnabled: wantPose3d,
        pose3dMode:
          pose3dMode === "lidar_iphone" ? "mono_world" : pose3dMode,
        pose3dPlayer:
          mode === "B" ? "right" : mode === "Both" ? "both" : "left",
        cameraGroupId:
          pose3dMode === "multi_triangulate"
            ? pose3dCameraGroupId || null
            : null,
        pose3dPeers:
          pose3dMode === "multi_triangulate" && pose3dPeerUrl
            ? [
                {
                  camera_id: pose3dPeerCameraId || "cam1",
                  video_url: pose3dPeerUrl,
                },
              ]
            : null,
        forceServer: Boolean(opts.forcePose3d || opts.forceServer),
        signal: ac.signal,
        onProgress: ({ progress, etaSeconds }) => {
          setPoseProgress(progress || 0);
          setPoseEtaSeconds(etaSeconds);
        },
      });
      if (!result) {
        setPoseError(t("poseAnalysisFailed", "Pose analysis did not complete."));
        return;
      }
      setPoseResult(result);
      if (wantPose3d) {
        const jid = result?.job_id || result?.video_hash || "";
        setPose3dJobId(jid || "");
        setPose3dStatus(t("pose3dFetching", "Loading Pose3D…"));
        let loaded = null;
        for (let attempt = 0; attempt < 16; attempt += 1) {
          if (ac.signal?.aborted) break;
          try {
            const p3d = await fetchPose3dResult({
              jobId: jid || null,
              teId,
              mode: pose3dMode === "lidar_iphone" ? "mono_world" : pose3dMode,
              videoUrl: url || mediaSrc || null,
              sequenceOrder,
              signal: ac.signal,
            });
            const blob = p3d?.result || p3d || null;
            if (
              blob?.samples?.length ||
              blob?.settings?.fail_reason
            ) {
              loaded = blob;
              break;
            }
          } catch (_) {
            /* worker may still be writing pose3d sidecar */
          }
          await new Promise((r) => setTimeout(r, 1500));
        }
        if (loaded?.samples?.length) {
          setPose3dResult(loaded);
          setPose3dStatus(
            t("pose3dReadyWeb", "Pose3D ready — open 3D view")
          );
          setPose3dViewerOpen(true);
        } else if (loaded?.settings?.fail_reason) {
          setPose3dResult(loaded);
          setPose3dStatus(
            t(
              "pose3dFailReason",
              "Pose3D unavailable: {{reason}}",
              { reason: String(loaded.settings.fail_reason) }
            )
          );
          setPose3dViewerOpen(true);
        } else {
          setPose3dStatus(
            t(
              "pose3dPending",
              "Pose3D not ready yet — try Regenerate"
            )
          );
        }
      }
      // Keep skeleton hidden; moderators can toggle via Pose.
      if (poseResultHasBothPlayers(result)) {
        setAnalysisSettings((prev) =>
          normalizeVideoAnalysisSettings({
            ...prev,
            poseDetectionPlayer: "Both",
          })
        );
      }
      setSessionCalibrated(true);
      if (result.table_geometry) setActiveGeometry(result.table_geometry);
      if (result.settings?.start_frame != null) {
        setPoseStartFrame(Number(result.settings.start_frame) || 0);
      }
      if (result.settings?.end_frame != null && result.settings.end_frame >= 0) {
        setPoseEndFrame(Number(result.settings.end_frame));
      }
      if (result.settings?.roi_left || result.settings?.roi_right) {
        setPlayerRois({
          left: result.settings.roi_left || null,
          right: result.settings.roi_right || null,
        });
      }
      // Seek using analysis fps when available (matches sample t_ms / frame_index).
      const analysisFps = Number(result.settings?.fps);
      const fd =
        Number.isFinite(analysisFps) && analysisFps > 1
          ? 1 / analysisFps
          : frameDurationRef.current || 1 / PLAYER_DEFAULT_FPS;
      const seekFrame =
        result.settings?.start_frame != null
          ? Number(result.settings.start_frame) || 0
          : startFrame;
      const seekSec = Math.max(0, seekFrame * fd);
      const video = videoRef.current;
      if (video && Number.isFinite(seekSec)) {
        video.currentTime = seekSec;
        setCurrentTime(seekSec);
        video.play().catch(() => {});
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      setPoseError(err?.message || t("poseAnalysisFailed", "Pose analysis failed."));
    } finally {
      setPoseAnalyzing(false);
      setPoseProgress(0);
      setPoseEtaSeconds(null);
    }
  }, [
    enablePose,
    teId,
    url,
    mediaSrc,
    sessionCalibrated,
    hasExistingCalibration,
    poseAnalyzing,
    poseStartFrame,
    poseEndFrame,
    swapPlayers,
    playerRois,
    viewSetup,
    effectivePoseFps,
    analysisSettings.poseDetectionPlayer,
    pose3dEnabled,
    pose3dMode,
    pose3dCameraGroupId,
    pose3dPeerUrl,
    pose3dPeerCameraId,
    t,
    sequenceOrder,
  ]);

  const cachedPose3dMatches = useMemo(
    () =>
      pose3dMatchesSegment(
        pose3dResult,
        poseStartFrame,
        poseEndFrame,
        poseResult,
        effectivePoseFps
      ),
    [
      pose3dResult,
      poseStartFrame,
      poseEndFrame,
      poseResult,
      effectivePoseFps,
    ]
  );

  const toggleKneesJointOverlay = useCallback(() => {
    if (isPlaying) return;
    setJointOverlayMode((mode) => (mode === "knees" ? "off" : "knees"));
  }, [isPlaying]);

  const handlePoseButton = useCallback(() => {
    if (isPlaying) return;

    if (enablePose && teId) {
      const pose3dOn = pose3dEnabled && pose3dMode !== "lidar_iphone";
      // Cached Pose3D for this In/Out + fps → open viewer (no re-run).
      if (pose3dOn && cachedPose3dMatches && poseFpsMatchesResult) {
        setPose3dViewerOpen(true);
        setPose3dStatus(
          t("pose3dCachedReady", "Pose3D cached — Open 3D or Regenerate")
        );
        return;
      }
      // Need Pose3D / fps / segment mismatch → run analysis at current settings.
      if (pose3dOn && (!cachedPose3dMatches || !poseFpsMatchesResult)) {
        runPoseAnalysis({ skipCalibCheck: true });
        return;
      }
      // Existing 2D result: with joint overlay, Pose toggles knee angle circles;
      // skeleton overlay is moderator-only (hidden by default).
      if (poseResult && poseRangeMatchesResult && poseFpsMatchesResult) {
        if (jointOverlayEnabled) {
          toggleKneesJointOverlay();
          return;
        }
        if (!canSeeVideoAnalysisInfo) return;
        setPoseOverlayOn((v) => !v);
        return;
      }
      runPoseAnalysis({ skipCalibCheck: true });
      return;
    }

    // Joint-overlay-only path (e.g. table replay): Pose toggles knee angles.
    if (jointOverlayEnabled) {
      toggleKneesJointOverlay();
    }
  }, [
    isPlaying,
    jointOverlayEnabled,
    enablePose,
    teId,
    toggleKneesJointOverlay,
    poseResult,
    poseRangeMatchesResult,
    poseFpsMatchesResult,
    pose3dEnabled,
    pose3dMode,
    cachedPose3dMatches,
    runPoseAnalysis,
    canSeeVideoAnalysisInfo,
    t,
  ]);

  const regeneratePose3d = useCallback(() => {
    if (!enablePose || !teId || poseAnalyzing) return;
    setPose3dEnabled(true);
    runPoseAnalysis({
      skipCalibCheck: true,
      forcePose3d: true,
      forceServer: true,
    });
  }, [enablePose, teId, poseAnalyzing, runPoseAnalysis]);

  const runBallAnalysis = useCallback(
    async (detectorOverride = null, opts = {}) => {
      if (!enablePose || !teId || ballAnalyzing) return;
      const detector = detectorOverride || ballDetector || "tracknet";
      setBallError("");

      const calib = resolveBallCalibration(opts);
      if (!calib.ready) {
        setBallError(
          calib.boxesMode === "A"
            ? t(
                "ballNeedsServerPlayerA",
                "Ball detection needs table corners and the server (Player A / left) box. Calibrate first."
              )
            : calib.boxesMode === "B"
              ? t(
                  "ballNeedsServerPlayerB",
                  "Ball detection needs table corners and the server (Player B / right) box. Calibrate first."
                )
              : t(
                  "ballNeedsTableAndPlayers",
                  "Ball detection needs table corners and both player boxes. Calibrate first."
                )
        );
        // Resume mid-wizard if table already exists.
        if (!calib.missingCorners && calib.missingPlayers) {
          setCalibIntent("ball");
          setPendingCorners(calib.corners);
          setPendingBallDetector(detector);
          setCalibPhase("players");
          setShowControls(false);
          videoRef.current?.pause();
          setIsPlaying(false);
          return;
        }
        setPendingBallDetector(detector);
        beginCalibrationWizard("ball");
        return;
      }

      const detectionRoi =
        clampDetectionRoiNorm(opts.detectionRoi) ||
        clampDetectionRoiNorm(opts.detection_roi) ||
        null;
      if (!detectionRoi && !opts.skipDetRoiConfirm) {
        setCalibIntent("ball");
        setPendingCorners(calib.corners);
        setPlayerRois(calib.playerRois);
        setPendingBallDetector(detector);
        setCalibPhase("detroi");
        setShowControls(false);
        videoRef.current?.pause();
        setIsPlaying(false);
        return;
      }

      ballAbortRef.current?.abort();
      const ac = new AbortController();
      ballAbortRef.current = ac;
      setBallAnalyzing(true);
      setBallProgress(0);
      setBallEtaSeconds(null);
      videoRef.current?.pause();
      setIsPlaying(false);
      try {
        const result = await fetchOrEnqueueBallResult(teId, {
          detector,
          videoUrl: url || mediaSrc || null,
          sequenceOrder,
          cornersNorm: calib.corners,
          playerRois: calib.playerRois,
          detectionRoi:
            detectionRoi ||
            computeDetectionRoiNorm({
              cornersNorm: calib.corners,
              playerRois: calib.playerRois,
            }),
          ballDetectionFps: 60,
          strokeKind: ballStrokeKind,
          strokeWindowsMs: buildBallStrokeWindows(
            ballStrokeDetections,
            ballStrokeKind
          ),
          force: Boolean(opts.force),
          signal: ac.signal,
          onProgress: ({ progress, etaSeconds }) => {
            setBallProgress(progress || 0);
            setBallEtaSeconds(etaSeconds);
          },
        });
        if (!result) {
          setBallError(t("ballAnalysisFailed", "Ball analysis did not complete."));
          return;
        }
        setBallResult(result);
        setBallOverlayOn(true);
        setBallDetector(detector);
        setSessionCalibrated(true);
        setPendingBallDetector(null);
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (
          err?.code === "TABLE_GEOMETRY_REQUIRED" ||
          err?.code === "BALL_CALIBRATION_REQUIRED"
        ) {
          const boxesMode = resolveBallCalibration().boxesMode;
          setBallError(
            boxesMode === "A"
              ? t(
                  "ballNeedsServerPlayerA",
                  "Ball detection needs table corners and the server (Player A / left) box. Calibrate first."
                )
              : boxesMode === "B"
                ? t(
                    "ballNeedsServerPlayerB",
                    "Ball detection needs table corners and the server (Player B / right) box. Calibrate first."
                  )
                : t(
                    "ballNeedsTableAndPlayers",
                    "Ball detection needs table corners and both player boxes. Calibrate first."
                  )
          );
          setPendingBallDetector(detector);
          beginCalibrationWizard("ball");
          return;
        }
        setBallError(
          err?.message || t("ballAnalysisFailed", "Ball analysis failed.")
        );
      } finally {
        setBallAnalyzing(false);
        setBallProgress(0);
        setBallEtaSeconds(null);
      }
    },
    [
      enablePose,
      teId,
      ballAnalyzing,
      ballDetector,
      resolveBallCalibration,
      analysisSettings.ballDetectionFps,
      url,
      mediaSrc,
      beginCalibrationWizard,
      t,
      sequenceOrder,
      poseResult,
      ballStrokeKind,
      ballStrokeDetections,
    ]
  );

  const handleBallButton = useCallback(() => {
    if (!enablePose || !teId) return;
    const matchesDetector = ballSelectionMatches(
      ballResult,
      ballDetector,
      ballStrokeKind
    );
    if (matchesDetector) {
      setBallOverlayOn((v) => !v);
      return;
    }
    runBallAnalysis(ballDetector);
  }, [enablePose, teId, ballResult, ballDetector, ballStrokeKind, runBallAnalysis]);

  const regenerateBallAnalysis = useCallback(() => {
    if (!enablePose || !teId || ballAnalyzing || poseAnalyzing || calibrating) {
      return;
    }
    const storedRoi = normalizeStoredDetectionRoi(
      ballResult?.settings?.detection_roi
    );
    setBallError("");
    setBallResult(null);
    setBallOverlayOn(false);
    runBallAnalysis(ballDetector, {
      force: true,
      skipDetRoiConfirm: Boolean(storedRoi) || sessionCalibrated || hasExistingCalibration,
      detectionRoi: storedRoi || undefined,
    });
  }, [
    enablePose,
    teId,
    ballAnalyzing,
    poseAnalyzing,
    calibrating,
    ballResult,
    ballDetector,
    sessionCalibrated,
    hasExistingCalibration,
    runBallAnalysis,
  ]);

  const handleBallDetectorChange = useCallback(
    (next) => {
      const d = BALL_DETECTORS.has(String(next || "").toLowerCase())
        ? String(next).toLowerCase()
        : "tracknet";
      setBallDetector(d);
      setBallError("");
      if (ballResult?.settings?.detector !== d) {
        setBallResult(null);
      }
    },
    [ballResult]
  );

  const handleBallStrokeKindChange = useCallback(
    (next) => {
      const kind = normalizeBallStrokeKind(next);
      setBallStrokeKind(kind);
      setBallError("");
      if (
        normalizeBallStrokeKind(ballResult?.settings?.stroke_kind) !== kind
      ) {
        setBallResult(null);
      }
    },
    [ballResult]
  );

  const handleStartCalibrate = useCallback(() => {
    beginCalibrationWizard("pose", { forceTable: true });
  }, [beginCalibrationWizard]);

  /** Draw / redraw the player box used for knees/elbows (selected A or B). */
  const beginJointPlayerBoxSelect = useCallback(() => {
    if (!teId || isPlaying) return;
    videoRef.current?.pause();
    setIsPlaying(false);
    setPoseError("");
    setShowControls(false);
    setCalibIntent("joint");
    setCalibPhase("players");
  }, [teId, isPlaying]);

    /**
   * Auto-detect player(s) on the paused frame (largest shoulder→hip on each
   * half; skip poses missing shoulder/hip), seed tracked joint ROIs for the
   * A/B/Both setting, then run pose on ROI crops only.
   */
  const handleAutoDetectPlayers = useCallback(async () => {
    if (!enablePose || !teId || poseAnalyzing || autoDetecting || calibrating) {
      return;
    }
    setPoseError("");
    videoRef.current?.pause();
    setIsPlaying(false);

    poseAbortRef.current?.abort();
    const ac = new AbortController();
    poseAbortRef.current = ac;
    setAutoDetecting(true);

    const mode = normalizePoseDetectionPlayer(
      analysisSettings.poseDetectionPlayer || "A"
    );
    const needLeft = mode === "A" || mode === "Both";
    const needRight = mode === "B" || mode === "Both";
    const tableCorners = resolvedTableCorners;

    try {
      const videoEl = videoRef.current;
      const frameJpeg = await capturePausedVideoJpeg(
        videoEl,
        url || mediaSrc || null,
        ac.signal
      );
      if (!frameJpeg) {
        throw new Error(
          t(
            "poseRoiCaptureFailed",
            "Could not capture this video frame for detection. Try again."
          )
        );
      }
      const captureMs =
        videoEl && Number.isFinite(videoEl.currentTime)
          ? Math.max(0, Math.round(videoEl.currentTime * 1000))
          : Math.round((currentTime || 0) * 1000);
      const videoSize = {
        w: videoEl?.videoWidth || videoPixelSize.w,
        h: videoEl?.videoHeight || videoPixelSize.h,
      };

      const data = await detectPoseCalibrationFrame(
        teId,
        captureMs,
        ac.signal,
        frameJpeg,
        videoSize,
        url || mediaSrc || null,
        viewSetup,
        tableCorners
      );
      if (ac.signal.aborted) return;

      const suggested = roisFromSuggestedPlayers(data);
      const left = needLeft ? suggested.left : null;
      const right = needRight ? suggested.right : null;
      const roisOk =
        (!needLeft || (left && left.w >= 0.04 && left.h >= 0.06)) &&
        (!needRight || (right && right.w >= 0.04 && right.h >= 0.06));
      if (!roisOk) {
        throw new Error(
          mode === "Both"
            ? t(
                "poseAutoDetectMissingBoth",
                "Could not auto-detect both players. Pause when both are fully visible, or use Calibrate to draw boxes."
              )
            : mode === "B"
              ? t(
                  "poseAutoDetectMissingB",
                  "Could not auto-detect Player B (right / D). Pause when the player is fully visible, or draw the box manually."
                )
              : t(
                  "poseAutoDetectMissingA",
                  "Could not auto-detect Player A (left / G). Pause when the player is fully visible, or draw the box manually."
                )
        );
      }

      const rois = { left, right };
      setPlayerRois(rois);
      setSessionCalibrated(true);

      if (tableId && resolvedTableCorners) {
        try {
          const saved = await saveTableGeometry({
            tableId,
            cornersNorm:
              resolvedTableCorners,
            roiLeft: left || null,
            roiRight: right || null,
          });
          setActiveGeometry(saved);
        } catch (err) {
          // Non-fatal: still run pose with in-memory ROIs.
          console.warn("[handleAutoDetectPlayers] save ROIs failed", err);
        }
      }

      setAutoDetecting(false);
      if (effectiveKotMatch) return;
      await runPoseAnalysis({
        skipCalibCheck: true,
        rois,
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      setPoseError(
        err?.message ||
          t(
            "poseAutoDetectFailed",
            "Auto-detect failed. Try again or draw the player boxes manually."
          )
      );
    } finally {
      if (!ac.signal.aborted) setAutoDetecting(false);
    }
  }, [
    activeGeometry,
    analysisSettings.poseDetectionPlayer,
    autoDetecting,
    calibrating,
    currentTime,
    enablePose,
    effectiveKotMatch,
    mediaSrc,
    pendingCorners,
    poseAnalyzing,
    resolvedTableCorners,
    runPoseAnalysis,
    t,
    tableId,
    teId,
    url,
    videoPixelSize.h,
    videoPixelSize.w,
    viewSetup,
  ]);

  const persistTableDependentMetrics = useCallback(
    (corners, savedGeo) => {
      const pts = pickTableCorners(corners);
      if (!pts) return;
      setPoseResult((prev) => {
        if (!prev) return prev;
        return applyTableCornersToPoseResult(prev, pts, viewSetup);
      });
      if (!teId || effectiveKotMatch) return;
      void refreshTableDependentMetrics({
        teId,
        cornersNorm: pts,
        geometryId: savedGeo?.geometry_id || null,
        videoUrl: url || mediaSrc || null,
        sequenceOrder,
        detector: ballDetector || null,
      })
        .then((data) => {
          if (data?.result && typeof data.result === "object") {
            setPoseResult((prev) => {
              if (!prev) return data.result;
              return {
                ...prev,
                ...data.result,
                samples: Array.isArray(data.result.samples)
                  ? data.result.samples
                  : prev.samples,
                settings: {
                  ...(prev.settings || {}),
                  ...(data.result.settings || {}),
                },
              };
            });
          }
          if (data?.ball_result && typeof data.ball_result === "object") {
            setBallResult((prev) => {
              if (!prev) return data.ball_result;
              const prevDet = String(prev.settings?.detector || "");
              const nextDet = String(
                data.ball_result.settings?.detector || ""
              );
              if (prevDet && nextDet && prevDet !== nextDet) return prev;
              return data.ball_result;
            });
          }
        })
        .catch((err) => {
          console.warn(
            "[refreshTableDependentMetrics]",
            err?.message || err
          );
        });
    },
    [ballDetector, effectiveKotMatch, mediaSrc, sequenceOrder, teId, url, viewSetup]
  );

  const handleTableCornersContinue = useCallback(
    async (corners) => {
      setCalibSaving(true);
      setPoseError("");
      setBallError("");
      try {
        setPendingCorners(corners);
        let saved = null;
        if (tableId) {
          saved = await saveTableGeometry({
            tableId,
            cornersNorm: corners,
          });
          setActiveGeometry(saved);
        } else {
          saved = {
            corners_norm: corners,
            geometry_id: null,
            table_id: null,
          };
          setActiveGeometry(saved);
        }
        persistTableDependentMetrics(corners, saved);
        videoRef.current?.pause();
        tableGlowPendingRef.current = true;
        if (effectiveKotMatch) {
          setSessionCalibrated(true);
          setCalibPhase(null);
          setShowControls(true);
          beginTableGlowIfPending();
          setKotDetectStatus(
            t(
              "kotPointsCalibratedHint",
              "Table calibrated — tap Detect points."
            )
          );
          return;
        }
        // Ball and pose both need player boxes next.
        setCalibPhase("players");
      } catch (err) {
        setPoseError(err?.message || t("poseCalibrateSaveFailed", "Could not save corners."));
      } finally {
        setCalibSaving(false);
      }
    },
    [beginTableGlowIfPending, effectiveKotMatch, persistTableDependentMetrics, tableId, t]
  );

  useEffect(() => {
    const live = resolvedTableCorners;
    if (!teId || !live || !poseResult?.samples?.length) return;
    if (!cornersChanged(pickTableCorners(poseResult), live)) return;
    persistTableDependentMetrics(live, activeGeometry);
  }, [
    activeGeometry,
    persistTableDependentMetrics,
    poseResult,
    resolvedTableCorners,
    teId,
  ]);

  const handlePlayerCalibConfirm = useCallback(
    async ({ roiLeft, roiRight, swapPlayers: swap }) => {
      setCalibSaving(true);
      try {
        const rois =
          calibIntent === "joint"
            ? {
                left: roiLeft || playerRois.left || null,
                right: roiRight || playerRois.right || null,
              }
            : { left: roiLeft, right: roiRight };
        setPlayerRois(rois);
        setSwapPlayers(Boolean(swap));
        setSessionCalibrated(true);
        setCalibPhase(null);
        setShowControls(true);
        if (calibIntent === "joint") {
          jointOverlayStoreRef.current.clear();
          setPoseError("");
          // Persist when a table geometry already exists; otherwise keep local for this clip.
          if (tableId && resolvedTableCorners) {
            try {
              const saved = await saveTableGeometry({
                tableId,
                cornersNorm: resolvedTableCorners,
                roiLeft: rois.left,
                roiRight: rois.right,
              });
              setActiveGeometry(saved);
            } catch (err) {
              setPoseError(
                err?.message ||
                  t(
                    "poseCalibrateSaveFailed",
                    "Could not save table calibration."
                  )
              );
            }
          }
          return;
        }
        // Persist Player A/B ROIs on the table calibration (shared across TEs).
        if (tableId && resolvedTableCorners) {
          try {
            const saved = await saveTableGeometry({
              tableId,
              cornersNorm:
                resolvedTableCorners,
              roiLeft: roiLeft || null,
              roiRight: roiRight || null,
            });
            setActiveGeometry(saved);
          } catch (err) {
            const msg =
              err?.message ||
              t("poseCalibrateSaveFailed", "Could not save table calibration.");
            if (calibIntent === "ball") setBallError(msg);
            else setPoseError(msg);
          }
        }
        if (calibIntent === "ball") {
          const detector = pendingBallDetector || ballDetector || "tracknet";
          const boxesMode = ballPlayerBoxesMode(
            ballStrokeKind,
            serveDetection,
            analysisSettings.poseDetectionPlayer
          );
          if (!ballPlayerRoisReady(boxesMode, roiLeft, roiRight)) {
            setBallError(
              boxesMode === "A"
                ? t(
                    "ballNeedsServerPlayerA",
                    "Ball detection needs the server (Player A / left) box."
                  )
                : boxesMode === "B"
                  ? t(
                      "ballNeedsServerPlayerB",
                      "Ball detection needs the server (Player B / right) box."
                    )
                  : t(
                      "ballNeedsBothPlayerBoxes",
                      "Ball detection needs both Player A and Player B boxes."
                    )
            );
            setCalibPhase("players");
            setShowControls(false);
            return;
          }
          setPendingBallDetector(detector);
          setCalibPhase("detroi");
          setShowControls(false);
          return;
        }
        beginTableGlowIfPending();
        if (effectiveKotMatch) {
          setKotDetectStatus(
            t(
              "kotPointsCalibratedHint",
              "Table calibrated — tap Detect points."
            )
          );
          return;
        }
        await runPoseAnalysis({
          skipCalibCheck: true,
          swapPlayers: Boolean(swap),
          rois,
        });
      } finally {
        setCalibSaving(false);
      }
    },
    [
      runPoseAnalysis,
      effectiveKotMatch,
      tableId,
      pendingCorners,
      activeGeometry,
      calibIntent,
      ballDetector,
      pendingBallDetector,
      ballStrokeKind,
      serveDetection,
      analysisSettings.poseDetectionPlayer,
      resolvedTableCorners,
      beginTableGlowIfPending,
      playerRois.left,
      playerRois.right,
      t,
    ]
  );

  const proposedDetectionRoi = useMemo(
    () =>
      computeDetectionRoiNorm({
        cornersNorm: resolvedTableCorners,
        playerRois: {
          left:
            playerRois?.left ||
            activeGeometry?.roi_left ||
            poseResult?.settings?.roi_left ||
            null,
          right:
            playerRois?.right ||
            activeGeometry?.roi_right ||
            poseResult?.settings?.roi_right ||
            null,
        },
      }),
    [resolvedTableCorners, activeGeometry, poseResult, playerRois]
  );

  const handleDetectionRoiAccept = useCallback(
    async (roi) => {
      const box = clampDetectionRoiNorm(roi) || proposedDetectionRoi;
      if (!box) {
        setBallError(
          t(
            "ballDetRoiInvalid",
            "Detection ROI is missing. Draw a box over the play area."
          )
        );
        return;
      }
      setCalibSaving(true);
      try {
        setCalibPhase(null);
        setCalibIntent("pose");
        setShowControls(true);
        beginTableGlowIfPending();
        const detector = pendingBallDetector || ballDetector || "tracknet";
        await runBallAnalysis(detector, {
          cornersNorm:
            resolvedTableCorners,
          playerRois,
          detectionRoi: box,
        });
      } finally {
        setCalibSaving(false);
      }
    },
    [
      proposedDetectionRoi,
      pendingBallDetector,
      ballDetector,
      runBallAnalysis,
      pendingCorners,
      activeGeometry,
      playerRois,
      resolvedTableCorners,
      beginTableGlowIfPending,
      t,
    ]
  );

  const handleCancelCalibration = useCallback(() => {
    tableGlowPendingRef.current = false;
    setCalibPhase(null);
    setCalibIntent("pose");
    setCalibSaving(false);
    setPendingBallDetector(null);
    setShowControls(true);
  }, []);

  const handleFlushPose = useCallback(async () => {
    if (!teId || poseAnalyzing) return;
    poseAbortRef.current?.abort();
    ballAbortRef.current?.abort();
    setPoseError("");
    setBallError("");
    setShowControls(true);
    try {
      await Promise.all([
        flushPoseResults(teId, {
          videoUrl: url || mediaSrc || null,
          sequenceOrder,
        }),
        flushBallResults(teId, {
          videoUrl: url || mediaSrc || null,
          sequenceOrder,
        }),
      ]);
    } catch (err) {
      setPoseError(
        err?.message ||
          t("poseFlushFailed", "Could not clear pose and ball data.")
      );
      return;
    }
    setPoseResult(null);
    setBallResult(null);
    setPoseOverlayOn(false);
    setBallOverlayOn(false);
    setPoseProgress(0);
    setBallProgress(0);
    setPoseEtaSeconds(null);
    setBallEtaSeconds(null);
    setPoseAnalyzing(false);
    setBallAnalyzing(false);
    setSessionCalibrated(false);
    setSwapPlayers(false);
    setPlayerRois({ left: null, right: null });
    setPoseStartFrame(null);
    setPoseEndFrame(null);
    setPendingCorners(null);
    setCalibPhase(null);
    tableGlowPendingRef.current = false;
    setTableHighlightUntil(0);
    setPose3dResult(null);
    setPose3dJobId("");
    setPose3dStatus("");
    beginCalibrationWizard("pose");
  }, [teId, poseAnalyzing, beginCalibrationWizard, t, url, mediaSrc, sequenceOrder]);

  // If gate is off and media is already attached, reveal once element can play.
  useEffect(() => {
    if (needsViewGate || !mediaSrc) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    if (video.readyState >= 1) {
      setViewReady(true);
      if (eagerPlayback && video.readyState >= 2) setIsMediaOpening(false);
      if (autoPlay) {
        playReviewClip(video, {
          eager: eagerPlayback,
          onMuted: () => setAudioMuted(true),
        });
      }
    }
    return undefined;
  }, [needsViewGate, mediaSrc, autoPlay, url, eagerPlayback]);

  // Keep the on-screen clip paused while calibrating (never swap its src).
  useEffect(() => {
    if (!calibrating) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    video.pause();
    return undefined;
  }, [calibrating, calibPhase]);

  // Table replay is a few seconds. Don't leave "Preparing view…" up while
  // the phone is allowed to start on the first decoded frame.
  useEffect(() => {
    if (!eagerPlayback || !mediaSrc || mediaLoadError) return undefined;
    const timer = window.setTimeout(() => {
      setIsMediaOpening(false);
      if (autoPlay) {
        playReviewClip(videoRef.current, {
          eager: true,
          onMuted: () => setAudioMuted(true),
        });
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [eagerPlayback, mediaSrc, mediaLoadError, autoPlay, url]);

  // Never leave the loader stuck if metadata never fires.
  useEffect(() => {
    if (viewReady || mediaLoadError || !mediaSrc) return undefined;
    const timer = window.setTimeout(() => {
      setViewReady(true);
      if (eagerPlayback) setIsMediaOpening(false);
      if (autoPlay) {
        playReviewClip(videoRef.current, {
          eager: eagerPlayback,
          onMuted: () => setAudioMuted(true),
        });
      }
    }, eagerPlayback ? 400 : 1200);
    return () => window.clearTimeout(timer);
  }, [viewReady, mediaLoadError, mediaSrc, autoPlay, eagerPlayback]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onTimeUpdate = () => {
      onMediaTimeUpdate?.();
      const windowed = clampPlaybackWindow(video.currentTime, playbackWindowRef.current);
      if (Math.abs(windowed.time - video.currentTime) > 0.05) {
        video.currentTime = windowed.time;
      }
      if (windowed.held) {
        if (!video.paused) video.pause();
        setIsPlaying(false);
        setCurrentTime(windowed.time);
        return;
      }
      const t = windowed.time;
      const segments = kotPointSegmentsRef.current;
      if (kotPointsOnlyRef.current && segments.length) {
        const next = nextPointSeekSec(segments, t);
        if (next != null && !video.paused) {
          video.currentTime = next;
          setCurrentTime(next);
          return;
        }
        const last = segments[segments.length - 1];
        if (
          last &&
          t * 1000 >= last.end_ms - 80 &&
          !video.paused
        ) {
          video.pause();
          setIsPlaying(false);
          setCurrentTime(t);
          return;
        }
      }
      setCurrentTime(t);
    };
    const refreshFrameDuration = () => {
      frameDurationRef.current = getVideoFrameDurationSeconds(video, {
        fallbackFps: PLAYER_DEFAULT_FPS,
      });
    };
    const probeSourceFps = () => {
      const gen = ++fpsProbeGenRef.current;
      probeVideoSourceFps(video, {
        fallbackFps: PLAYER_DEFAULT_FPS,
        maxSamples: 18,
        timeoutMs: 1800,
      }).then((fps) => {
        if (gen !== fpsProbeGenRef.current) return;
        if (Number.isFinite(fps) && fps > 1) {
          frameDurationRef.current = 1 / fps;
          setVideoSourceFps(fps);
        }
      });
    };
    const onPlay = () => {
      if (holdPausedAfterSeekRef.current) {
        video.pause();
        setIsPlaying(false);
        return;
      }
      const segments = kotPointSegmentsRef.current;
      if (kotPointsOnlyRef.current && segments.length) {
        const snapped = snapToPointPlayback(segments, video.currentTime);
        if (Math.abs(snapped - video.currentTime) > 0.05) {
          video.currentTime = snapped;
          setCurrentTime(snapped);
        }
      }
      setIsPlaying(true);
      setIsBuffering(false);
      clearBufferStallTimer();
      applyPlaybackRate(video, playbackRate);
      refreshFrameDuration();
      const srcKey = video.currentSrc || "";
      if (srcKey && fpsProbedSrcRef.current !== srcKey) {
        fpsProbedSrcRef.current = srcKey;
        probeSourceFps();
      }
    };
    const onPause = () => {
      setIsPlaying(false);
      // Seek/scrub often fires waiting while paused — don't treat as stall.
      clearBufferStallTimer();
      setIsBuffering(false);
    };
    const onSeeked = () => {
      setCurrentTime(video.currentTime);
      if (holdPausedAfterSeekRef.current) {
        video.pause();
        setIsPlaying(false);
      }
    };
    const refreshHorizon = () => setDuration(mediaSeekHorizon(video));
    const onDurationChange = () => refreshHorizon();
    const onProgress = () => refreshHorizon();
    const onWaiting = () => {
      // During the initial open, keep the loading overlay (not "Buffering…").
      // Table replay should not cover the picture with that overlay.
      if (
        !eagerPlaybackRef.current &&
        (isMediaOpeningRef.current || (Number(video.currentTime) || 0) < 0.35)
      ) {
        setIsMediaOpening(true);
        return;
      }
      setIsBuffering(true);
      if (!video.paused) armBufferStallTimer();
    };
    const onStalled = () => {
      if (
        !eagerPlaybackRef.current &&
        (isMediaOpeningRef.current || (Number(video.currentTime) || 0) < 0.35)
      ) {
        setIsMediaOpening(true);
        return;
      }
      setIsBuffering(true);
      if (!video.paused) armBufferStallTimer();
    };
    const onPlaying = () => {
      if (holdPausedAfterSeekRef.current) {
        video.pause();
        setIsPlaying(false);
        return;
      }
      setIsMediaOpening(false);
      setIsBuffering(false);
      setLoadIdleSlow(false);
      setMediaLoadProgress(null);
      clearBufferStallTimer();
      applyPlaybackRate(video, playbackRate);
      refreshFrameDuration();
    };
    const onCanPlayThrough = () => {
      setIsMediaOpening(false);
      setIsBuffering(false);
      setLoadIdleSlow(false);
      setMediaLoadProgress(null);
      clearBufferStallTimer();
    };
    const onEnded = () => {
      onMediaEnded?.();
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("progress", onProgress);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("stalled", onStalled);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplaythrough", onCanPlayThrough);

    return () => {
      clearBufferStallTimer();
      fpsProbeGenRef.current += 1;
      if (strokePlayStopRef.current) {
        video.removeEventListener("timeupdate", strokePlayStopRef.current);
        strokePlayStopRef.current = null;
      }
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("stalled", onStalled);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplaythrough", onCanPlayThrough);
    };
  }, [playbackRate, url, mediaSrc, armBufferStallTimer, clearBufferStallTimer, onMediaEnded, onMediaTimeUpdate]);

  // While opening or mid-play buffering, refresh percent / ETA from buffered ranges.
  useEffect(() => {
    const needsTrack =
      Boolean(mediaSrc) &&
      !mediaLoadError &&
      (isMediaOpening || (isBuffering && isPlaying));
    if (!needsTrack) return undefined;
    const tick = () => {
      const el = videoRef.current;
      if (!el) return;
      const nowMs = Date.now();
      const bufferedEnd = bufferedEndSeconds(el);
      const samples = loadProgressSamplesRef.current;
      samples.push({ atMs: nowMs, bufferedEnd });
      if (samples.length > 40) samples.splice(0, samples.length - 40);
      setMediaLoadProgress(
        estimateVideoLoadProgress(el, { samples, nowMs })
      );
    };
    tick();
    const id = window.setInterval(tick, 400);
    return () => window.clearInterval(id);
  }, [
    mediaSrc,
    mediaLoadError,
    isMediaOpening,
    isBuffering,
    isPlaying,
  ]);

  // Scrubber / charts stay on `timeupdate` (~4Hz). Pose + ball canvas overlays
  // sync to presented frames via requestVideoFrameCallback so 60fps sources
  // stay fluid without re-rendering charts every frame (that froze playback).

  // Auto-hide UI after inactivity. Feedback review keeps its own bar.
  // Any chrome / HUD interaction bumps chromeEpoch so the 2s window restarts.
  useEffect(() => {
    if (keepEvalKotTransport) {
      setShowControls(true);
      return undefined;
    }
    if (replaySessionId) return undefined;
    if (!showControls || saving || calibrating || annotateOpen) return undefined;
    const hideMs =
      controlsHideMs != null && Number.isFinite(Number(controlsHideMs))
        ? Math.max(500, Number(controlsHideMs))
        : UI_HIDE_MS;
    const timer = window.setTimeout(() => {
      // Still scrubbing / holding a control — reschedule instead of hiding.
      if (controlsPointerDownRef.current) {
        setChromeEpoch((n) => n + 1);
        return;
      }
      setShowControls(false);
    }, hideMs);
    return () => window.clearTimeout(timer);
  }, [showControls, chromeEpoch, saving, calibrating, annotateOpen, isPlaying, rotationDeg, flipH, flipV, keepEvalKotTransport, mode, controlsHideMs, replaySessionId]);

  // Landscape immersive fullscreen
  useEffect(() => {
    if (mode !== "fullscreen") return undefined;

    const syncOrientation = () => {
      const landscape =
        typeof window.matchMedia === "function"
          ? window.matchMedia("(orientation: landscape)").matches
          : window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
    };
    syncOrientation();

    const onOrientationChange = () => syncOrientation();
    window.addEventListener("orientationchange", onOrientationChange);
    window.addEventListener("resize", onOrientationChange);

    return () => {
      window.removeEventListener("orientationchange", onOrientationChange);
      window.removeEventListener("resize", onOrientationChange);
    };
  }, [mode]);

  // Fullscreen API requires a user gesture — do not auto-call from an effect.
  // Landscape layout is handled via CSS; optional orientation lock stays best-effort
  // only when already in browser fullscreen from a prior gesture.
  useEffect(() => {
    if (mode !== "fullscreen" || !isLandscape) return undefined;
    if (!document.fullscreenElement) return undefined;
    try {
      window.screen.orientation?.lock?.("landscape")?.catch?.(() => {});
    } catch {
      /* not supported / not allowed */
    }
    return () => {
      try {
        window.screen.orientation?.unlock?.();
      } catch {
        /* ignore */
      }
    };
  }, [mode, isLandscape]);

  const rootClass = [
    "review-video-player",
    `review-video-player--${mode}`,
    scale > 1 ? "review-video-player--zoomed" : "",
    showControls && !calibrating
      ? "review-video-player--controls-visible"
      : "",
    annotateOpen ? "review-video-player--annotating" : "",
    feedbackRecording ? "review-video-player--feedback-recording" : "",
    replaySessionId ? "review-video-player--feedback-review" : "",
    basicPlayback ? "review-video-player--basic" : "",
    feedbackOnDemand ? "review-video-player--feedback-on-demand" : "",
    (basicPlayback || feedbackOnDemand) &&
    feedbackViewer &&
    feedbackSessions.length
      ? "review-video-player--has-feedback"
      : "",
    keepEvalKotTransport ? "review-video-player--transport-locked" : "",
    topChromeCollapsed ? "review-video-player--top-collapsed" : "",
    calibrating ? "review-video-player--calibrating" : "",
    canTransformVideo ? "review-video-player--transformable" : "",
    isLandscape ? "review-video-player--landscape" : "",
    !viewReady ? "review-video-player--view-pending" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={(node) => {
        rootRef.current = node;
        if (node && toolbarHost !== node) setToolbarHost(node);
      }}
      className={rootClass}
    >
      {feedbackRecording ? (
        <div className="review-video-player__feedback-mode review-video-player__feedback-mode--rec" role="status">
          <span className="review-video-player__feedback-mode-dot" aria-hidden />
          {t("feedbackRecording", "Recording feedback")}
        </div>
      ) : replaySessionId ? (
        <div
          className={`review-video-player__feedback-mode review-video-player__feedback-mode--review${
            feedbackListOpen ? " is-list-open" : ""
          }`}
          role="group"
          aria-label={t("feedbackReview", "Feedback review")}
        >
          <div className="review-video-player__feedback-mode-bar">
            {feedbackSessions.length ? (
              <button
                type="button"
                className={`review-video-player__feedback-mode-btn review-video-player__feedback-mode-btn--icon${
                  feedbackListOpen ? " is-on" : ""
                }`}
                aria-pressed={feedbackListOpen}
                aria-expanded={feedbackListOpen}
                aria-label={
                  feedbackListOpen
                    ? t("hideFeedbackList", "Hide feedbacks")
                    : t("showFeedbackList", "Show feedbacks")
                }
                title={
                  feedbackListOpen
                    ? t("hideFeedbackList", "Hide feedbacks")
                    : t("showFeedbackList", "Show feedbacks")
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedbackListOpen((open) => !open);
                }}
              >
                <MessageSquare size={14} strokeWidth={2.4} aria-hidden />
                <span className="review-video-player__feedback-mode-count">
                  {feedbackSessions.length}
                </span>
              </button>
            ) : null}
            <button
              type="button"
              className="review-video-player__feedback-mode-btn review-video-player__feedback-mode-btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                endFeedbackReview({ resume: true });
              }}
            >
              {t("continueVideo", "Continue video")}
            </button>
            <button
              type="button"
              className="review-video-player__feedback-mode-btn review-video-player__feedback-mode-btn--icon"
              onClick={(e) => {
                e.stopPropagation();
                endFeedbackReview({ resume: false });
              }}
              aria-label={t("closeFeedback", "Close feedback")}
              title={t("closeFeedback", "Close feedback")}
            >
              <X size={14} strokeWidth={2.4} aria-hidden />
            </button>
          </div>
          {feedbackListOpen && feedbackSessions.length ? (
            <div
              className="review-video-player__feedback-mode-list"
              role="list"
              aria-label={t("feedbackList", "Available feedbacks")}
            >
              {feedbackSessions.map((session, index) => (
                <button
                  key={session.id}
                  type="button"
                  role="listitem"
                  className={`review-video-player__feedback-mode-item${
                    replaySessionId === session.id ? " is-on" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToFeedback(session);
                    setFeedbackListOpen(false);
                  }}
                >
                  {t("feedbackN", "Feedback {{n}}", { n: index + 1 })}
                  {" · "}
                  {formatFeedbackClock(session.anchorMs)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {viewReady &&
      enablePose &&
      poseResult?.samples?.length &&
      !calibrating &&
      !playingStrokeReel &&
      poseChartsVisible ? (
        <PoseAngleCharts
          samples={poseResult.samples}
          currentTimeSec={deferredChartTime}
          visible={poseChartsVisible}
          onVisibleChange={setPoseChartsVisible}
          viewSetup={viewSetup}
          fhTopspinDetection={fhTopspinDetection}
          fhDetection={fhDetection}
          bhTopspinDetection={bhTopspinDetection}
          bhLeftDetection={bhLeftDetection}
          serveDetection={serveDetection}
          bounceHeatmap={ballResult?.serve_heatmap || ballResult?.serve_analysis?.heatmap || null}
          serveMetrics={
            ballResult?.serve_analysis?.metrics ||
            (serveDetection
              ? {
                  serve_try_count: serveDetection.serve_try_count,
                  serve_short_count: serveDetection.serve_short_count,
                  serve_long_count: serveDetection.serve_long_count,
                  serve_missed_count: serveDetection.serve_missed_count,
                }
              : null)
          }
          onSeek={seekTo}
        />
      ) : null}

      {canTransformVideo && !calibrating && (
        <div
          className="review-video-player__chrome review-video-player__chrome--top"
          aria-hidden={showControls || topChromeCollapsed ? undefined : true}
          onPointerDownCapture={onControlsActivityCapture}
          onPointerUpCapture={onControlsPointerUpCapture}
          onPointerCancelCapture={onControlsPointerUpCapture}
          onInputCapture={onControlsActivityCapture}
        >
          <button
            type="button"
            className="review-video-player__btn review-video-player__chrome-toggle"
            onClick={() => {
              setTopChromeCollapsed((collapsed) => {
                if (collapsed) setShowControls(true);
                return !collapsed;
              });
            }}
            aria-expanded={!topChromeCollapsed}
            aria-label={
              topChromeCollapsed
                ? t("expandVideoTools", "Expand tools")
                : t("collapseVideoTools", "Collapse tools")
            }
            title={
              topChromeCollapsed
                ? t("expandVideoTools", "Expand tools")
                : t("collapseVideoTools", "Collapse tools")
            }
          >
            {topChromeCollapsed ? (
              <ChevronDown size={18} strokeWidth={2.2} />
            ) : (
              <ChevronUp size={18} strokeWidth={2.2} />
            )}
          </button>
          <div
            className="review-video-player__orient"
            role="toolbar"
            aria-label={t("videoOrientation", "Video orientation")}
          >
            <button
              type="button"
              className="review-video-player__btn"
              onClick={() => rotateBy(-90)}
              disabled={saving || reuploading || requestingPhoneReupload}
              aria-label={t("rotateLeft", "Rotate left")}
              title={t("rotateLeft", "Rotate left")}
            >
              <RotateCcw size={18} strokeWidth={2.2} />
              <span>90°</span>
            </button>
            <button
              type="button"
              className="review-video-player__btn"
              onClick={() => rotateBy(90)}
              disabled={saving || reuploading || requestingPhoneReupload}
              aria-label={t("rotateRight", "Rotate right")}
              title={t("rotateRight", "Rotate right")}
            >
              <RotateCw size={18} strokeWidth={2.2} />
              <span>90°</span>
            </button>
            <button
              type="button"
              className={`review-video-player__btn ${
                flipH ? "review-video-player__btn--active" : ""
              }`}
              onClick={toggleFlipH}
              disabled={saving || reuploading || requestingPhoneReupload}
              aria-pressed={flipH}
              aria-label={t("flipHorizontal", "Flip horizontal")}
              title={t("flipHorizontal", "Flip horizontal")}
            >
              <FlipHorizontal2 size={18} strokeWidth={2.2} />
              <span>H</span>
            </button>
            <button
              type="button"
              className={`review-video-player__btn ${
                flipV ? "review-video-player__btn--active" : ""
              }`}
              onClick={toggleFlipV}
              disabled={saving || reuploading || requestingPhoneReupload}
              aria-pressed={flipV}
              aria-label={t("flipVertical", "Flip vertical")}
              title={t("flipVertical", "Flip vertical")}
            >
              <FlipVertical2 size={18} strokeWidth={2.2} />
              <span>V</span>
            </button>
            <button
              type="button"
              className="review-video-player__btn"
              onClick={resetUserTransform}
              disabled={saving || reuploading || requestingPhoneReupload || !hasChanges}
              aria-label={t("resetOrientation", "Reset")}
              title={t("resetOrientation", "Reset")}
            >
              <span className="review-video-player__reset-label">
                {t("reset", "Reset")}
              </span>
            </button>

            {teId && (
              <button
                type="button"
                className="review-video-player__btn"
                onClick={() => setShowClipComposer(true)}
                disabled={saving || reuploading || requestingPhoneReupload}
                aria-label={t("shareRally", "Share best rally")}
                title={t("shareRally", "Share best rally")}
              >
                <Share2 size={18} strokeWidth={2.2} />
                <span>{t("share", "Share")}</span>
              </button>
            )}
            <button
              type="button"
              className="review-video-player__btn"
              onClick={handleSaveTransform}
              disabled={saving || reuploading || requestingPhoneReupload || !messageId}
              aria-label={t("saveView", "Save view")}
              title={t(
                "saveVideoViewHint",
                "Save zoom, pan, and orientation for this video (does not re-encode the file)"
              )}
            >
              <Save size={18} strokeWidth={2.2} />
              <span>{saving ? t("saving", "Saving…") : t("save", "Save")}</span>
            </button>
            <button
              type="button"
              className="review-video-player__btn"
              onClick={handleReupload}
              disabled={
                saving ||
                reuploading ||
                requestingPhoneReupload ||
                !messageId ||
                !canBakeEncode
              }
              aria-label={
                is4KVideo && !orientDirty
                  ? t("encode4KVideo", "Encode 4K to 720p")
                  : t("bakeOrientsVideo", "Bake orientation")
              }
              title={
                is4KVideo
                  ? t(
                      "reuploadVideoEncode4KHint",
                      "Re-encode 4K to 720p at 60fps (and bake rotation/flip if set); zoom/pan ignored"
                    )
                  : t(
                      "reuploadVideoBakeHint",
                      "Re-encode with current rotation/flip and replace this video (zoom/pan ignored)"
                    )
              }
            >
              <Upload size={18} strokeWidth={2.2} />
              <span>
                {reuploading
                  ? reencodeProgress
                    ? t("bakeOrientsWorkingPct", "Re-encoding… {{pct}}%", {
                        pct: reencodeProgress.pct || 0,
                      })
                    : t("bakeOrientsWorking", "Re-encoding…")
                  : is4KVideo && !orientDirty
                    ? t("encode4K", "Encode")
                    : t("bakeOrients", "Bake")}
              </span>
            </button>
            <button
              type="button"
              className="review-video-player__btn"
              onClick={handleRequestPhoneReupload}
              disabled={
                saving ||
                reuploading ||
                requestingPhoneReupload ||
                !messageId ||
                typeof onRequestReupload !== "function"
              }
              aria-label={t(
                "requestOriginalReupload",
                "Request original from phone"
              )}
              title={t(
                "requestOriginalReuploadHint",
                "Clear this cloud video and ask the recording phone to re-upload the original"
              )}
            >
              <Smartphone size={18} strokeWidth={2.2} />
              <span>
                {requestingPhoneReupload
                  ? t("requestingOriginalReupload", "Requesting…")
                  : t("requestOriginal", "Original")}
              </span>
            </button>
            {enablePose && teId ? (
              <div className="review-video-player__pose-tools">
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    poseStartFrame != null
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={markPoseIn}
                  disabled={poseAnalyzing || autoDetecting || calibrating}
                  aria-label={t("poseMarkIn", "Mark pose start frame")}
                  title={t(
                    "poseMarkInHint",
                    "Mark start frame at playhead (I)"
                  )}
                >
                  <span>In</span>
                  {poseStartFrame != null ? (
                    <span className="review-video-player__pose-frame-n">
                      {poseStartFrame}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    poseEndFrame != null
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={markPoseOut}
                  disabled={poseAnalyzing || autoDetecting || calibrating}
                  aria-label={t("poseMarkOut", "Mark pose end frame")}
                  title={t(
                    "poseMarkOutHint",
                    "Mark end frame at playhead (O)"
                  )}
                >
                  <span>Out</span>
                  {poseEndFrame != null ? (
                    <span className="review-video-player__pose-frame-n">
                      {poseEndFrame}
                    </span>
                  ) : null}
                </button>
                {poseRangeLabel ? (
                  <button
                    type="button"
                    className="review-video-player__pose-range"
                    onClick={clearPoseRange}
                    disabled={poseAnalyzing || autoDetecting}
                    title={t("poseClearRange", "Clear pose frame range")}
                  >
                    {poseRangeLabel}
                    <span aria-hidden>×</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    (jointOverlayEnabled
                      ? jointOverlayMode === "knees"
                      : poseOverlayOn && poseResult)
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={handlePoseButton}
                  disabled={isPlaying || poseAnalyzing || autoDetecting}
                  aria-label={t("poseOverlay", "Pose")}
                  title={
                    isPlaying
                      ? t(
                          "posePauseToEnable",
                          "Pause the video to use Pose"
                        )
                      : jointOverlayEnabled &&
                          poseResult &&
                          poseRangeMatchesResult
                        ? t(
                            "poseToggleKneeAngles",
                            "Show / hide knee angle circles (paused only)"
                          )
                        : poseResult && poseRangeMatchesResult
                          ? canSeeVideoAnalysisInfo
                            ? t("poseToggleOverlay", "Show / hide pose overlay")
                            : t(
                                "poseReadyModeratorOnly",
                                "Pose ready — skeleton overlay is for moderators"
                              )
                          : poseRangeLabel
                            ? t(
                                "poseAnalyzeRange",
                                "Analyze pose between In and Out frames"
                              )
                            : sessionCalibrated
                              ? t("poseAnalyze", "Analyze pose on server")
                              : t(
                                  "poseAnalyzeAutoPlayers",
                                  "Analyze pose (auto: largest shoulder→hip left=A, right=B)"
                                )
                  }
                >
                  <PersonStanding size={18} strokeWidth={2.2} />
                  <span>
                    {poseAnalyzing
                      ? t("poseAnalyzing", "Pose…")
                      : jointOverlayEnabled &&
                          poseResult &&
                          poseRangeMatchesResult
                        ? jointOverlayMode === "knees"
                          ? t("poseKneesOn", "Knees: On")
                          : t("poseKneesOff", "Knees: Off")
                        : poseResult && poseRangeMatchesResult
                          ? canSeeVideoAnalysisInfo
                            ? poseOverlayOn
                              ? t("poseOn", "Pose: On")
                              : t("poseOff", "Pose: Off")
                            : t("poseReady", "Pose ready")
                          : poseRangeLabel
                            ? t("poseRunRange", "Pose range")
                            : t("pose", "Pose")}
                  </span>
                </button>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    strokeReelExporting
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={
                    strokeReelExporting
                      ? () => strokeReelAbortRef.current?.abort()
                      : () => handleGenerateStrokeReel("stroke_slowmo")
                  }
                  disabled={
                    !strokeReelCanStart ||
                    poseAnalyzing ||
                    autoDetecting ||
                    calibrating ||
                    reuploading
                  }
                  aria-label={t(
                    "strokeSlowMoGenerate",
                    "Generate stroke slow-mo reel"
                  )}
                  title={
                    strokeReelReady
                      ? t(
                          "strokeSlowMoGenerateHint",
                          "Uses stored pose. Portrait slow-mo with knee angles around the best stroke in the max rally; saved for the active player."
                        )
                      : t(
                          "strokeSlowMoGenerateStoredHint",
                          "Uses stored pose for this clip — no new pose job. Click to load results and export."
                        )
                  }
                >
                  <Film size={18} strokeWidth={2.2} />
                  <span>
                    {strokeReelExporting
                      ? strokeReelProgress > 0
                        ? t("strokeSlowMoExportingPct", "Exporting slow-mo · {{pct}}%", {
                            pct: strokeReelProgress,
                          })
                        : t("strokeSlowMoWorking", "Slow-mo…")
                      : t("strokeSlowMoShort", "Slow-mo reel")}
                  </span>
                </button>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    strokeReelExporting
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={
                    strokeReelExporting
                      ? () => strokeReelAbortRef.current?.abort()
                      : () => handleGenerateStrokeReel("max_rally")
                  }
                  disabled={
                    !strokeReelCanStart ||
                    poseAnalyzing ||
                    autoDetecting ||
                    calibrating ||
                    reuploading
                  }
                  aria-label={t("maxRallyReelGenerate", "Generate max rally reel")}
                  title={t(
                    "maxRallyReelGenerateHint",
                    "Portrait clip of the longest rally, slow-mo on the middle stroke. Saved for the active player."
                  )}
                >
                  <Trophy size={18} strokeWidth={2.2} />
                  <span>
                    {strokeReelExporting
                      ? strokeReelProgress > 0
                        ? t("strokeSlowMoExportingPct", "Exporting slow-mo · {{pct}}%", {
                            pct: strokeReelProgress,
                          })
                        : t("maxRallyReelWorking", "Max rally…")
                      : t("maxRallyReelShort", "Reel Max Rally")}
                  </span>
                </button>
                {playingStrokeReel ? (
                  <>
                    <button
                      type="button"
                      className="review-video-player__btn review-video-player__btn--pose-active"
                      onClick={restoreOriginalStrokeReelPlayback}
                      aria-label={t(
                        "strokeSlowMoRestoreOriginal",
                        "Original video"
                      )}
                      title={t(
                        "strokeSlowMoRestoreOriginalHint",
                        "Return to the source evaluation clip"
                      )}
                    >
                      <Undo2 size={18} strokeWidth={2.2} />
                      <span>
                        {t("strokeSlowMoRestoreOriginal", "Original video")}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="review-video-player__btn"
                      onClick={() => setShowReelShareSheet(true)}
                      aria-label={t("shareReel", "Share reel")}
                      title={t(
                        "shareReelHintPersonal",
                        "Share this reel to Instagram, TikTok, WhatsApp, or download it."
                      )}
                      style={{
                        background: "linear-gradient(135deg, #FF007A 0%, #7928CA 100%)",
                        color: "#fff",
                        borderColor: "transparent",
                        fontWeight: 600,
                      }}
                    >
                      <Share2 size={18} strokeWidth={2.2} />
                      <span>{t("shareRally", "Share Rally")}</span>
                    </button>
                    <button
                      type="button"
                      className="review-video-player__btn"
                      onClick={() => setShowReelShareSheet(true)}
                      aria-label={t("downloadReel", "Download")}
                      title={t("downloadReel", "Download")}
                    >
                      <Download size={18} strokeWidth={2.2} />
                      <span>{t("downloadReel", "Download")}</span>
                    </button>
                  </>
                ) : null}
                <label
                  className="review-video-player__btn"
                  title={t(
                    "pose3dToggleHint",
                    "Also generate Pose3D and open the in-browser 3D viewer"
                  )}
                  style={{ gap: 6, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={pose3dEnabled}
                    disabled={poseAnalyzing || autoDetecting}
                    onChange={(e) => setPose3dEnabled(e.target.checked)}
                  />
                  <span>{t("pose3d", "Pose3D")}</span>
                </label>
                {pose3dEnabled ? (
                  <select
                    className="review-video-player__btn"
                    value={pose3dMode}
                    disabled={poseAnalyzing || autoDetecting}
                    onChange={(e) => setPose3dMode(e.target.value)}
                    aria-label={t("pose3dMode", "Pose3D mode")}
                    title={t("pose3dMode", "Pose3D mode")}
                  >
                    <option value="mono_world">
                      {t("pose3dMono", "Mono (world)")}
                    </option>
                    <option value="multi_triangulate">
                      {t("pose3dMulti", "Multi-camera")}
                    </option>
                    <option value="strided_transformer">
                      {t(
                        "pose3dStrided",
                        "StridedTransformer lift (STT)"
                      )}
                    </option>
                    <option value="lidar_iphone">
                      {t("pose3dLidar", "iPhone LiDAR (upload)")}
                    </option>
                  </select>
                ) : null}
                {pose3dEnabled && pose3dMode === "strided_transformer" ? (
                  <span
                    className="review-video-player__btn"
                    style={{ opacity: 0.85, fontSize: 12 }}
                    title={t(
                      "pose3dStridedHint",
                      "Server lift: 2D pose → StridedTransformer-Pose3D (Vegetebird / visionary-minds test_scripts). Requires STRIDED_TRANSFORMER_ROOT on the pose worker."
                    )}
                  >
                    {t("pose3dStridedNote", "STT: single-video neural lift")}
                  </span>
                ) : null}
                {pose3dEnabled && pose3dMode === "multi_triangulate" ? (
                  <>
                    <input
                      className="review-video-player__btn"
                      style={{ minWidth: 120 }}
                      placeholder={t("pose3dCameraGroup", "camera_group_id")}
                      value={pose3dCameraGroupId}
                      disabled={poseAnalyzing || autoDetecting}
                      onChange={(e) => setPose3dCameraGroupId(e.target.value)}
                      title={t(
                        "pose3dCameraGroupHint",
                        "Multi-cam rig id from pose3d/camera-calibration"
                      )}
                    />
                    <input
                      className="review-video-player__btn"
                      style={{ minWidth: 160 }}
                      placeholder={t("pose3dPeerUrl", "Peer camera video URL")}
                      value={pose3dPeerUrl}
                      disabled={poseAnalyzing || autoDetecting}
                      onChange={(e) => setPose3dPeerUrl(e.target.value)}
                    />
                    <input
                      className="review-video-player__btn"
                      style={{ width: 72 }}
                      placeholder="cam1"
                      value={pose3dPeerCameraId}
                      disabled={poseAnalyzing || autoDetecting}
                      onChange={(e) => setPose3dPeerCameraId(e.target.value)}
                      title={t("pose3dPeerCamId", "Peer camera_id in rig")}
                    />
                  </>
                ) : null}
                {pose3dEnabled && pose3dMode === "lidar_iphone" ? (
                  <span
                    className="review-video-player__btn"
                    style={{ opacity: 0.8, fontSize: 12 }}
                    title={t(
                      "pose3dLidarHint",
                      "Capture on iPhone Pro (LiDAR) and upload via pose3d/upload — Pose button still runs 2D/mono for comparison."
                    )}
                  >
                    {t("pose3dLidarNote", "LiDAR: use iPhone app")}
                  </span>
                ) : null}
                {pose3dResult?.samples?.length ? (
                  <button
                    type="button"
                    className="review-video-player__btn review-video-player__btn--pose-active"
                    title={
                      cachedPose3dMatches
                        ? t("pose3dOpenViewer", "Open Pose3D web viewer")
                        : t(
                            "pose3dCacheMismatch",
                            "Cached Pose3D does not match current In/Out or pose fps — Regenerate"
                          )
                    }
                    onClick={() => {
                      if (cachedPose3dMatches) {
                        setPose3dViewerOpen(true);
                      } else {
                        regeneratePose3d();
                      }
                    }}
                  >
                    <span>
                      {cachedPose3dMatches
                        ? t("pose3dOpen3d", "Open 3D")
                        : t("pose3dRegenShort", "Regen 3D")}
                    </span>
                  </button>
                ) : null}
                {pose3dEnabled && pose3dMode !== "lidar_iphone" ? (
                  <button
                    type="button"
                    className="review-video-player__btn"
                    disabled={poseAnalyzing || autoDetecting}
                    title={t(
                      "pose3dRegenerateHint",
                      "Force a new Pose3D job for the current In/Out segment"
                    )}
                    onClick={regeneratePose3d}
                  >
                    <span>{t("pose3dRegenerate", "Regenerate 3D")}</span>
                  </button>
                ) : null}
                {pose3dJobId ? (
                  <button
                    type="button"
                    className="review-video-player__btn"
                    title={
                      pose3dStatus ||
                      t("pose3dCopyJob", "Copy job id for Quest XR")
                    }
                    onClick={() => {
                      try {
                        navigator.clipboard?.writeText(pose3dJobId);
                        setPose3dStatus(
                          t("pose3dCopied", "Job id copied for Quest XR")
                        );
                      } catch (_) {
                        /* ignore */
                      }
                    }}
                  >
                    <span>{t("pose3dXr", "XR job")}</span>
                  </button>
                ) : null}
                {pose3dStatus ? (
                  <span
                    className="review-video-player__btn"
                    style={{ opacity: 0.85, fontSize: 12, maxWidth: 220 }}
                    title={pose3dStatus}
                  >
                    {pose3dStatus}
                  </span>
                ) : null}
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    showPose3dCompare
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  disabled={!teId}
                  onClick={() => setShowPose3dCompare((v) => !v)}
                  title={t(
                    "pose3dCompareToggle",
                    "Compare mono / multi / LiDAR Pose3D"
                  )}
                >
                  <span>{t("pose3dCompare", "3D compare")}</span>
                </button>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    poseChartsVisible ? "review-video-player__btn--pose-active" : ""
                  }`}
                  onClick={() => setPoseChartsVisible((v) => !v)}
                  disabled={!poseResult?.samples?.length}
                  aria-pressed={poseChartsVisible}
                  aria-label={t("poseChartsToggle", "Show / hide pose charts")}
                  title={t("poseChartsToggle", "Show / hide pose charts")}
                >
                  <span>
                    {poseChartsVisible
                      ? t("poseChartsOn", "Charts: On")
                      : t("poseChartsOff", "Charts: Off")}
                  </span>
                </button>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    calibrating ? "review-video-player__btn--pose-active" : ""
                  }`}
                  onClick={handleStartCalibrate}
                  disabled={poseAnalyzing || autoDetecting}
                  aria-label={t("poseCalibrateTable", "Calibrate table")}
                  title={t(
                    "poseCalibrateTableHint",
                    "Calibrate table corners then assign Player A (left/G) and Player B (right/D), like iOS."
                  )}
                >
                  <span>{t("calibrate", "Calibrate")}</span>
                </button>
                <button
                  type="button"
                  className="review-video-player__btn"
                  onClick={handleAutoDetectPlayers}
                  disabled={poseAnalyzing || autoDetecting || calibrating}
                  aria-label={t("poseAutoDetect", "Auto-detect")}
                  title={
                    analysisSettings.poseDetectionPlayer === "Both"
                      ? t(
                          "poseAutoDetectBothHint",
                          "Auto-detect Players A and B on this frame, then track only those ROIs (same pick as batch)."
                        )
                      : analysisSettings.poseDetectionPlayer === "B"
                        ? t(
                            "poseAutoDetectBHint",
                            "Auto-detect Player B (right / D) on this frame, then track only that ROI."
                          )
                        : t(
                            "poseAutoDetectAHint",
                            "Auto-detect Player A (left / G) on this frame, then track only that ROI."
                          )
                  }
                >
                  <span>
                    {autoDetecting
                      ? t("poseAutoDetectingShort", "Detecting…")
                      : t("poseAutoDetect", "Auto-detect")}
                  </span>
                </button>
                <button
                  type="button"
                  className="review-video-player__btn"
                  onClick={() => setAnalysisSettingsOpen(true)}
                  disabled={poseAnalyzing || autoDetecting}
                  aria-label={t("videoAnalysisSettings", "Video analysis settings")}
                  title={
                    usedPoseFps != null
                      ? usedPoseFps !== effectivePoseFps
                        ? t(
                            "poseAnalyzedFpsDiffHint",
                            `Analyzed at ${usedPoseFps} fps · next run: ${effectivePoseFps} fps`
                          )
                        : t(
                            "poseAnalyzedFpsHint",
                            `Analyzed at ${usedPoseFps} fps`
                          )
                      : t(
                          "videoAnalysisSettingsHint",
                          `Next run pose sample rate: ${effectivePoseFps} fps`
                        )
                  }
                >
                  <span>{`${displayedPoseFps} fps`}</span>
                </button>
                <button
                  type="button"
                  className="review-video-player__btn"
                  onClick={handleFlushPose}
                  disabled={poseAnalyzing || autoDetecting || calibrating || ballAnalyzing}
                  aria-label={t("poseFlush", "Clear pose and ball data and restart")}
                  title={t(
                    "poseFlushHint",
                    "Delete cached pose 2D/3D and ball results and restart calibration"
                  )}
                >
                  <span>{t("poseRestart", "Clear")}</span>
                </button>
                <select
                  className="review-video-player__btn review-video-player__ball-detector"
                  value={ballDetector}
                  onChange={(e) => handleBallDetectorChange(e.target.value)}
                  disabled={ballAnalyzing || poseAnalyzing || calibrating}
                  aria-label={t("ballDetector", "Ball detector")}
                  title={t(
                    "ballDetectorHint",
                    "TrackNet (ML), FMO (needs table corners), YOLOv5, or YOLO26"
                  )}
                >
                  <option value="tracknet">TrackNet</option>
                  <option value="fmo">FMO</option>
                  <option value="yolov5">YOLOv5</option>
                  <option value="yolo26">YOLO26</option>
                </select>
                <select
                  className="review-video-player__btn review-video-player__ball-stroke"
                  value={ballStrokeKind}
                  onChange={(e) => handleBallStrokeKindChange(e.target.value)}
                  disabled={ballAnalyzing || poseAnalyzing || calibrating}
                  aria-label={t("ballStrokeKind", "Stroke type for ball detection")}
                  title={t(
                    "ballStrokeKindHint",
                    "FH/BH: ball from forward swing through recovery. Serve: still toss-hand through far-side out"
                  )}
                >
                  {BALL_STROKE_KINDS.map((kind) => {
                    const n = countStrokesForKind(ballStrokeDetections, kind.id);
                    const label = t(kind.labelKey, kind.label);
                    return (
                      <option key={kind.id} value={kind.id}>
                        {n > 0 ? `${label} (${n})` : label}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    ballOverlayOn &&
                    ballSelectionMatches(ballResult, ballDetector, ballStrokeKind)
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={handleBallButton}
                  disabled={ballAnalyzing || poseAnalyzing || calibrating}
                  aria-label={t("ballTrack", "Ball track")}
                  title={
                    ballSelectionMatches(ballResult, ballDetector, ballStrokeKind)
                      ? t("ballToggleOverlay", "Show / hide ball track")
                      : ballDetector === "fmo"
                        ? t(
                            "ballAnalyzeFmo",
                            "Detect ball with FMO (requires table calibration)"
                          )
                        : ballDetector === "yolov5"
                          ? t("ballAnalyzeYolo", "Detect ball with YOLOv5")
                          : ballDetector === "yolo26"
                            ? t("ballAnalyzeYolo26", "Detect ball with YOLO26")
                            : t("ballAnalyzeTracknet", "Detect ball with TrackNet")
                  }
                >
                  <Target size={18} strokeWidth={2.2} />
                  <span>
                    {ballAnalyzing
                      ? t("ballAnalyzing", "Ball…")
                      : ballSelectionMatches(
                          ballResult,
                          ballDetector,
                          ballStrokeKind
                        )
                        ? ballOverlayOn
                          ? t("ballOn", "Ball: On")
                          : t("ballOff", "Ball: Off")
                        : t("ball", "Ball")}
                  </span>
                </button>
                <button
                  type="button"
                  className="review-video-player__btn"
                  onClick={regenerateBallAnalysis}
                  disabled={
                    ballAnalyzing || poseAnalyzing || calibrating || autoDetecting
                  }
                  aria-label={t("ballRegenerate", "Regenerate ball detection")}
                  title={t(
                    "ballRegenerateHint",
                    "Re-run ball detection only. Pose 2D and 3D stay as they are."
                  )}
                >
                  <RefreshCw size={18} strokeWidth={2.2} />
                  <span>
                    {ballAnalyzing
                      ? t("ballAnalyzing", "Ball…")
                      : t("ballRegenerateShort", "Regen ball")}
                  </span>
                </button>
              </div>
            ) : jointOverlayEnabled && teId ? (
              <div className="review-video-player__pose-tools">
                <button
                  type="button"
                  className={`review-video-player__btn ${
                    jointOverlayMode === "knees"
                      ? "review-video-player__btn--pose-active"
                      : ""
                  }`}
                  onClick={handlePoseButton}
                  disabled={isPlaying || jointOverlayLoading}
                  aria-label={t("poseOverlay", "Pose")}
                  title={
                    isPlaying
                      ? t(
                          "posePauseToEnable",
                          "Pause the video to use Pose"
                        )
                      : t(
                          "poseToggleKneeAngles",
                          "Show / hide knee angle circles (paused only)"
                        )
                  }
                >
                  <PersonStanding size={18} strokeWidth={2.2} />
                  <span>
                    {jointOverlayLoading
                      ? t("poseAnalyzing", "Pose…")
                      : jointOverlayMode === "knees"
                        ? t("poseKneesOn", "Knees: On")
                        : t("pose", "Pose")}
                  </span>
                </button>
              </div>
            ) : null}
          </div>

          {(saveError ||
            saveOk ||
            poseError ||
            ballError ||
            strokeReelError ||
            strokeReelStatus) && (
            <p
              className={`review-video-player__orient-status ${
                saveError || poseError || ballError || strokeReelError
                  ? "review-video-player__orient-status--error"
                  : "review-video-player__orient-status--ok"
              }`}
              role="status"
            >
              {strokeReelError ||
                ballError ||
                poseError ||
                saveError ||
                strokeReelStatus ||
                t(
                  "videoViewSetupSuccess",
                  "View saved. It will reopen with this zoom and orientation."
                )}
            </p>
          )}
        </div>
      )}

      <div
        ref={viewportRef}
        className="review-video-player__viewport"
        style={{ touchAction: "none" }}
      >
        <div
          className="review-video-player__transform"
          style={{
            ...zoomStyle,
            ...(viewReady
              ? null
              : { opacity: 0, visibility: "hidden", pointerEvents: "none" }),
          }}
          aria-hidden={!viewReady}
        >
          <video
            key={url}
            ref={(node) => {
              videoRef.current = node;
              if (typeof mediaElRef === "function") mediaElRef(node);
              else if (mediaElRef) mediaElRef.current = node;
            }}
            className={`review-video-player__video ${videoClassName}`.trim()}
            style={{
              ...(displayTransform ? { transform: displayTransform } : null),
              ...(viewReady
                ? null
                : { opacity: 0, visibility: "hidden" }),
            }}
            src={mediaSrc || undefined}
            playsInline
            // Large OVH S3 clips (100MB+) with preload=auto issue one open-ended
            // Range GET. Chrome then logs net::ERR_CONTENT_LENGTH_MISMATCH 206
            // when that transfer is aborted (seek, Game-points skip, or a
            // mid-file stall ~124MB). Metadata-only matches the other players.
            // Table replay is a few seconds — buffer it so the coach phone starts.
            preload={eagerPlayback ? "auto" : "metadata"}
            autoPlay={false}
            muted={!viewReady || audioMuted}
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={revealIfMediaReady}
            onCanPlay={revealIfMediaReady}
            onError={handleMediaError}
            onClick={handleVideoClick}
          />
          {replayScreenUrl ? (
            <video
              ref={replayScreenRef}
              className="review-video-player__feedback-demo"
              src={replayScreenUrl}
              playsInline
              controls={false}
            />
          ) : null}
          <VideoFrameAnnotator
            active={basicPlayback ? false : annotateOpen}
            onClose={() => {
              setAnnotateOpen(false);
              setShowControls(true);
            }}
            notes={basicPlayback || feedbackOnDemand ? [] : frameNotes}
            onChangeNotes={persistFrameNotes}
            currentTimeSec={currentTime}
            isPlaying={isPlaying}
            playbackStrokes={
              (basicPlayback || feedbackOnDemand) && !feedbackModeOpen
                ? null
                : replayStrokes
            }
            onStrokeCommitted={(stroke) => {
              const el = videoRef.current;
              const videoMs = Math.round((el?.currentTime ?? currentTime) * 1000);
              const fps = feedbackRecorderRef.current?.fps || feedbackFps;
              feedbackRecorderRef.current?.addStroke(stroke, {
                videoMs,
                frame: frameFromVideoMs(videoMs, fps),
              });
            }}
            onVoiceReady={(url, meta) => {
              const el = videoRef.current;
              const videoMs = Math.round((el?.currentTime ?? currentTime) * 1000);
              const fps = feedbackRecorderRef.current?.fps || feedbackFps;
              feedbackRecorderRef.current?.addVoice(url, {
                ...meta,
                videoMs,
                frame: frameFromVideoMs(videoMs, fps),
              });
            }}
            onDemoReady={(url, meta) => {
              const el = videoRef.current;
              const videoMs = Math.round((el?.currentTime ?? currentTime) * 1000);
              const fps = feedbackRecorderRef.current?.fps || feedbackFps;
              feedbackRecorderRef.current?.addScreen(url, {
                ...meta,
                videoMs,
                frame: frameFromVideoMs(videoMs, fps),
              });
            }}
            onTranscriptReady={(text) => {
              if (!activePlayerId || !text) return;
              saveCycleFeedbackCue({
                playerId: activePlayerId,
                text,
                teId,
              }).catch(() => {});
            }}
            onPrepareFeedback={(seed) => {
              feedbackSeedRef.current = seed;
            }}
            onBeginScreenRecording={beginScreenRecording}
            onEndScreenRecording={endScreenRecording}
            onFeedbackRecordingChange={setFeedbackRecording}
            videoWidth={videoPixelSize.w}
            videoHeight={videoPixelSize.h}
            contentTransform={displayTransform}
            canReplayLast={feedbackSessions.length > 0}
            onReplayLast={() => {
              const last = feedbackSessions[feedbackSessions.length - 1];
              if (last) jumpToFeedback(last);
            }}
            toolbarRoot={toolbarHost}
            onPause={() => {
              const el = videoRef.current;
              if (el && !el.paused) el.pause();
              setIsPlaying(false);
            }}
            t={t}
          />
          {viewReady &&
          enablePose &&
          !calibrating &&
          !playingStrokeReel &&
          ((poseResult && (poseOverlayOn || poseChartsVisible)) ||
            tableHighlightUntil > 0) ? (
            <div
              className="pose-skeleton-overlay-wrap"
              style={displayTransform ? { transform: displayTransform } : undefined}
            >
              <PoseSkeletonOverlay
                sample={poseSample}
                samples={poseResult?.samples || null}
                videoRef={videoRef}
                analysisFps={
                  Number(poseResult?.settings?.fps) > 1
                    ? Number(poseResult.settings.fps)
                    : null
                }
                tableCorners={tableCornersForOverlay}
                videoWidth={videoPixelSize.w}
                videoHeight={videoPixelSize.h}
                visible
                showSkeleton={Boolean(
                  poseOverlayOn && poseResult && canSeeVideoAnalysisInfo
                )}
                showRoiBoxes={false}
                showTrunkRotation={false}
                tableHighlightUntil={tableHighlightUntil}
                shoulderWidthRef={
                  fhTopspinDetection?.shoulder_width_ref ??
                  fhDetection?.shoulder_width_ref ??
                  bhTopspinDetection?.shoulder_width_ref ??
                  bhLeftDetection?.shoulder_width_ref ??
                  null
                }
                seedRoiLeft={
                  playerRois.left || poseResult?.settings?.roi_left || null
                }
                seedRoiRight={
                  playerRois.right || poseResult?.settings?.roi_right || null
                }
              />
            </div>
          ) : null}

          {viewReady &&
          jointOverlayEnabled &&
          !calibrating &&
          !playingStrokeReel &&
          !isPlaying &&
          jointOverlayMode !== "off" ? (
            <div
              className="pose-skeleton-overlay-wrap"
              style={displayTransform ? { transform: displayTransform } : undefined}
            >
              <JointAngleOverlay
                sample={jointOverlaySample}
                mode={jointOverlayMode}
                videoWidth={videoPixelSize.w}
                videoHeight={videoPixelSize.h}
                poseSide={
                  jointOverlayAllPlayers ? "both" : jointOverlaySide
                }
                selectionRoi={
                  jointOverlayCoachControls ? jointOverlaySelectionRoi : null
                }
                selectionLabel={
                  jointOverlayCoachControls
                    ? jointOverlaySide === "right"
                      ? "B / D"
                      : "A / G"
                    : null
                }
                emphasize
                visible
              />
            </div>
          ) : null}

          {viewReady &&
          enablePose &&
          !playingStrokeReel &&
          ballOverlayOn &&
          ballSelectionMatches(ballResult, ballDetector, ballStrokeKind) &&
          ballResult?.samples?.length &&
          !calibrating ? (
            <div
              className="pose-skeleton-overlay-wrap ball-track-overlay-wrap"
              style={displayTransform ? { transform: displayTransform } : undefined}
            >
              <BallTrackOverlay
                samples={ballResult.samples}
                events={ballResult.events || null}
                trajectories={ballResult.trajectories || null}
                tMs={Math.round(currentTime * 1000)}
                videoRef={videoRef}
                videoWidth={
                  Number(ballResult.settings?.width) || videoPixelSize.w
                }
                videoHeight={
                  Number(ballResult.settings?.height) || videoPixelSize.h
                }
                detectionRoi={ballDetectionRoi}
                detector={
                  ballResult.settings?.detector || ballDetector || "tracknet"
                }
                serveEvents={serveDetection?.events || null}
                tableCorners={resolvedTableCorners}
                visible
              />
            </div>
          ) : null}

          {calibPhase === "confirm" ? (
            <ConfirmCalibrationOverlay
              tableCorners={resolvedTableCorners}
              roiLeft={playerRois.left}
              roiRight={playerRois.right}
              confirming={calibSaving || poseAnalyzing}
              videoWidth={videoPixelSize.w}
              videoHeight={videoPixelSize.h}
              contentTransform={displayTransform || null}
              toolbarContainer={calibToolbarEl}
              onCancel={handleCancelCalibration}
              onRecalibrate={() => {
                beginCalibrationWizard("pose", { forceTable: true });
              }}
              onConfirm={async () => {
                setCalibSaving(true);
                try {
                  setSessionCalibrated(true);
                  setCalibPhase(null);
                  setShowControls(true);
                  beginTableGlowIfPending();
                  await runPoseAnalysis({
                    skipCalibCheck: true,
                    rois: playerRois,
                  });
                } finally {
                  setCalibSaving(false);
                }
              }}
            />
          ) : null}

          {calibPhase === "table" ? (
            <TableCornerCalibrationOverlay
              key="table-calib"
              initialCorners={pendingCorners || resolvedTableCorners}
              saving={calibSaving}
              continueLabel={t("poseCalibrateContinue", "Continue to players")}
              videoWidth={videoPixelSize.w}
              videoHeight={videoPixelSize.h}
              contentTransform={displayTransform || null}
              toolbarContainer={calibToolbarEl}
              onCancel={handleCancelCalibration}
              onSave={handleTableCornersContinue}
            />
          ) : null}

          {calibPhase === "players" ? (
            <PlayerCalibrationOverlay
              key={
                calibIntent === "ball"
                  ? `ball-players-${ballPlayerBoxesMode(
                      ballStrokeKind,
                      serveDetection,
                      analysisSettings.poseDetectionPlayer
                    )}`
                  : calibIntent === "joint"
                    ? `joint-player-${jointOverlayPlayer}`
                    : "pose-players"
              }
              tableCorners={
                resolvedTableCorners
              }
              confirming={calibSaving || ballAnalyzing}
              videoWidth={videoPixelSize.w}
              videoHeight={videoPixelSize.h}
              initialRoiLeft={playerRois.left}
              initialRoiRight={playerRois.right}
              contentTransform={displayTransform || null}
              viewSetup={viewSetup}
              toolbarContainer={calibToolbarEl}
              teId={teId}
              timeMs={Math.round((currentTime || 0) * 1000)}
              videoRef={videoRef}
              videoUrl={url || mediaSrc || null}
              // Serve: only the server box. Joint overlay: selected A/B only.
              detectionPlayer={
                calibIntent === "ball"
                  ? ballPlayerBoxesMode(
                      ballStrokeKind,
                      serveDetection,
                      analysisSettings.poseDetectionPlayer
                    )
                  : calibIntent === "joint"
                    ? jointOverlayPlayer
                    : analysisSettings.poseDetectionPlayer || "A"
              }
              onBack={() => {
                if (calibIntent === "joint") {
                  handleCancelCalibration();
                  return;
                }
                setPendingCorners((prev) => prev || resolvedTableCorners);
                setCalibPhase("table");
              }}
              onCancel={handleCancelCalibration}
              onConfirm={handlePlayerCalibConfirm}
            />
          ) : null}

          {calibPhase === "detroi" ? (
            <DetectionRoiOverlay
              key="ball-detroi"
              proposedRoi={proposedDetectionRoi}
              tableCorners={resolvedTableCorners}
              roiLeft={
                playerRois.left ||
                activeGeometry?.roi_left ||
                poseResult?.settings?.roi_left ||
                null
              }
              roiRight={
                playerRois.right ||
                activeGeometry?.roi_right ||
                poseResult?.settings?.roi_right ||
                null
              }
              confirming={calibSaving || ballAnalyzing}
              videoWidth={videoPixelSize.w}
              videoHeight={videoPixelSize.h}
              contentTransform={displayTransform || null}
              toolbarContainer={calibToolbarEl}
              onCancel={handleCancelCalibration}
              onBack={() => {
                setCalibIntent("ball");
                setCalibPhase("players");
              }}
              onAccept={handleDetectionRoiAccept}
              onConfirmManual={handleDetectionRoiAccept}
            />
          ) : null}
        </div>

        {calibrating ? (
          <div
            ref={setCalibToolbarEl}
            className="table-calib-overlay__toolbar-host"
          />
        ) : null}

        {viewReady &&
        enablePose &&
        poseOverlayOn &&
        poseResult &&
        !calibrating &&
        !playingStrokeReel &&
        hasFeature("pose_frame_angles") ? (
          <PoseAnglesPanel
            sample={poseSample}
            tableCorners={tableCornersForOverlay}
            fhTopspinDetection={fhTopspinDetection}
            fhDetection={fhDetection}
            bhTopspinDetection={bhTopspinDetection}
            bhLeftDetection={bhLeftDetection}
            currentTimeSec={currentTime}
          />
        ) : null}

        {showPose3dCompare && teId && !calibrating ? (
          <div
            className="review-video-player__pose3d-compare"
            style={{
              position: "absolute",
              left: 12,
              bottom: 72,
              right: 12,
              maxWidth: 560,
              zIndex: 6,
              background: "rgba(20,24,28,0.92)",
              color: "#fff",
              padding: 12,
              borderRadius: 8,
            }}
          >
            <Pose3DComparePanel teId={teId} t={t} />
          </div>
        ) : null}

        {poseAnalyzing || autoDetecting || ballAnalyzing ? (
          <PoseProcessingOverlay
            progress={
              autoDetecting ? 0 : ballAnalyzing ? ballProgress : poseProgress
            }
            etaSeconds={
              autoDetecting
                ? null
                : ballAnalyzing
                  ? ballEtaSeconds
                  : poseEtaSeconds
            }
            message={
              autoDetecting
                ? t(
                    "poseAutoDetecting",
                    "Auto-detecting player(s) on this frame…"
                  )
                : ballAnalyzing
                  ? t(
                      "ballDetecting",
                      ballDetector === "fmo"
                        ? "Detecting ball (FMO)…"
                        : ballDetector === "yolov5"
                          ? "Detecting ball (YOLOv5)…"
                          : ballDetector === "yolo26"
                            ? "Detecting ball (YOLO26)…"
                            : "Detecting ball (TrackNet)…"
                    )
                  : undefined
            }
          />
        ) : null}

        {(isMediaOpening || (!viewReady && !mediaLoadError)) && !mediaLoadError && (
          <div className="review-video-player__view-loading" role="status">
            <Loader2
              className="review-video-player__view-loading-icon"
              size={28}
              strokeWidth={2.4}
              aria-hidden
            />
            <span className="review-video-player__view-loading-label">
              {mediaLoadProgress?.percent != null
                ? t("videoLoadingPercent", "Loading video… {{pct}}%", {
                    pct: mediaLoadProgress.percent,
                  })
                : mediaLoadProgress?.bufferedEnd > 0.5
                  ? t(
                      "videoLoadingBuffered",
                      "Loading video… {{ready}} ready",
                      {
                        ready: formatTime(mediaLoadProgress.bufferedEnd),
                      }
                    )
                  : t("preparingVideoView", "Preparing view…")}
            </span>
            {mediaLoadProgress?.percent != null ? (
              <div
                className="review-video-player__load-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={mediaLoadProgress.percent}
              >
                <div
                  className="review-video-player__load-bar-fill"
                  style={{ width: `${mediaLoadProgress.percent}%` }}
                />
              </div>
            ) : (
              <div
                className="review-video-player__load-bar review-video-player__load-bar--indeterminate"
                aria-hidden
              >
                <div className="review-video-player__load-bar-fill" />
              </div>
            )}
            {mediaLoadProgress?.etaSec != null ? (
              <span className="review-video-player__load-eta">
                {t("videoLoadingEta", "About {{eta}} left", {
                  eta: formatEtaSeconds(mediaLoadProgress.etaSec),
                })}
              </span>
            ) : loadIdleSlow ? (
              <span className="review-video-player__load-eta">
                {t(
                  "videoLoadingStillWorking",
                  "Still downloading — this can take a minute on large clips."
                )}
              </span>
            ) : mediaLoadProgress?.indeterminate ? (
              <span className="review-video-player__load-eta">
                {t(
                  "videoLoadingLargeHint",
                  "Large clip — waiting for enough data to start…"
                )}
              </span>
            ) : null}
            {loadIdleSlow ? (
              <button
                type="button"
                className="review-video-player__btn review-video-player__media-error-btn"
                style={{ pointerEvents: "auto" }}
                onClick={handleRetryMedia}
              >
                {t("retryVideo", "Retry")}
              </button>
            ) : null}
          </div>
        )}

        {viewReady &&
        !mediaLoadError &&
        mediaSigning &&
        !mediaSrc &&
        effectiveWebPlaybackStatus !== "processing" ? (
          <div className="review-video-player__compat-banner" role="status">
            <Loader2
              className="review-video-player__view-loading-icon"
              size={16}
              strokeWidth={2.4}
              aria-hidden
            />
            <span>
              {t("authorizingVideoPlayback", "Authorizing video playback…")}
              {mediaLoadProgress?.percent != null
                ? ` · ${mediaLoadProgress.percent}%`
                : ""}
              {mediaLoadProgress?.etaSec != null
                ? ` · ~${formatEtaSeconds(mediaLoadProgress.etaSec)}`
                : ""}
            </span>
          </div>
        ) : null}

        {viewReady &&
        !mediaLoadError &&
        effectiveWebPlaybackStatus === "processing" ? (
          <div className="review-video-player__compat-banner" role="status">
            <Loader2
              className="review-video-player__view-loading-icon"
              size={16}
              strokeWidth={2.4}
              aria-hidden
            />
            <span>
              {t(
                "preparingVideoForWeb",
                "Converting phone video for web playback…"
              )}
            </span>
          </div>
        ) : null}

        {mediaLoadError ? (
          <div
            className="review-video-player__view-loading review-video-player__view-loading--error"
            role="alert"
          >
            <span className="review-video-player__view-loading-label">
              {mediaLoadError}
            </span>
            <div className="review-video-player__media-error-actions">
              <button
                type="button"
                className="review-video-player__btn review-video-player__media-error-btn"
                onClick={handleRetryMedia}
              >
                {t("retryVideo", "Retry")}
              </button>
              {canTransformVideo &&
              messageId &&
              typeof onRequestReupload === "function" ? (
                <button
                  type="button"
                  className="review-video-player__btn review-video-player__media-error-btn"
                  onClick={handleRequestPhoneReupload}
                  disabled={
                    saving ||
                    reuploading ||
                    requestingPhoneReupload
                  }
                >
                  <Smartphone size={16} strokeWidth={2.2} aria-hidden />
                  {requestingPhoneReupload
                    ? t("requestingOriginalReupload", "Requesting…")
                    : t("requestOriginal", "Original")}
                </button>
              ) : null}
              {canTransformVideo && messageId && canBakeEncode ? (
                <button
                  type="button"
                  className="review-video-player__btn review-video-player__media-error-btn"
                  onClick={handleReupload}
                  disabled={
                    saving ||
                    reuploading ||
                    requestingPhoneReupload
                  }
                >
                  <Upload size={16} strokeWidth={2.2} aria-hidden />
                  {reuploading
                    ? t("bakeOrientsWorking", "Re-encoding…")
                    : t("bakeOrients", "Bake")}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {isBuffering &&
        !isMediaOpening &&
        !mediaLoadError &&
        isPlaying ? (
          <div className="review-video-player__view-loading review-video-player__view-loading--buffer" role="status">
            <Loader2
              className="review-video-player__view-loading-icon"
              size={28}
              strokeWidth={2.4}
              aria-hidden
            />
            <span className="review-video-player__view-loading-label">
              {mediaLoadProgress?.percent != null
                ? t("videoBufferingPercent", "Buffering… {{pct}}%", {
                    pct: mediaLoadProgress.percent,
                  })
                : t("videoBuffering", "Buffering…")}
            </span>
            {mediaLoadProgress?.percent != null ? (
              <div
                className="review-video-player__load-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={mediaLoadProgress.percent}
              >
                <div
                  className="review-video-player__load-bar-fill"
                  style={{ width: `${mediaLoadProgress.percent}%` }}
                />
              </div>
            ) : (
              <div
                className="review-video-player__load-bar review-video-player__load-bar--indeterminate"
                aria-hidden
              >
                <div className="review-video-player__load-bar-fill" />
              </div>
            )}
            {mediaLoadProgress?.etaSec != null ? (
              <span className="review-video-player__load-eta">
                {t("videoLoadingEta", "About {{eta}} left", {
                  eta: formatEtaSeconds(mediaLoadProgress.etaSec),
                })}
              </span>
            ) : null}
          </div>
        ) : null}

        {viewReady && liveHrLines.length > 0 && !calibrating && (
          <div className="review-video-player__hr" aria-live="polite">
            {liveHrLines.map((line) => (
              <div key={line.key} className="review-video-player__hr-line">
                {line.playerName ? (
                  <span className="review-video-player__hr-name">
                    {line.playerName}
                  </span>
                ) : null}
                <Heart
                  className="review-video-player__hr-icon"
                  size={14}
                  strokeWidth={2.4}
                  fill="currentColor"
                  aria-hidden
                />
                <span className="review-video-player__hr-value">{line.bpm}</span>
                <span className="review-video-player__hr-unit">bpm</span>
              </div>
            ))}
          </div>
        )}

        {(saving || reuploading || requestingPhoneReupload) && (
          <div className="review-video-player__save-overlay" role="status">
            <div className="review-video-player__save-card">
              <p className="review-video-player__save-title">
                {reuploading
                  ? t("bakeOrientsWorking", "Re-encoding…")
                  : requestingPhoneReupload
                    ? t("requestingOriginalReupload", "Requesting…")
                  : t("savingVideoView", "Saving view…")}
              </p>
              {reuploading && reencodeProgress ? (
                <>
                  <div
                    className="review-video-player__reencode-bar"
                    aria-hidden
                  >
                    <div
                      className="review-video-player__reencode-bar-fill"
                      style={{
                        width: `${Math.max(2, reencodeProgress.pct || 0)}%`,
                      }}
                    />
                  </div>
                  <p className="review-video-player__reencode-meta">
                    {t("bakeOrientsProgressPct", "{{pct}}%", {
                      pct: reencodeProgress.pct || 0,
                    })}
                    {formatReencodeEta(reencodeProgress.etaSeconds)
                      ? ` · ${t(
                          "bakeOrientsEta",
                          "~{{eta}} left",
                          {
                            eta: formatReencodeEta(
                              reencodeProgress.etaSeconds
                            ),
                          }
                        )}`
                      : ""}
                  </p>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {!calibrating ? (
      <div
        className="review-video-player__chrome review-video-player__chrome--bottom"
        aria-hidden={showControls ? undefined : true}
        onPointerDownCapture={onControlsActivityCapture}
        onPointerUpCapture={onControlsPointerUpCapture}
        onPointerCancelCapture={onControlsPointerUpCapture}
        onInputCapture={onControlsActivityCapture}
      >
        <div
          className="review-video-player__speeds"
          role="group"
          aria-label={t("playbackSpeed", "Playback speed")}
        >
          {!basicPlayback
            ? PLAYBACK_SPEEDS.map((rate) => (
                <button
                  key={rate}
                  type="button"
                  className={`review-video-player__speed-btn ${
                    playbackRate === rate
                      ? "review-video-player__speed-btn--active"
                      : ""
                  }`}
                  onClick={() => handleSpeedChange(rate)}
                  aria-pressed={playbackRate === rate}
                >
                  {rate === 1 ? "1×" : `${rate}×`}
                </button>
              ))
            : null}
          <button
            type="button"
            className={`review-video-player__speed-btn review-video-player__mute-btn ${
              audioMuted ? "review-video-player__mute-btn--on" : ""
            }`}
            onClick={toggleAudioMute}
            aria-pressed={audioMuted}
            aria-label={
              audioMuted
                ? t("unmuteVideo", "Unmute")
                : t("muteVideo", "Mute")
            }
            title={
              audioMuted
                ? t("unmuteVideo", "Unmute")
                : t("muteVideo", "Mute")
            }
          >
            {audioMuted ? (
              <VolumeX size={16} strokeWidth={2.2} aria-hidden />
            ) : (
              <Volume2 size={16} strokeWidth={2.2} aria-hidden />
            )}
          </button>
        </div>

        <div className="review-video-player__scrubber-wrap">
          {duration > 0 && kotPointsAvailable ? (
            <div
              className="review-video-player__scrubber-range review-video-player__scrubber-range--kot"
              aria-hidden
            >
              {kotPointSegments.map((seg) => (
                <div
                  key={`kot-seg-${seg.index}`}
                  className="review-video-player__scrubber-kot-point"
                  style={{
                    left: `${Math.max(
                      0,
                      Math.min(100, (seg.start_ms / 1000 / duration) * 100)
                    )}%`,
                    width: `${Math.max(
                      0.4,
                      Math.min(
                        100,
                        ((seg.end_ms - seg.start_ms) / 1000 / duration) * 100
                      )
                    )}%`,
                  }}
                />
              ))}
            </div>
          ) : null}
          {duration > 0 &&
          (poseStartFrame != null || poseEndFrame != null) ? (
            <div className="review-video-player__scrubber-range" aria-hidden>
              <div
                className="review-video-player__scrubber-range-fill"
                style={{
                  left: `${Math.max(
                    0,
                    Math.min(
                      100,
                      ((poseStartFrame != null
                        ? poseStartFrame * (frameDurationRef.current || 1 / PLAYER_DEFAULT_FPS)
                        : 0) /
                        duration) *
                        100
                    )
                  )}%`,
                  width: `${Math.max(
                    0.5,
                    Math.min(
                      100,
                      ((((poseEndFrame != null
                        ? poseEndFrame
                        : Math.floor(
                            duration / (frameDurationRef.current || 1 / PLAYER_DEFAULT_FPS)
                          )) -
                        (poseStartFrame != null ? poseStartFrame : 0) +
                        1) *
                        (frameDurationRef.current || 1 / PLAYER_DEFAULT_FPS)) /
                        duration) *
                        100
                    )
                  )}%`,
                }}
              />
            </div>
          ) : null}
          <input
            type="range"
            className="review-video-player__scrubber"
            min={clampPlaybackWindow(0, playbackWindowRef.current).start || 0}
            max={Math.max(
              1,
              clampPlaybackWindow(0, playbackWindowRef.current).end ??
                (duration ||
                  mediaSeekHorizon(videoRef.current) ||
                  1)
            )}
            step={0.01}
            value={Math.min(
              Math.max(
                currentTime,
                clampPlaybackWindow(0, playbackWindowRef.current).start || 0
              ),
              Math.max(
                1,
                clampPlaybackWindow(0, playbackWindowRef.current).end ??
                  (duration ||
                    mediaSeekHorizon(videoRef.current) ||
                    1)
              )
            )}
            onChange={(event) =>
              seekTo(Number(event.target.value), { pause: false })
            }
            aria-label={t("seek", "Seek")}
          />
        </div>

        <div className="review-video-player__time">
          <span>
            {formatTime(
              clampPlaybackWindow(currentTime, playbackWindowRef.current).end != null
                ? Math.max(
                    0,
                    currentTime -
                      clampPlaybackWindow(currentTime, playbackWindowRef.current).start
                  )
                : currentTime
            )}
          </span>
          <span className="review-video-player__time-sep">/</span>
          <span>
            {formatTime(
              clampPlaybackWindow(currentTime, playbackWindowRef.current).end != null
                ? Math.max(
                    0,
                    clampPlaybackWindow(currentTime, playbackWindowRef.current).end -
                      clampPlaybackWindow(currentTime, playbackWindowRef.current).start
                  )
                : duration
            )}
          </span>
        </div>

        {jointOverlayEnabled ? (
          <div
            className="review-video-player__joint-overlay"
            role="group"
            aria-label={t("jointOverlay", "Joint overlay")}
          >
            {jointOverlayCoachControls ? (
              <div
                className="review-video-player__joint-overlay-players"
                role="group"
                aria-label={t("jointOverlayPlayer", "Player for angles")}
              >
                {["A", "B"].map((player) => (
                  <button
                    key={player}
                    type="button"
                    className={`review-video-player__joint-overlay-btn ${
                      jointOverlayPlayer === player
                        ? "review-video-player__joint-overlay-btn--active"
                        : ""
                    }`}
                    onClick={() => {
                      setJointOverlayPlayer(player);
                      jointOverlayStoreRef.current.clear();
                    }}
                    aria-pressed={jointOverlayPlayer === player}
                    disabled={isPlaying || jointOverlayLoading}
                    title={
                      player === "A"
                        ? t(
                            "jointOverlayPlayerAHint",
                            "Use Player A (left / G) for knee and elbow angles"
                          )
                        : t(
                            "jointOverlayPlayerBHint",
                            "Use Player B (right / D) for knee and elbow angles"
                          )
                    }
                  >
                    {player === "A"
                      ? t("jointOverlayPlayerA", "A")
                      : t("jointOverlayPlayerB", "B")}
                  </button>
                ))}
                <button
                  type="button"
                  className="review-video-player__joint-overlay-btn"
                  onClick={beginJointPlayerBoxSelect}
                  disabled={isPlaying || jointOverlayLoading || !teId}
                  title={t(
                    "jointOverlaySelectBoxHint",
                    "Draw or redraw the selected player's box for angle detection"
                  )}
                >
                  {t("jointOverlaySelectBox", "Box")}
                </button>
              </div>
            ) : null}
            {["knees", "elbows", "off"].map((mode) => (
              <button
                key={mode}
                type="button"
                className={`review-video-player__joint-overlay-btn ${
                  jointOverlayMode === mode
                    ? "review-video-player__joint-overlay-btn--active"
                    : ""
                }`}
                onClick={() => {
                  if (isPlaying && mode !== "off") return;
                  setJointOverlayMode(mode);
                }}
                aria-pressed={jointOverlayMode === mode}
                disabled={
                  (isPlaying && mode !== "off") ||
                  (jointOverlayLoading && mode !== "off")
                }
                title={
                  isPlaying && mode !== "off"
                    ? t(
                        "posePauseToEnable",
                        "Pause the video to use Pose"
                      )
                    : mode === "knees"
                      ? jointOverlayAllPlayers
                        ? t(
                            "jointOverlayKneesAllHint",
                            "Detect players' knees on this paused frame"
                          )
                        : jointOverlayCoachControls
                          ? t(
                              "jointOverlayKneesCoachHint",
                              "Detect knees for the selected player inside their box"
                            )
                          : t(
                              "jointOverlayKneesHint",
                              "Detect left-player knees on this paused frame"
                            )
                      : mode === "elbows"
                        ? jointOverlayAllPlayers
                          ? t(
                              "jointOverlayElbowsAllHint",
                              "Detect players' elbows on this paused frame"
                            )
                          : jointOverlayCoachControls
                            ? t(
                                "jointOverlayElbowsCoachHint",
                                "Detect elbows for the selected player inside their box"
                              )
                            : t(
                                "jointOverlayElbowsHint",
                                "Detect left-player elbows on this paused frame"
                              )
                        : undefined
                }
              >
                {mode === "knees"
                  ? t("jointOverlayKnees", "Knees")
                  : mode === "elbows"
                    ? t("jointOverlayElbows", "Elbows")
                    : t("jointOverlayOff", "Off")}
              </button>
            ))}
            {jointOverlayMode !== "off" &&
            !isPlaying &&
            !jointOverlayLoading &&
            !jointOverlaySample &&
            poseError ? (
              <p className="review-video-player__joint-overlay-error" role="status">
                {poseError}
                {jointOverlayCoachControls ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="review-video-player__joint-overlay-error-action"
                      onClick={beginJointPlayerBoxSelect}
                      disabled={!teId}
                    >
                      {t("jointOverlaySelectBox", "Box")}
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="review-video-player__transport">
          <div className="review-video-player__transport-main" role="group">
            <button
              type="button"
              className="review-video-player__btn review-video-player__btn--seek"
              onClick={() => seekBySeconds(-SEEK_SECONDS)}
              aria-label={t("rewind3s", "Rewind 3 seconds")}
            >
              <RotateCcw size={18} strokeWidth={2.2} />
              <span>−3s</span>
            </button>

            {!basicPlayback ? (
            <button
              type="button"
              className="review-video-player__btn review-video-player__btn--frame"
              onClick={() => stepFrame(false)}
              aria-label={t("previousFrame", "Previous frame")}
            >
              <ChevronFirst size={20} strokeWidth={2.2} />
              <span>1f</span>
            </button>
            ) : null}

            <button
              type="button"
              className="review-video-player__btn review-video-player__btn--play"
              onClick={togglePlay}
              aria-label={isPlaying ? t("pause", "Pause") : t("play", "Play")}
            >
              {isPlaying ? (
                <Pause size={22} strokeWidth={2.4} fill="currentColor" />
              ) : (
                <Play size={22} strokeWidth={2.4} fill="currentColor" />
              )}
            </button>

            {!basicPlayback ? (
            <button
              type="button"
              className="review-video-player__btn review-video-player__btn--frame"
              onClick={() => stepFrame(true)}
              aria-label={t("nextFrame", "Next frame")}
            >
              <ChevronLast size={20} strokeWidth={2.2} />
              <span>1f</span>
            </button>
            ) : null}

            <button
              type="button"
              className="review-video-player__btn review-video-player__btn--seek"
              onClick={() => seekBySeconds(SEEK_SECONDS)}
              aria-label={t("forward3s", "Forward 3 seconds")}
            >
              <RotateCw size={18} strokeWidth={2.2} />
              <span>+3s</span>
            </button>
          </div>
          {playingStrokeReel || isShareableReel ? (
            <div className="review-video-player__transport-extra">
              <button
                type="button"
                className="review-video-player__btn"
                onClick={() => setShowReelShareSheet(true)}
                aria-label={t("shareReel", "Share reel")}
                title={t(
                  "shareReelHintPersonal",
                  "Share this reel to Instagram, TikTok, WhatsApp, or download it."
                )}
              >
                <Share2 size={18} strokeWidth={2.2} />
                <span>{t("share", "Share")}</span>
              </button>
              <button
                type="button"
                className="review-video-player__btn"
                onClick={() => setShowReelShareSheet(true)}
                aria-label={t("downloadReel", "Download")}
                title={t("downloadReel", "Download")}
              >
                <Download size={18} strokeWidth={2.2} />
                <span>{t("downloadReel", "Download")}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
      ) : null}

      {viewReady && basicPlayback && feedbackViewer && feedbackSessions.length ? (
        <div
          className="review-video-player__analysis-hud"
          onPointerDownCapture={onControlsActivityCapture}
        >
          {feedbackModeOpen ? (
            <div className="review-video-player__feedback-list" role="list">
              {feedbackSessions.map((session, index) => (
                <div
                  key={session.id}
                  role="listitem"
                  className={
                    replaySessionId === session.id
                      ? "review-video-player__feedback-chip is-on"
                      : "review-video-player__feedback-chip"
                  }
                >
                  <button
                    type="button"
                    className="review-video-player__feedback-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFeedbackModeOpen(true);
                      jumpToFeedback(session);
                    }}
                  >
                    {t("feedbackN", "Feedback {{n}}", { n: index + 1 })}
                    {" · "}
                    {formatFeedbackClock(session.anchorMs)}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <button
            type="button"
            className={`review-video-player__analysis-info-btn review-video-player__feedback-blink${
              feedbackModeOpen ? " is-on" : ""
            }`}
            aria-pressed={feedbackModeOpen}
            aria-label={
              feedbackModeOpen
                ? t("hideFeedbackList", "Hide feedbacks")
                : t("showFeedbackList", "Show feedbacks")
            }
            title={t(
              "openMatchFeedback",
              "Open feedback. The take on this frame plays, or pick one from the list."
            )}
            onClick={(e) => {
              e.stopPropagation();
              toggleBasicFeedback();
            }}
          >
            <MessageSquare strokeWidth={2.4} />
          </button>
        </div>
      ) : null}

      {/* Player-root overlay: above chrome, clear of fullscreen close "X". */}
      {viewReady && !basicPlayback ? (
        <div
          className={`review-video-player__analysis-hud${
            analysisInfoOpen
              ? " review-video-player__analysis-hud--open"
              : ""
          }`}
          onPointerDownCapture={onControlsActivityCapture}
          onPointerUpCapture={onControlsPointerUpCapture}
          onPointerCancelCapture={onControlsPointerUpCapture}
          onInputCapture={onControlsActivityCapture}
        >
          {feedbackSessions.length &&
          (feedbackOnDemand
            ? feedbackViewer && feedbackModeOpen
            : feedbackListOpen) ? (
            <div className="review-video-player__feedback-list" role="list">
              {feedbackOnDemand ? null : (
                <>
                  <label className="review-video-player__feedback-auto">
                    <input
                      type="checkbox"
                      checked={autoPlayFeedback}
                      onChange={(e) => setAutoPlayFeedback(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>
                      {t("autoPlayFeedback", "Auto-play feedback with video")}
                    </span>
                  </label>
                  {!autoPlayFeedback ? (
                    <p className="review-video-player__feedback-hint">
                      {t(
                        "tapFeedbackToPlay",
                        "Tap a feedback entry to play drawings and voice."
                      )}
                    </p>
                  ) : null}
                </>
              )}
              {feedbackSessions.map((session, index) => (
                <div
                  key={session.id}
                  role="listitem"
                  className={
                    replaySessionId === session.id
                      ? "review-video-player__feedback-chip is-on"
                      : "review-video-player__feedback-chip"
                  }
                >
                  <button
                    type="button"
                    className="review-video-player__feedback-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      jumpToFeedback(session);
                      if (!feedbackOnDemand) setFeedbackListOpen(false);
                    }}
                    title={t(
                      "playFeedback",
                      "Play this feedback with the same pauses, speed, and drawings"
                    )}
                  >
                    {t("feedbackN", "Feedback {{n}}", { n: index + 1 })}
                    {" · "}
                    {formatFeedbackClock(session.anchorMs)}
                  </button>
                  {feedbackOnDemand ? null : (
                    <button
                      type="button"
                      className="review-video-player__feedback-delete"
                      aria-label={t("deleteFeedback", "Delete feedback")}
                      title={t("deleteFeedback", "Delete feedback")}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFeedback(session.id);
                      }}
                    >
                      <Trash2 size={12} strokeWidth={2.4} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          {feedbackOnDemand && feedbackViewer && feedbackSessions.length ? (
            <button
              type="button"
              className={`review-video-player__analysis-info-btn review-video-player__feedback-blink${
                feedbackModeOpen ? " is-on" : ""
              }`}
              aria-pressed={feedbackModeOpen}
              aria-label={t("feedbackTool", "Feedback")}
              title={t(
                "openMatchFeedback",
                "Open feedback. The take on this frame plays, or pick one from the list."
              )}
              onClick={(e) => {
                e.stopPropagation();
                toggleBasicFeedback();
              }}
            >
              <MessageSquare size={16} strokeWidth={2.4} />
            </button>
          ) : null}
          {!feedbackOnDemand && feedbackSessions.length ? (
            <button
              type="button"
              className={`review-video-player__analysis-info-btn review-video-player__feedback-blink${
                feedbackListOpen ? " is-on" : ""
              }`}
              aria-pressed={feedbackListOpen}
              aria-expanded={feedbackListOpen}
              aria-label={
                feedbackListOpen
                  ? t("hideFeedbackList", "Hide feedbacks")
                  : t("showFeedbackList", "Show feedbacks")
              }
              title={
                feedbackListOpen
                  ? t("hideFeedbackList", "Hide feedbacks")
                  : t("showFeedbackList", "Show feedbacks")
              }
              onClick={(e) => {
                e.stopPropagation();
                setFeedbackListOpen((open) => !open);
                setShowControls(true);
              }}
            >
              <MessageSquare size={16} strokeWidth={2.4} />
            </button>
          ) : null}
          {feedbackOnDemand ? null : (
            <button
              type="button"
              className="review-video-player__analysis-info-btn"
              aria-pressed={annotateOpen}
              aria-label={t("feedbackTool", "Feedback")}
              title={t(
                "feedbackToolHint",
                "Pause on a frame, then draw and record voice feedback."
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (annotateOpen) {
                  setAnnotateOpen(false);
                  setShowControls(true);
                  return;
                }
                const el = videoRef.current;
                if (el && !el.paused) {
                  el.pause();
                  setIsPlaying(false);
                }
                setShowControls(true);
                setAnnotateOpen(true);
              }}
            >
              <MessageSquarePlus size={16} strokeWidth={2.4} />
            </button>
          )}
          <button
            type="button"
            className="review-video-player__analysis-info-btn"
            aria-label={t("labelStroke", "Label stroke")}
            title={t(
              "labelStrokeHint",
              "Pause on a frame, then tap a phase to mark it."
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (isPlaying) togglePlay();
              setStrokeLabelOpen(true);
            }}
          >
            <Tag size={16} strokeWidth={2.4} />
          </button>
          {enablePose ? (
            <button
              type="button"
              className="review-video-player__analysis-info-btn"
              aria-label={t("strokeAnalysis", "Stroke analysis")}
              title={t(
                "strokeAnalysisHint",
                "Break down the nearest stroke: phases, durations, angles, and points to improve."
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (isPlaying) togglePlay();
                setStrokeAnalysisOpen(true);
              }}
            >
              <Sparkles size={16} strokeWidth={2.4} />
            </button>
          ) : null}
          {canSeeVideoAnalysisInfo ? (
          <button
            type="button"
            className="review-video-player__analysis-info-btn"
            aria-expanded={analysisInfoOpen}
            aria-controls="review-video-analysis-info"
            aria-label={t("videoAnalysisInfo", "Video and analysis info")}
            title={t("videoAnalysisInfo", "Video and analysis info")}
            onClick={(e) => {
              e.stopPropagation();
              setAnalysisInfoOpen((v) => !v);
            }}
          >
            <Info size={16} strokeWidth={2.4} />
          </button>
          ) : null}
          {canSeeVideoAnalysisInfo && analysisInfoOpen ? (
            <div
              id="review-video-analysis-info"
              className="review-video-player__analysis-hud-panel"
              role="dialog"
              aria-label={t("videoAnalysisInfo", "Video and analysis info")}
              onClick={(e) => e.stopPropagation()}
            >
              {analysisHudSections.map((section) => (
                <div
                  key={section.id}
                  className="review-video-player__analysis-hud-section"
                >
                  <div className="review-video-player__analysis-hud-title">
                    {section.title}
                  </div>
                  {section.rows.map((row) => (
                    <div
                      key={`${section.id}-${row.label}`}
                      className="review-video-player__analysis-hud-row"
                    >
                      <span className="review-video-player__analysis-hud-key">
                        {row.label}
                      </span>
                      <span className="review-video-player__analysis-hud-val">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              {ballPlayheadState ? (
                <div className="review-video-player__analysis-hud-line review-video-player__analysis-hud-state">
                  {ballPlayheadState}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {enablePose ? (
        <VideoAnalysisSettingsDialog
          open={analysisSettingsOpen}
          onClose={() => setAnalysisSettingsOpen(false)}
          onSaved={setAnalysisSettings}
          t={t}
        />
      ) : null}
      {pose3dViewerOpen ? (
        <Suspense fallback={null}>
          <Pose3DWebViewer
            open={pose3dViewerOpen}
            onClose={() => setPose3dViewerOpen(false)}
            result={pose3dResult}
            jobId={pose3dJobId}
            teId={teId}
            mode={pose3dMode === "lidar_iphone" ? null : pose3dMode}
            onResultLoaded={setPose3dResult}
            t={t}
            zIndex={12000}
          />
        </Suspense>
      ) : null}

      <StrokeLabelEditor
        open={strokeLabelOpen}
        onClose={() => setStrokeLabelOpen(false)}
        storageKey={strokeLabelStorageKey(messageId, url)}
        currentTimeSec={currentTime}
        onSeek={(sec) => seekTo(sec, { pause: true })}
        onStepFrame={stepFrame}
        t={t}
      />

      <StrokeAnalysisPanel
        open={strokeAnalysisOpen}
        onClose={() => setStrokeAnalysisOpen(false)}
        detections={ballStrokeDetections}
        poseSamples={poseResult?.samples || null}
        storedAnalyses={storedStrokeAnalyses}
        fullClipPoseReady={fullClipPoseReady}
        currentTimeSec={currentTime}
        onSeek={(sec) => seekTo(sec, { pause: true })}
        onPlayStroke={playStrokeAtSlowSpeed}
        t={t}
      />

      {showClipComposer && teId ? (
        <div
          className="review-video-player__clip-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowClipComposer(false);
          }}
        >
          <div className="review-video-player__clip-modal-inner">
            <ViralClipComposer
              teId={teId}
              videoUrl={mediaSrc || null}
              onClose={() => setShowClipComposer(false)}
            />
          </div>
        </div>
      ) : null}

      <ClipShareSheet
        open={showReelShareSheet}
        onClose={() => setShowReelShareSheet(false)}
        videoUrl={
          playingStrokeReel
            ? strokeReelBlobUrlRef.current || mediaSrc
            : url || mediaSrc
        }
        blob={strokeReelBlobRef.current}
        kind={shareReelKind || shareableReelKind || "max_rally"}
        t={t}
      />
    </div>
  );
}

export default ReviewVideoPlayer;
